import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion, useDragControls, useMotionValue, useTransform } from 'framer-motion';
import {
  User, Loader2, MapPin, Clock, CalendarDays, ClipboardList,
  Search, Calendar, Banknote, MessageCircle, Store, CreditCard,
  CheckCircle, AlertTriangle, Phone, Mail, ChevronLeft, ChevronRight, Scissors, Check,
  X, Instagram, Facebook, Globe, History, Info,
  Star, Award, Ticket, Gift, Home, LocateFixed, Plus, Minus, Users
} from 'lucide-react';
import { format, parse, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, subDays, formatDistanceToNowStrict } from 'date-fns';
import { es } from 'date-fns/locale';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../firebase';

import LocationSelector from './LocationSelector';
import BancardPaymentModal from '../common/BancardPaymentModal';
import { LoginModal } from '../../LoginModal';
import ToastContainer from '../common/ToastContainer';
import BrandSplash, { saveBrandCache } from '../common/BrandSplash';
import { formatPrice, formatDate, getStatusColor, getStatusText, getStatusTextColor, formatDuration } from '../../utils/helpers';
import { getDomicilioConfig, domicilioDisponible, esServicioDomicilio, itemPersonaAdicional, dentroDeGranAsuncion, textoDireccionDomicilio, coordenadasDeLink, extraerUrl, esLinkCorto } from '../../utils/domicilio';
import { claveTelefono, telefonoValido } from '../../utils/phone';
import { httpsCallable } from 'firebase/functions';

// ============================================
// HELPERS
// ============================================
const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#ffffff';
  const hex = hexcolor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) || 0;
  const g = parseInt(hex.substr(2, 2), 16) || 0;
  const b = parseInt(hex.substr(4, 2), 16) || 0;
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#0f172a' : '#ffffff';
};

// =====================================================================
// 🕐 Minutos que faltan desde AHORA (hora del dispositivo) hasta el
// turno seleccionado. Se usa SOLO para elegir qué texto mostrar en la
// pantalla de éxito — es una copia de la MISMA regla de negocio que ya
// usa el backend (diff < 180 → se autoconfirma sin botones).
// No reemplaza al backend: el server es la fuente de verdad real.
// =====================================================================
// Caché en memoria (dura lo que dura la visita) de las reseñas que llegaron
// por WhatsApp, por local — ver fetchFromBookings en el perfil del barbero.
const resenasWhatsAppPorLocal = new Map();

const getMinutosHastaTurno = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  try {
    const [h, m] = timeStr.split(':').map(Number);
    const turno = parse(dateStr, 'yyyy-MM-dd', new Date());
    turno.setHours(h, m, 0, 0);
    return Math.round((turno.getTime() - Date.now()) / 60000);
  } catch {
    return null;
  }
};

// Los pasos entran desde el lado hacia el que se navega (custom = 1 avanzar,
// -1 volver, 0 sin desplazamiento si el sistema pide reducir movimiento).
const screenVariants = {
  hidden: (dir = 1) => ({ opacity: 0, x: dir * 24 }),
  visible: { opacity: 1, x: 0, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } },
  exit: (dir = 1) => ({ opacity: 0, x: dir * -12, transition: { duration: 0.1 } })
};

// Avisos que aparecen en medio del formulario (ej: mientras escribe el
// teléfono): se abren en altura en vez de empujar todo de golpe.
const revealVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1 },
};
const revealTransition = { duration: 0.2, ease: [0.23, 1, 0.32, 1] };
// Con "reducir movimiento" activado: la altura cambia al instante, solo queda el fade.
const revealTransitionReduced = { height: { duration: 0 }, opacity: { duration: 0.2 } };

// Fondo oscuro de los modales: entra y sale con fade en vez de aparecer de golpe.
const backdropFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
};
// Salida del panel: más corta que la entrada, así cerrar se siente inmediato.
const panelExitTransition = { duration: 0.15, ease: [0.23, 1, 0.32, 1] };
const getPanelExit = (reduceMotion) => (
  reduceMotion
    ? { opacity: 0, transition: panelExitTransition }
    : { opacity: 0, scale: 0.97, transition: panelExitTransition }
);

// Hojas de abajo: en mobile se cierran arrastrando hacia abajo, como una hoja
// nativa. Solo el encabezado (handleProps) arranca el arrastre, así el
// contenido con scroll de adentro sigue scrolleando normal.
const useSheetDrag = (onClose, desktopQuery = '(min-width: 640px)') => {
  const controls = useDragControls();
  const reduceMotion = useReducedMotion();
  const [active] = useState(() => typeof window !== 'undefined' && !window.matchMedia(desktopQuery).matches);
  if (!active) return { active, panelProps: {}, handleProps: {}, exit: null };
  return {
    active,
    panelProps: {
      drag: 'y',
      dragControls: controls,
      dragListener: false,
      dragConstraints: { top: 0, bottom: 0 },
      dragElastic: { top: 0, bottom: 1 },
      dragMomentum: false,
      onDragEnd: (_, info) => { if (info.offset.y > 110 || info.velocity.y > 600) onClose(); },
    },
    handleProps: { onPointerDown: (e) => controls.start(e) },
    // Al cerrarse baja hasta afuera, también si se cierra tocando el fondo.
    exit: reduceMotion
      ? { opacity: 0, transition: panelExitTransition }
      : { y: '100%', transition: { duration: 0.22, ease: [0.32, 0.72, 0, 1] } },
  };
};

const SheetGrabber = ({ onImage }) => (
  <span aria-hidden="true" className="block w-10 h-1 rounded-full"
    style={{ background: onImage ? 'rgba(255,255,255,0.6)' : 'var(--cv-line-strong)' }} />
);

// ============================================
// TOKENS DE DISEÑO
// ============================================
// El color de cada barbería (brandColor) es el único acento de la vista.
// Todo lo demás es una base neutra — grafito en oscuro, porcelana en
// claro — para que cualquier color de marca combine sin pelearse.
const GOLD = '#C9A23A';   // reservado para membresías
const DANGER = '#E5484D';
const WARN = '#E38B12';
const OK = '#2FA36B';

const getTokens = (isDarkMode) => (isDarkMode ? {
  bg: '#1C1B19',
  surface: '#262421',
  sunk: '#22201D',
  sheet: '#2A2825',
  line: 'rgba(255,255,255,0.09)',
  lineStrong: 'rgba(255,255,255,0.2)',
  ink: '#F3F1EE',
  inkSoft: 'rgba(243,241,238,0.7)',
  inkFaint: 'rgba(243,241,238,0.45)',
  hover: 'rgba(255,255,255,0.04)',
  material: 'rgba(28,27,25,0.72)',
} : {
  bg: '#EDEFF1',
  surface: '#FFFFFF',
  sunk: '#E3E6E9',
  sheet: '#FFFFFF',
  line: 'rgba(21,23,26,0.1)',
  lineStrong: 'rgba(21,23,26,0.22)',
  ink: '#15171A',
  inkSoft: 'rgba(21,23,26,0.68)',
  inkFaint: 'rgba(21,23,26,0.45)',
  hover: 'rgba(21,23,26,0.03)',
  material: 'rgba(237,239,241,0.72)',
});

// Variables CSS que usan las clases de abajo (inputs, foco, troquelado).
const getCssVars = (t, brandColor) => ({
  '--cv-bg': t.bg,
  '--cv-surface': t.surface,
  '--cv-sunk': t.sunk,
  '--cv-sheet': t.sheet,
  '--cv-ink-soft': t.inkSoft,
  '--cv-line': t.line,
  '--cv-line-strong': t.lineStrong,
  '--cv-ink': t.ink,
  '--cv-ink-faint': t.inkFaint,
  '--cv-hover': t.hover,
  '--cv-brand': brandColor,
  '--cv-material': t.material,
  '--cv-brand-ring': `${brandColor}40`,
});

const getInitials = (name, fallback = 'B') => (
  name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : fallback
);

// ============================================
// ESTILOS
// ============================================
const premiumStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@600;700;800;900&family=Schibsted+Grotesk:wght@400;500;600;700;800&display=swap');

  .cv-root { font-family: 'Schibsted Grotesk', system-ui, sans-serif; -webkit-tap-highlight-color: transparent; }
  .cv-display { font-family: 'Big Shoulders Display', 'Schibsted Grotesk', sans-serif; font-weight: 800; line-height: 0.95; letter-spacing: -0.005em; }
  .cv-display.cv-num { font-variant-numeric: tabular-nums; }
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

  .cv-root button:focus-visible, .cv-root a:focus-visible, .cv-root [role="button"]:focus-visible {
    outline: 2px solid var(--cv-brand); outline-offset: 2px;
  }
  .cv-root button { -webkit-user-select: none; user-select: none; }
  /* Toque inmediato (sin la espera del doble toque) y sin el menú de
     mantener apretado en links que funcionan como botones. */
  .cv-root button, .cv-root a, .cv-root [role="button"] { touch-action: manipulation; }
  .cv-root a { -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; }
  /* El scroll de cada pantalla no se contagia a la página: sin rebote del
     fondo ni "tirar para recargar" que borre la reserva a medias. */
  .cv-scroll { overscroll-behavior-y: contain; }

  /* Materiales estilo iOS: las barras flotan sobre el contenido, que pasa
     por debajo desenfocado (en vez de barras sólidas con borde). */
  .cv-material {
    background: var(--cv-material);
    -webkit-backdrop-filter: blur(20px) saturate(180%); backdrop-filter: blur(20px) saturate(180%);
  }
  /* Botones sobre la foto: vidrio oscuro, la foto se sigue viendo detrás. */
  .cv-glass {
    background: rgba(0,0,0,0.3); color: #fff;
    -webkit-backdrop-filter: blur(14px) saturate(160%); backdrop-filter: blur(14px) saturate(160%);
  }
  @media (prefers-reduced-transparency: reduce) {
    .cv-material { background: var(--cv-bg); -webkit-backdrop-filter: none; backdrop-filter: none; }
    .cv-glass { background: rgba(0,0,0,0.6); -webkit-backdrop-filter: none; backdrop-filter: none; }
  }

  .cv-input {
    width: 100%; font-size: 16px; font-weight: 500; font-family: inherit;
    padding: 13px 14px; border-radius: 12px;
    background: var(--cv-surface); color: var(--cv-ink);
    border: 1px solid var(--cv-line); outline: none;
    transition: border-color .15s ease, box-shadow .15s ease;
  }
  .cv-input::placeholder { color: var(--cv-ink-faint); }
  .cv-input:focus { border-color: var(--cv-brand); box-shadow: 0 0 0 3px var(--cv-brand-ring); }
  .cv-input[aria-invalid="true"] { border-color: ${DANGER}; }
  .cv-input:disabled { opacity: .6; }

  @media (hover: hover) {
    .cv-row:hover { background-color: var(--cv-hover) !important; }
  }

  /* Troquelado del ticket: línea punteada con dos muescas del color del fondo */
  .cv-perf { position: relative; border-top: 2px dashed var(--cv-line-strong); margin: 0 20px; }
  .cv-perf::before, .cv-perf::after {
    content: ''; position: absolute; top: -12px; width: 22px; height: 22px;
    border-radius: 50%; background: var(--cv-bg);
  }
  .cv-perf::before { left: -32px; }
  .cv-perf::after { right: -32px; }


  @media (prefers-reduced-motion: reduce) {
    .cv-root *, .cv-root *::before, .cv-root *::after { transition-duration: .01ms !important; animation-duration: .01ms !important; }
  }
`;

// =====================================================================
// ⭐ RESEÑAS DE GOOGLE — calificación de Google Maps del local + hasta 5
// reseñas (las que entrega Google), guardadas una vez por día por una
// Cloud Function en locations/{id}.googleReviews. Se muestra solo si la
// empresa tiene la función habilitada y la barbería la prendió.
// =====================================================================
const EstrellasGoogle = ({ valor, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${valor} de 5 estrellas`}>
    {[1, 2, 3, 4, 5].map(i => {
      const relleno = Math.max(0, Math.min(1, valor - (i - 1)));
      return (
        <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
          <Star size={size} color="#FBBC05" strokeWidth={1.5} className="absolute inset-0" />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${relleno * 100}%` }}>
            <Star size={size} fill="#FBBC05" color="#FBBC05" strokeWidth={1.5} />
          </span>
        </span>
      );
    })}
  </span>
);
const GoogleG = ({ size = 18 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
  </svg>
);

// Píldora compacta (va debajo de la dirección): logo de Google + nota + estrellas
const GoogleRatingPill = ({ data, t, textPrimary, textSecondary, onClick }) => (
  <motion.button type="button" whileTap={{ scale: 0.97 }} onClick={onClick}
    aria-label={`Calificación en Google: ${Number(data.rating).toFixed(1)} de 5, ${data.total} reseñas. Ver reseñas`}
    className="self-start mt-1 inline-flex items-center gap-2 h-10 pl-1.5 pr-3 rounded-full"
    style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}>
    <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }}>
      <GoogleG size={15} />
    </span>
    <span className="text-[15px] font-bold cv-num">{Number(data.rating).toFixed(1).replace('.', ',')}</span>
    <EstrellasGoogle valor={data.rating} size={13} />
    <span className="text-[13px] cv-num" style={{ color: textSecondary }}>({data.total})</span>
    <ChevronRight size={15} style={{ color: textSecondary }} />
  </motion.button>
);

// Contenido de la hoja de reseñas: solo las de 5 estrellas
const GoogleReviewsContent = ({ data, t, textPrimary, textSecondary, brandColor, contrastText, onClose }) => {
  const fmt = (v) => Number(v).toFixed(1).replace('.', ',');
  const resenas = (data.reviews || []).filter(r => Number(r.rating) === 5 && r.text);
  return (
    <>
      {/* Encabezado */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shrink-0" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
            <GoogleG size={24} />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-medium" style={{ color: textSecondary }}>Opiniones en Google</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="cv-display cv-num leading-none" style={{ fontSize: '2.4rem' }}>{fmt(data.rating)}</span>
              <span className="flex flex-col gap-0.5">
                <EstrellasGoogle valor={data.rating} size={14} />
                <span className="text-[12px] cv-num" style={{ color: textSecondary }}>{data.total} reseñas</span>
              </span>
            </div>
          </div>
        </div>
        <motion.button whileTap={{ scale: 0.9 }} onClick={onClose} aria-label="Cerrar reseñas"
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ boxShadow: `0 0 0 1px ${t.line}` }}>
          <X size={18} />
        </motion.button>
      </div>

      {/* Reseñas de 5 estrellas */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-5 px-5 mt-5 space-y-2.5">
        {resenas.length === 0 ? (
          <p className="text-[14px] py-6 text-center" style={{ color: textSecondary }}>Mirá todas las opiniones en Google Maps.</p>
        ) : resenas.map((r, i) => (
          <motion.a key={i} href={r.url || data.mapsUrl || undefined} target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1], delay: 0.05 + i * 0.05 }}
            className="block rounded-[18px] p-4" style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}>
            <div className="flex items-center gap-2.5">
              {r.authorPhoto
                ? <img src={r.authorPhoto} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover shrink-0" />
                : <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-semibold" style={{ background: t.line }}>{(r.author || '?').charAt(0)}</span>}
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold truncate">{r.author}</p>
                <div className="flex items-center gap-1.5">
                  <EstrellasGoogle valor={5} size={11} />
                  <span className="text-[12px]" style={{ color: textSecondary }}>{r.relativeTime}</span>
                </div>
              </div>
            </div>
            <p className="text-[14px] leading-relaxed mt-2.5 line-clamp-6">{r.text}</p>
          </motion.a>
        ))}
      </div>

      {/* Acciones */}
      <div className="pt-4 space-y-2">
        {data.writeReviewUrl && (
          <motion.a whileTap={{ scale: 0.98 }} href={data.writeReviewUrl} target="_blank" rel="noopener noreferrer"
            className="w-full h-12 rounded-full flex items-center justify-center gap-2 text-[15px] font-semibold"
            style={{ background: brandColor, color: contrastText }}>
            <Star size={16} fill="currentColor" /> Dejar mi reseña
          </motion.a>
        )}
        {data.mapsUrl && (
          <a href={data.mapsUrl} target="_blank" rel="noopener noreferrer" className="w-full h-10 flex items-center justify-center gap-1 text-[13px] font-semibold" style={{ color: textSecondary }}>
            Ver todas en Google Maps <ChevronRight size={14} />
          </a>
        )}
      </div>
    </>
  );
};

const BarberProfileModal = ({ barber, isOpen, onClose, brandColor, isDarkMode, onSelect, selectedBarber, currentPlan, locationId, enabledFeatures }) => {
  const contrastText = getContrastColor(brandColor);
  const isSelected = selectedBarber?.id === barber?.id;
  // En mobile es una hoja que sube desde el borde de abajo; en desktop va
  // centrado, así que entra con una escala leve en vez de deslizarse.
  const reduceMotion = useReducedMotion();
  const [isDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  const sheetHidden = reduceMotion
    ? { opacity: 0 }
    : isDesktop ? { opacity: 0, scale: 0.97 } : { y: '100%' };
  const sheetTransition = isDesktop || reduceMotion
    ? { duration: 0.2, ease: [0.23, 1, 0.32, 1] }
    : { duration: 0.35, ease: [0.32, 0.72, 0, 1] };
  const sheetDrag = useSheetDrag(onClose, '(min-width: 768px)');
  // ✅ Además del plan empresarial, ahora también exige que Joel haya
  // habilitado "Reseñas" para esta empresa puntual desde el SuperAdmin
  // (independiente del plan) — si lo apaga ahí, esto deja de mostrarse
  // acá también, sin tener que tocar nada más.
  const isPremiumPlan = !!enabledFeatures?.reviews; // interruptor del SuperAdmin, cualquier plan

  // =====================================================================
  // ⭐ RESEÑAS DE CLIENTES — solo plan empresarial (mismo gate que ya
  // usaba el promedio de estrellas). Se traen desde
  // locations/{locId}/barbers/{barberId}/reviews (reseñas dejadas desde la
  // app) — acá solo agregamos la LECTURA para mostrarlas en el perfil.
  //
  // ✅ FIX: además de la subcolección, TAMBIÉN hay que mirar `bookings`
  // filtrando isReviewed:true — ahí viven las reseñas que llegaron por
  // WhatsApp, que nunca se guardaron en la subcolección (mismo patrón que
  // ya usa BarberReviews.jsx en el panel admin para no perder ninguna).
  // Sin esto, "faltaban" justo las reseñas más viejas — la mayoría había
  // llegado por WhatsApp antes de que existiera el flujo de reseña en la
  // app misma.
  //
  // ⚠️ Los hooks van ANTES del condicional "isOpen && barber" de más abajo
  // a propósito — si no, se rompen las Reglas de los Hooks de React
  // (deben ejecutarse siempre, en el mismo orden, en cada render).
  // =====================================================================
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  // La bio larga arranca recortada a 3 líneas, con "Ver más" (como App Store).
  const [bioAbierta, setBioAbierta] = useState(false);
  useEffect(() => { setBioAbierta(false); }, [barber?.id]);

  useEffect(() => {
    if (!isOpen || !isPremiumPlan || !barber?.id || !locationId) {
      setReviews([]);
      return;
    }
    setLoadingReviews(true);

    const barberDocId = barber.docId || barber.id;
    const seenIds = new Set();
    const merged = [];

    const getTimestamp = (data) => {
      if (data.createdAt?.toDate) return data.createdAt.toDate().getTime();
      if (data.createdAt?.seconds) return data.createdAt.seconds * 1000;
      return 0;
    };

    // 1. Subcolección de reseñas (dejadas desde la app)
    const fetchSubcollection = async () => {
      try {
        const reviewsRef = collection(db, "locations", String(locationId), "barbers", String(barberDocId), "reviews");
        const q = query(reviewsRef, orderBy("createdAt", "desc"), limit(30));
        const snap = await getDocs(q);
        snap.forEach(d => {
          if (seenIds.has(d.id)) return;
          seenIds.add(d.id);
          const data = d.data();
          merged.push({
            id: d.id,
            clientName: data.clientName || 'Cliente',
            stars: Number(data.stars || 0),
            comment: data.comment || '',
            timestamp: getTimestamp(data)
          });
        });
      } catch (e) {
        console.error("❌ Error cargando reseñas (subcolección):", e.message);
      }
    };

    // 2. Reseñas guardadas directo en la reserva (llegan por WhatsApp)
    // 💸 FIX DE COSTOS: esta consulta trae las reseñas de TODO el local (el
    // filtro por barbero se hace acá abajo), así que se baja una sola vez
    // por local y por visita, y se reutiliza para cada perfil que se abra
    // — antes se volvía a bajar entera con cada barbero que el cliente
    // miraba. Si falla, se borra de la caché para reintentar la próxima vez.
    const fetchFromBookings = async () => {
      try {
        const locKey = String(locationId);
        if (!resenasWhatsAppPorLocal.has(locKey)) {
          const bookingsQ = query(
            collection(db, "bookings"),
            where("locationId", "==", locKey),
            where("isReviewed", "==", true),
            where("isPrimary", "==", true)
          );
          resenasWhatsAppPorLocal.set(locKey, getDocs(bookingsQ).catch(e => {
            resenasWhatsAppPorLocal.delete(locKey);
            throw e;
          }));
        }
        const snap = await resenasWhatsAppPorLocal.get(locKey);
        snap.forEach(d => {
          if (seenIds.has(d.id)) return;
          const data = d.data();
          const bId = data.barber?.id;
          if (String(bId) !== String(barberDocId) && String(bId) !== String(barber.id)) return;
          if (!data.reviewStars) return;
          if (data.reviewComment?.startsWith?.('Error:')) return;
          seenIds.add(d.id);
          merged.push({
            id: d.id,
            clientName: data.client?.name || 'Cliente',
            stars: Number(data.reviewStars || 0),
            comment: data.reviewComment || '',
            timestamp: getTimestamp(data)
          });
        });
      } catch (e) {
        console.error("❌ Error cargando reseñas (bookings/WhatsApp):", e.message);
      }
    };

    Promise.all([fetchSubcollection(), fetchFromBookings()])
      .then(() => {
        merged.sort((a, b) => b.timestamp - a.timestamp);
        setReviews(merged);
      })
      .finally(() => setLoadingReviews(false));
  }, [isOpen, barber?.id, locationId, isPremiumPlan]);

  const t = getTokens(isDarkMode);
  const ratingCount = barber?.reviewsCount || barber?.ratingSummary?.count || 0;
  const ratingAvg = Number(barber?.rating || barber?.ratingSummary?.average || 0).toFixed(1);
  const bio = (barber?.experience || barber?.bio || '').trim();
  const bioLarga = bio.length > 160;
  // Datos clave en una fila, como la ficha de una app: solo los que existen.
  const datosClave = barber ? [
    isPremiumPlan && ratingCount > 0 && {
      key: 'rating', valor: <><Star size={15} fill="currentColor" className="-mt-0.5" /> {ratingAvg}</>,
      etiqueta: `${ratingCount} reseña${ratingCount === 1 ? '' : 's'}`,
    },
    barber.yearsExperience && { key: 'exp', valor: barber.yearsExperience, etiqueta: `año${Number(barber.yearsExperience) === 1 ? '' : 's'} de experiencia` },
    (barber.category || '').trim().toLowerCase() === 'servicio especializado' && { key: 'nivel', valor: <Award size={20} />, etiqueta: 'Servicio especializado' },
  ].filter(Boolean) : [];
  const haceCuanto = (ts) => (ts ? formatDistanceToNowStrict(new Date(ts), { addSuffix: true, locale: es }) : '');

  // El condicional va DENTRO del AnimatePresence (y no como "return null"
  // antes) para que al cerrar se ejecute la animación de salida.
  return (
    <AnimatePresence>
      {isOpen && barber && (
      <motion.div
        key="barber-profile"
        {...backdropFade}
        className="fixed inset-0 z-[11000] flex items-end md:items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      >
        <motion.div
          initial={sheetHidden}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={sheetHidden}
          transition={sheetTransition}
          {...sheetDrag.panelProps}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Perfil de ${barber.name}`}
          className="w-full md:max-w-md overflow-hidden rounded-t-[28px] md:rounded-[28px] flex flex-col max-h-[92dvh] md:max-h-[88dvh]"
          style={{ willChange: 'transform, opacity', background: t.sheet, color: t.ink }}
        >
          {/* FOTO */}
          <div {...sheetDrag.handleProps} className="relative shrink-0 overflow-hidden touch-none md:touch-auto"
            style={{ background: t.sunk, height: 'min(44dvh, 380px)' }}>
            {!barber.image && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="cv-display" style={{ color: t.inkFaint, fontSize: '8rem' }}>
                  {getInitials(barber.name)}
                </span>
              </div>
            )}
            {barber.image && (
              <img
                src={barber.image}
                alt={barber.name}
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover object-top"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)' }} />

            {/* Asa de la hoja — solo mobile */}
            <div className="md:hidden absolute top-2.5 left-1/2 -translate-x-1/2"><SheetGrabber onImage /></div>

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              aria-label="Cerrar perfil"
              className="cv-glass absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
            >
              <X size={18} />
            </motion.button>

            <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 text-white">
              <h2 className="cv-display" style={{ fontSize: 'clamp(2.75rem, 13vw, 3.5rem)', textWrap: 'balance' }}>{barber.name}</h2>
              <p className="mt-1.5 text-[15px] font-medium" style={{ color: 'rgba(255,255,255,0.82)' }}>
                {barber.title?.trim() || barber.category || 'Profesional'}
              </p>
            </div>
          </div>

          {/* CONTENIDO — con scroll propio; el botón de reservar queda fijo abajo */}
          <div className="cv-scroll flex-1 min-h-0 overflow-y-auto no-scrollbar pb-6">

            {/* Datos clave en fila, separados por líneas finas */}
            {datosClave.length > 0 && (
              <div className="flex px-2 py-4" style={{ borderBottom: `1px solid ${t.line}` }}>
                {datosClave.map((d, i) => (
                  <div key={d.key} className="flex-1 min-w-0 flex flex-col items-center text-center px-2"
                    style={{ borderLeft: i > 0 ? `1px solid ${t.line}` : 'none' }}>
                    <span className="flex items-center gap-1 h-6 text-[19px] font-bold cv-num">{d.valor}</span>
                    <span className="mt-1 text-[12px] font-medium leading-tight" style={{ color: t.inkFaint }}>{d.etiqueta}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="px-6">
              {bio && (
                <div className="pt-5">
                  <p className={`text-[15px] leading-relaxed ${bioLarga && !bioAbierta ? 'line-clamp-3' : ''}`}
                    style={{ color: t.inkSoft, maxWidth: '62ch' }}>
                    {bio}
                  </p>
                  {bioLarga && (
                    <button onClick={() => setBioAbierta(v => !v)}
                      className="mt-1 h-8 text-[14px] font-semibold" style={{ color: brandColor }}>
                      {bioAbierta ? 'Ver menos' : 'Ver más'}
                    </button>
                  )}
                </div>
              )}

              {barber.specialty && (
                <section className="pt-6">
                  <h3 className="text-[17px] font-bold mb-3">Especialidades</h3>
                  <div className="flex flex-wrap gap-2">
                    {barber.specialty.split(',').map(spec => spec.trim()).filter(Boolean).map((spec) => (
                      <span key={spec} className="px-3.5 h-9 inline-flex items-center rounded-full text-[14px] font-medium"
                        style={{ background: t.sunk }}>
                        {spec}
                      </span>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* ⭐ RESEÑAS — solo plan empresarial. Carrusel horizontal de
                tarjetas (como "Calificaciones y reseñas" del App Store): se
                ven de entrada y se deslizan, sin acordeón ni paginador. */}
            {isPremiumPlan && (
              <section className="pt-7">
                <div className="flex items-baseline justify-between px-6 mb-3">
                  <h3 className="text-[17px] font-bold">Reseñas</h3>
                  {ratingCount > 0 && (
                    <span className="flex items-center gap-1 text-[14px] font-semibold cv-num" style={{ color: t.inkSoft }}>
                      <Star size={13} fill={brandColor} color={brandColor} /> {ratingAvg} · {ratingCount}
                    </span>
                  )}
                </div>

                {loadingReviews ? (
                  <div className="flex gap-3 px-6 overflow-hidden" aria-label="Cargando reseñas">
                    {[1, 2].map(i => (
                      <div key={i} className="shrink-0 w-[78%] h-[132px] rounded-[20px] animate-pulse" style={{ background: t.sunk }} />
                    ))}
                  </div>
                ) : reviews.length === 0 ? (
                  <p className="px-6 text-[15px]" style={{ color: t.inkSoft }}>Todavía no tiene reseñas.</p>
                ) : (
                  <ul className="flex gap-3 overflow-x-auto no-scrollbar px-6 pb-1"
                    style={{ scrollSnapType: 'x mandatory', scrollPaddingInline: 24 }}>
                    {reviews.slice(0, 12).map((r) => (
                      <li key={r.id} className="shrink-0 w-[78%] max-w-[300px] p-4 rounded-[20px] flex flex-col"
                        style={{ background: t.sunk, scrollSnapAlign: 'start' }}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-0.5" aria-label={`${r.stars} de 5 estrellas`}>
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} size={13}
                                fill={n <= r.stars ? brandColor : 'transparent'}
                                color={n <= r.stars ? brandColor : t.lineStrong}
                              />
                            ))}
                          </span>
                          <span className="text-[12px] shrink-0" style={{ color: t.inkFaint }}>{haceCuanto(r.timestamp)}</span>
                        </div>
                        <p className="mt-2.5 text-[14px] leading-relaxed line-clamp-4 flex-1" style={{ color: r.comment ? t.ink : t.inkFaint }}>
                          {r.comment || 'Sin comentario'}
                        </p>
                        <span className="mt-3 text-[13px] font-semibold truncate" style={{ color: t.inkSoft }}>
                          {r.clientName || 'Cliente'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {(barber.instagram || barber.facebook) && (
              <div className="flex gap-2 px-6 pt-7">
                {barber.instagram && (
                  <a href={barber.instagram} target="_blank" rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 h-11 rounded-full text-[14px] font-semibold active:scale-[0.98] transition-transform"
                    style={{ boxShadow: `0 0 0 1px ${t.line}`, color: t.ink }}>
                    <Instagram size={16} /> Instagram
                  </a>
                )}
                {barber.facebook && (
                  <a href={barber.facebook} target="_blank" rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 h-11 rounded-full text-[14px] font-semibold active:scale-[0.98] transition-transform"
                    style={{ boxShadow: `0 0 0 1px ${t.line}`, color: t.ink }}>
                    <Facebook size={16} /> Facebook
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Botón fijo abajo: siempre a la vista, aunque el perfil sea largo */}
          <div className="shrink-0 px-6 pt-3 pb-[calc(env(safe-area-inset-bottom)+16px)] md:pb-5"
            style={{ borderTop: `1px solid ${t.line}` }}>
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => { onSelect(barber); onClose(); }}
              className="w-full h-14 rounded-2xl font-bold text-[16px] flex items-center justify-center gap-2"
              style={{
                background: isSelected ? 'transparent' : brandColor,
                color: isSelected ? t.ink : contrastText,
                border: isSelected ? `1.5px solid ${t.lineStrong}` : 'none',
              }}
            >
              {isSelected ? <><CheckCircle size={18} /> Ya lo elegiste</> : `Reservar con ${barber.name.split(' ')[0]}`}
            </motion.button>
          </div>

        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============================================
// TARJETA DE BARBERO
// ============================================
const BarberCard = ({
  barber, isSelected, brandColor, isDarkMode, onSelect, onViewProfile,
  isSingleBarber = false, currentPlan, enabledFeatures, openDays
}) => {
  const t = getTokens(isDarkMode);
  const contrastText = getContrastColor(brandColor);
  // ✅ Mismo candado que en BarberProfileModal — plan empresarial Y el
  // switch de Reseñas habilitado puntualmente para esta empresa.
  const isPremiumPlan = !!enabledFeatures?.reviews; // interruptor del SuperAdmin, cualquier plan
  const hasProfileInfo = !!(barber.bio || barber.experience || barber.specialty || barber.instagram || barber.facebook);
  const ratingCount = barber.reviewsCount || barber.ratingSummary?.count || 0;

  // 📅 DÍAS DE TRABAJO — a pedido, SOLO aparece si en el perfil de este
  // barbero se le cambiaron los días que atiende (workingDays) y quedó
  // trabajando menos días que los que abre la barbería. Si no tiene nada
  // cargado, o tiene marcados todos los días del local, no se muestra nada
  // (aunque la barbería no abra los 7 días).
  const diasLocal = Array.isArray(openDays) && openDays.length > 0 ? openDays : [0, 1, 2, 3, 4, 5, 6];
  const diasPropios = Array.isArray(barber.workingDays) && barber.workingDays.length > 0 ? barber.workingDays : null;
  const diasEfectivos = (diasPropios || []).filter(d => diasLocal.includes(d));
  const diasQueAtiende = diasPropios && diasEfectivos.length > 0 && diasEfectivos.length < diasLocal.length
    ? diasEfectivos.slice().sort((a, b) => a - b)
      .map(d => ({ 0: 'dom', 1: 'lun', 2: 'mar', 3: 'mié', 4: 'jue', 5: 'vie', 6: 'sáb' }[d])).join(', ')
    : null;

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(barber); }
  };

  return (
    <motion.div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(barber)}
      onKeyDown={handleKey}
      className={`relative h-full cursor-pointer overflow-hidden rounded-[20px] ${isSingleBarber ? 'grid grid-cols-[minmax(0,0.85fr)_1fr]' : 'flex flex-col'}`}
      style={{
        background: t.surface,
        boxShadow: isSelected ? `0 0 0 2.5px ${brandColor}` : `0 0 0 1px ${t.line}`,
        transition: 'box-shadow 0.18s ease',
      }}
    >
      {/* FOTO */}
      <div className={`relative overflow-hidden ${isSingleBarber ? 'min-h-[240px]' : 'aspect-[4/5]'}`}
        style={{ background: t.sunk }}>
        <span className="cv-display absolute inset-0 flex items-center justify-center"
          style={{ color: t.inkFaint, fontSize: isSingleBarber ? '5.5rem' : '3.5rem' }}>
          {getInitials(barber.name)}
        </span>
        {barber.image && (
          <img
            src={barber.image}
            alt={barber.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}

        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.16, ease: [0.23, 1, 0.32, 1] }}
              className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: brandColor, color: contrastText }}
            >
              <Check size={16} strokeWidth={3} />
            </motion.div>
          )}
        </AnimatePresence>

        {isPremiumPlan && ratingCount > 0 && (
          <span className="absolute bottom-2.5 left-2.5 flex items-center gap-1 px-2 py-1 rounded-full text-[12px] font-bold cv-num"
            style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
            <Star size={11} fill="#fff" color="#fff" />
            {Number(barber.rating || barber.ratingSummary?.average || 0).toFixed(1)}
            <span style={{ opacity: 0.7, fontWeight: 500 }}>({ratingCount})</span>
          </span>
        )}
      </div>

      {/* DATOS */}
      <div className={`flex flex-col ${isSingleBarber ? 'p-5 justify-end' : 'p-3 pb-3.5 flex-1'}`}>
        <h3 className="cv-display" style={{ fontSize: isSingleBarber ? '2.5rem' : '1.6rem', color: t.ink }}>
          {barber.name}
        </h3>
        <p className="text-[13px] mt-1" style={{ color: t.inkSoft }}>
          {barber.title?.trim() || barber.category || 'Barbero'}
        </p>
        {diasQueAtiende && (
          <p className="text-[12px] font-semibold mt-1.5 flex items-center gap-1" style={{ color: t.ink }}>
            <Calendar size={12} style={{ color: brandColor }} /> Solo {diasQueAtiende}
          </p>
        )}

        {hasProfileInfo && (
          <button
            onClick={(e) => { e.stopPropagation(); onViewProfile(barber); }}
            className="self-start mt-auto pt-2.5 text-[13px] font-semibold underline underline-offset-4"
            style={{ color: t.inkSoft, textDecorationColor: t.lineStrong }}
          >
            Ver perfil
          </button>
        )}
      </div>
    </motion.div>
  );
};
// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const ClientView = ({
  currentUser, handleClientLogout, setShowLoginModal, showLoginModal,
  loading, isDarkMode, themeClasses, shopSettings, logoVersion, clientLoading, setClientLoading,
  view, setView, step, setStep,
  locations, barbers: todosLosBarberos, services,
  selectedLocation, handleSelectLocation, setSelectedLocation,
  selectedBarber, setSelectedBarber,
  selectedServices, handleSelectService, setSelectedServices,
  selectedDate, setSelectedDate,
  selectedTime, setSelectedTime,
  clientInfo, setClientInfo,
  formatOpenDays, getAvailableDates, timeSlots, lunchSlots,
  isBlockFree, getEndTime, totalDuration, totalPrice,
  confirmBooking, deleteBooking, generateGoogleCalendarLink,
  myBookings, searchPhone, setSearchPhone, searchMyBookings,
  showPaymentModal, setShowPaymentModal, showSuccess, setShowSuccess,
  isPaid, setIsPaid, lastBookingId, setLastBookingId,
  paymentMethod, setPaymentMethod, showModal, setShowModal, modalTitle, modalMessage,
  viewServicePhoto, setViewServicePhoto, activeCategory, setActiveCategory,
  phoneError, handlePhoneChange, showAlert, toasts, removeToast, currentPlan, setLoading, submitReview, setBarbers, exchangeRates,
  membershipCoverage, effectiveTotalPrice, myMembership, checkMyMembership,
  membershipPlansAll,
  enabledFeatures, loadingLocations,
  modoDomicilio, setModoDomicilio, datosDomicilio, setDatosDomicilio
}) => {
  const [isReady, setIsReady] = useState(false);

  // 🏠 Servicio a domicilio (ver utils/domicilio.js)
  const ofreceDomicilio = domicilioDisponible(enabledFeatures, shopSettings);
  const domicilioConfig = getDomicilioConfig(shopSettings);
  const [buscandoGps, setBuscandoGps] = useState(false);
  const [errorGps, setErrorGps] = useState('');
  const [linkPegado, setLinkPegado] = useState('');
  const [leyendoLink, setLeyendoLink] = useState(false);
  const resetDomicilio = () => {
    setModoDomicilio(false);
    setDatosDomicilio({ ciudad: '', direccion: '', referencia: '', lat: null, lng: null, origenUbicacion: null, linkUbicacion: '' });
    setErrorGps('');
    setLinkPegado('');
  };
  // Cambiar de modalidad cambia la lista y los precios: se arranca de cero
  const elegirModalidad = (aDomicilio) => {
    if (aDomicilio === modoDomicilio) return;
    setModoDomicilio(aDomicilio);
    setSelectedServices([]);
    setSelectedTime('');
    setActiveCategory('Todos');
  };
  // Si cambió de profesional y este no hace nada a domicilio, vuelve a "En el local"
  // (mismo filtro de categorías que serviciosDelBarbero, más abajo)
  useEffect(() => {
    if (!modoDomicilio || !selectedBarber) return;
    const cats = selectedBarber.allowedCategories || [];
    const haceDomicilio = ofreceDomicilio && (services || []).some(s =>
      esServicioDomicilio(s, domicilioConfig) && (cats.length === 0 || cats.includes(s.category?.trim() || 'General')));
    if (!haceDomicilio) elegirModalidad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoDomicilio, selectedBarber, services, ofreceDomicilio]);
  // 👥 Personas adicionales: se suman a selectedServices como un ítem cada una
  const adicionalesElegidos = (selectedServices || []).filter(s => s.adicionalDomicilioId);
  const personasEnVisita = 1 + adicionalesElegidos.length;
  const maxPersonas = Math.max(1, Number(domicilioConfig.maxPersonas) || 1);
  const cantidadAdicional = (opcionId) => adicionalesElegidos.filter(s => s.adicionalDomicilioId === opcionId).length;
  const sumarAdicional = (opcion) => {
    if (personasEnVisita >= maxPersonas) return;
    setSelectedServices(prev => {
      const usados = prev.filter(s => s.adicionalDomicilioId === opcion.id).map(s => Number(s.id.split('_').pop()) || 0);
      return [...prev, itemPersonaAdicional(opcion, Math.max(0, ...usados) + 1)];
    });
  };
  const restarAdicional = (opcionId) => {
    setSelectedServices(prev => {
      const idx = prev.map(s => s.adicionalDomicilioId).lastIndexOf(opcionId);
      return idx === -1 ? prev : prev.filter((_, k) => k !== idx);
    });
  };
  // Sin la visita base elegida no hay personas adicionales (se sacan solas)
  useEffect(() => {
    const hayBase = (selectedServices || []).some(s => !s.adicionalDomicilioId);
    if (!hayBase && adicionalesElegidos.length > 0) setSelectedServices(prev => prev.filter(s => !s.adicionalDomicilioId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices]);
  // Al cerrar la pantalla de éxito, la próxima reserva vuelve a "En el local"
  useEffect(() => {
    if (!showSuccess) return;
    return () => resetDomicilio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccess]);
  // GPS del celular — gratis, no usa ninguna API de Google
  const usarMiUbicacion = () => {
    if (!navigator.geolocation) return setErrorGps('Tu navegador no permite compartir la ubicación. Escribí la dirección.');
    setBuscandoGps(true);
    setErrorGps('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBuscandoGps(false);
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lng = Number(pos.coords.longitude.toFixed(6));
        if (!dentroDeGranAsuncion(lat, lng)) {
          setDatosDomicilio(prev => ({ ...prev, lat: null, lng: null }));
          return setErrorGps('Tu ubicación está fuera de Gran Asunción. Solo atendemos a domicilio dentro de esa zona.');
        }
        setDatosDomicilio(prev => ({ ...prev, lat, lng, origenUbicacion: 'gps', linkUbicacion: '' }));
      },
      (err) => {
        setBuscandoGps(false);
        setErrorGps(err.code === 1
          ? 'No diste permiso para usar tu ubicación. Podés activarlo en el navegador o seguir solo con la dirección.'
          : 'No pudimos obtener tu ubicación. Probá de nuevo o seguí solo con la dirección.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };
  // 🔗 Link de la ubicación donde va a estar (se lo mandaron por WhatsApp, o lo
  // copió de Google Maps). Los links cortos los resuelve resolverLinkUbicacion.
  const agregarLinkUbicacion = async (texto = linkPegado) => {
    const url = extraerUrl(texto);
    if (!String(texto || '').trim()) return;
    setErrorGps('');
    let coords = coordenadasDeLink(texto);
    if (!coords && esLinkCorto(url)) {
      setLeyendoLink(true);
      try {
        const res = await httpsCallable(functions, 'resolverLinkUbicacion')({ url });
        coords = coordenadasDeLink(res.data?.url);
      } catch (e) {
        console.error('resolverLinkUbicacion:', e.message);
      } finally {
        setLeyendoLink(false);
      }
    }
    if (coords) {
      if (!dentroDeGranAsuncion(coords.lat, coords.lng)) {
        return setErrorGps('Esa ubicación está fuera de Gran Asunción. Solo atendemos a domicilio dentro de esa zona.');
      }
      setDatosDomicilio(prev => ({ ...prev, ...coords, origenUbicacion: 'link', linkUbicacion: url }));
      return setLinkPegado('');
    }
    // Link de mapas del que no se pudo sacar el punto: se guarda igual para el profesional
    if (/^https?:\/\/([a-z0-9-]+\.)*(google\.[a-z.]+|goo\.gl|apple\.com)\//i.test(url) && /map/i.test(url)) {
      setDatosDomicilio(prev => ({ ...prev, lat: null, lng: null, origenUbicacion: 'link', linkUbicacion: url }));
      return setLinkPegado('');
    }
    setErrorGps('No encontramos una ubicación en ese link. Pegá el link de Google Maps o el que te mandaron por WhatsApp.');
  };

  // 🚶 Los profesionales que atienden por orden de llegada (walkInOnly,
  // se marca en Editar barbero) NO se ofrecen para reservar online —
  // solo se nombran en un aviso debajo de la lista.
  const barbers = useMemo(() => (todosLosBarberos || []).filter(b => !b.walkInOnly), [todosLosBarberos]);
  const barberosSinTurno = useMemo(() => (todosLosBarberos || []).filter(b => b.walkInOnly), [todosLosBarberos]);

  // ⏱️ Duración real de un servicio para el barbero elegido — mismo
  // criterio que en App.js: si el barbero tiene una duración
  // personalizada cargada para este servicio puntual, se muestra esa;
  // si no, la estándar del servicio. Así el cliente ve el tiempo real
  // que le va a tomar con ESE barbero, no un número genérico.
  // 🔧 A pedido: se muestra el valor real (sin redondear) — la reserva
  // ahora también guarda la duración exacta, sin forzarla a múltiplos
  // de 30 (ver el cambio en App.js: totalDuration ya no pasa por
  // roundDurationToSlots).
  const getServiceDurationDisplay = (service) => {
    const override = selectedBarber?.serviceDurationOverrides?.[service.id];
    return (override !== undefined && override !== null && override !== '') ? Number(override) : Number(service.duration) || 0;
  };

  // 🎫 "¿Sos socio?" — chequeo opcional en el paso 2 (servicios), para
  // pre-tildar lo que cubre su plan sin cambiar el orden del asistente.
  const [socioPhoneInput, setSocioPhoneInput] = useState('');
  const [checkingSocio, setCheckingSocio] = useState(false);
  const [socioCheckAttempted, setSocioCheckAttempted] = useState(false);

  const [showMembershipInfoModal, setShowMembershipInfoModal] = useState(false);

  // 🚫 Aviso de cupo agotado — el socio ya usó todas las visitas del mes de
  // su plan (ej: Cabo, 4 por mes). Antes de confirmar se le avisa que este
  // turno se cobra al precio normal, o que puede renovar el plan. Se muestra
  // una sola vez por socio y período (clave: socio + vencimiento).
  const [showAvisoSinCupo, setShowAvisoSinCupo] = useState(false);
  // 'socio' = se abrió al reconocerlo en "¿Tenés membresía?" (todavía no
  // eligió servicio); 'confirmar' = en el paso 4, con el precio del turno.
  const [avisoSinCupoOrigen, setAvisoSinCupoOrigen] = useState('confirmar');
  const [avisoSinCupoVisto, setAvisoSinCupoVisto] = useState(null);
  const claveAvisoSinCupo = membershipCoverage?.sinCupo && !membershipCoverage?.covered
    ? `${membershipCoverage.member?.id}_${membershipCoverage.member?.renewalDate || ''}`
    : null;
  const faltaAvisoSinCupo = !!claveAvisoSinCupo && avisoSinCupoVisto !== claveAvisoSinCupo;
  useEffect(() => {
    if (step === 4 && faltaAvisoSinCupo) { setAvisoSinCupoOrigen('confirmar'); setShowAvisoSinCupo(true); }
  }, [step, faltaAvisoSinCupo]);
  // ⏳ Aviso de plan por vencer — al reconocer al socio, si está al día pero
  // le quedan 5 días o menos para renovar (mismo criterio que la etiqueta
  // "Por vencer" del panel de Membresías).
  const [showAvisoPorVencer, setShowAvisoPorVencer] = useState(false);
  const diasParaVencer = (m) => {
    if (!m?.member?.renewalDate || m.expired || m.member.cicloPagado === false) return null;
    const hoyD = new Date(); hoyD.setHours(0, 0, 0, 0);
    const [y, mo, d] = m.member.renewalDate.split('-').map(Number);
    const dias = Math.round((new Date(y, mo - 1, d) - hoyD) / 86400000);
    return dias >= 0 && dias <= 5 ? dias : null;
  };

  // ¿Ya usó todas las visitas del mes de su plan? (planes sin tope nunca)
  const sinCupoDelMes = (m) => !!m?.plan && !m.expired
    && m.plan.visitsPerMonth !== null && m.plan.visitsPerMonth !== undefined
    && (m.member?.visitsUsedThisPeriod || 0) >= m.plan.visitsPerMonth;
  const aceptarAvisoSinCupo = () => {
    if (claveAvisoSinCupo) setAvisoSinCupoVisto(claveAvisoSinCupo);
    setShowAvisoSinCupo(false);
  };

  // 🎫 A pedido — bug encontrado: checkMyMembership solo se disparaba
  // desde el cuadro aparte "¿Tenés membresía?" — si el cliente no usaba
  // ESE cuadro específico y simplemente completaba su teléfono en el
  // paso 4 del flujo normal de reserva, su membresía nunca se chequeaba:
  // myMembership se quedaba en null, y por lo tanto NINGÚN servicio se
  // marcaba como cubierto, ni se aplicaba ningún descuento, ni el total
  // reflejaba el precio de socio — aunque el cliente sí fuera socio.
  // Acá se agrega el mismo chequeo, pero disparado por el teléfono que
  // el cliente ya está completando de todos modos para reservar: en
  // cuanto phoneError queda vacío (número completo y válido), se busca
  // la membresía — sin que el cliente tenga que hacer nada aparte.
  useEffect(() => {
    if (!clientInfo?.phone || phoneError || !selectedLocation?.id || typeof checkMyMembership !== 'function') return;
    checkMyMembership(clientInfo.phone, selectedLocation.id);
  }, [clientInfo?.phone, phoneError, selectedLocation?.id]);

  // 🚫 A pedido de Capelli: clientes que reservan y no asisten se
  // pueden bloquear desde la ficha del cliente (en el panel admin) —
  // acá se verifica, con el mismo disparador que el chequeo de
  // membresía de arriba (teléfono completo), si el número está
  // bloqueado para ESTA empresa. Reusa el mismo documento
  // client_profiles (keyed por companyId_teléfono) que ya existe para
  // la ficha — no hace falta ninguna colección ni endpoint nuevo.
  const [clienteBloqueado, setClienteBloqueado] = useState(null); // null | { reason }
  useEffect(() => {
    if (!clientInfo?.phone || phoneError || !selectedLocation?.companyId) { setClienteBloqueado(null); return; }
    const docId = `${selectedLocation.companyId}_${clientInfo.phone}`;
    getDoc(doc(db, 'client_profiles', docId))
      .then(snap => {
        if (snap.exists() && snap.data().blocked) {
          setClienteBloqueado({ reason: snap.data().blockReason || null });
        } else {
          setClienteBloqueado(null);
        }
      })
      .catch(err => { console.error('Error chequeando bloqueo:', err); setClienteBloqueado(null); });
  }, [clientInfo?.phone, phoneError, selectedLocation?.companyId]);

  // 💰 SEÑA POR TRANSFERENCIA — a pedido, para barberos puntuales con
  // problema de clientes que reservan y no asisten. Si el barbero
  // elegido tiene requiereSeniaTransferencia activado, el cliente tiene
  // que confirmar este check (declarando que va a transferir y a
  // avisar por WhatsApp) para poder finalizar la reserva — reemplaza
  // por completo la elección normal de "Pagar en Local"/"Pagar Online".
  const [confirmoTransferencia, setConfirmoTransferencia] = useState(false);
  useEffect(() => { setConfirmoTransferencia(false); }, [selectedBarber?.id]);

  // Al cambiar de paso, el contenido arranca desde arriba: el contenedor con
  // scroll es el mismo para los pasos 2-4 y si no, conserva la posición del anterior.
  const stepScrollRef = useRef(null);
  // Encabezado compacto del paso 1: aparece cuando el hero sale de pantalla.
  const heroRef = useRef(null);
  const [heroCompacto, setHeroCompacto] = useState(false);
  // Scroll del paso 1 como motion value: la foto del local reacciona al
  // scroll sin re-renderizar React en cada frame.
  const heroScrollY = useMotionValue(0);
  const heroAltoRef = useRef(1);
  const heroProgreso = (v) => Math.min(Math.max(v / heroAltoRef.current, 0), 1);
  // La foto queda casi fija (sube a menos de la mitad de la velocidad del
  // scroll) y se oscurece mientras el panel la tapa.
  const heroFotoY = useTransform(heroScrollY, (v) => Math.max(v, 0) * 0.55);
  // Al tirar hacia abajo desde arriba (rebote de iOS), la foto se estira
  // desde abajo en vez de dejar un hueco arriba — como en las apps nativas.
  const heroFotoScale = useTransform(heroScrollY, (v) => (v < 0 ? 1 + -v / heroAltoRef.current : 1));
  const heroSombra = useTransform(heroScrollY, (v) => heroProgreso(v) * 0.65);
  const heroNombreOpacity = useTransform(heroScrollY, (v) => 1 - Math.min(heroProgreso(v) * 2.2, 1));
  // Pasos 2-4: la línea bajo el encabezado aparece solo cuando hay contenido
  // pasando por debajo (como en iOS).
  const [pasoConScroll, setPasoConScroll] = useState(false);
  useEffect(() => {
    if (stepScrollRef.current) stepScrollRef.current.scrollTop = 0;
    setPasoConScroll(false);
    setHeroCompacto(false);
    heroScrollY.set(0);
  }, [step, heroScrollY]);

  // Encabezado de los pasos 2-4: flota sobre el contenido (material
  // translúcido), así que el contenido arranca debajo de su alto real.
  const pasoHeaderRef = useRef(null);
  const [pasoHeaderAlto, setPasoHeaderAlto] = useState(150);
  const pasosVisibles = view === 'booking' && step > 1 && !showSuccess;
  useEffect(() => {
    const el = pasoHeaderRef.current;
    if (!pasosVisibles || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setPasoHeaderAlto(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [pasosVisibles]);

  // Dirección de la navegación entre pasos, para que el paso nuevo entre
  // desde el lado correcto (se actualiza solo cuando cambia el paso).
  const navDirRef = useRef({ step, dir: 1 });
  if (navDirRef.current.step !== step) {
    navDirRef.current = { step, dir: step > navDirRef.current.step ? 1 : -1 };
  }

  // =====================================================================
  // 📱 BOTÓN / GESTO "ATRÁS" DEL CELULAR — sin esto, el atrás de Android y
  // el swipe de iOS sacaban al cliente de la página en medio de la
  // reserva. Cada avance (y abrir "Mis turnos") deja una entrada en el
  // historial, y "atrás" vuelve un paso en vez de irse. Se conserva el
  // state que ya tenga la entrada (el del router) para no romperlo.
  // =====================================================================
  const pasoInicialRef = useRef(1);
  pasoInicialRef.current = locations.length > 1 ? 0 : 1;
  const navRef = useRef({ step, view, fromPop: false });
  useEffect(() => {
    const prev = navRef.current;
    navRef.current = { step, view, fromPop: false };
    if (prev.fromPop) return;
    const avanzo = (view === 'booking' && prev.view === 'booking' && step > prev.step)
      || (view === 'myBookings' && prev.view !== 'myBookings');
    const entrada = { ...(window.history.state || {}), cvStep: step, cvView: view };
    if (avanzo) window.history.pushState(entrada, '');
    else if (window.history.state?.cvStep !== undefined) window.history.replaceState(entrada, '');
  }, [step, view]);
  useEffect(() => {
    const onPop = (e) => {
      const destino = { step: e.state?.cvStep ?? pasoInicialRef.current, view: e.state?.cvView || 'booking' };
      if (destino.step !== navRef.current.step || destino.view !== navRef.current.view) {
        navRef.current.fromPop = true;
      }
      setIsProfileModalOpen(false);
      setViewServicePhoto(null);
      setView(destino.view);
      setStep(destino.step);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Los botones "volver" de la pantalla usan el historial si la entrada es
  // nuestra, así el atrás del celular y el de la app quedan sincronizados.
  const volverPaso = (pasoDestino = step - 1) => {
    if (window.history.state?.cvStep === step && pasoDestino === step - 1) window.history.back();
    else setStep(pasoDestino);
  };
  const volverDeMisTurnos = () => {
    if (window.history.state?.cvView === 'myBookings') window.history.back();
    else setView('booking');
  };

  // Pantalla de borde a borde mientras se ve la vista del cliente: la foto
  // del local llega hasta el notch y la barra de estado toma el color del
  // fondo. Se restaura al salir, así el panel admin no cambia.
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const prevViewport = viewport?.getAttribute('content');
    if (viewport && !/viewport-fit/.test(prevViewport || '')) {
      viewport.setAttribute('content', `${prevViewport}, viewport-fit=cover`);
    }
    return () => { if (viewport && prevViewport) viewport.setAttribute('content', prevViewport); };
  }, []);
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', getTokens(isDarkMode).bg);
    return () => meta.setAttribute('content', prev);
  }, [isDarkMode]);

  // Recordar la barbería en el teléfono: desde la próxima visita, la
  // pantalla de inicio (index.html) ya muestra su logo y colores al instante.
  useEffect(() => {
    if (!shopSettings?.shopName && !shopSettings?.logoUrl) return;
    saveBrandCache({
      name: shopSettings?.shopName || '',
      logo: shopSettings?.logoUrl || '',
      color: shopSettings?.brandColor || '#F59E0B',
      dark: !!isDarkMode,
    });
  }, [shopSettings?.shopName, shopSettings?.logoUrl, shopSettings?.brandColor, isDarkMode]);
  // La pantalla de inicio se funde con la página cuando ya está lista.
  const [splashSaliendo, setSplashSaliendo] = useState(true);

  const reduceMotion = useReducedMotion();
  const pasoCustom = reduceMotion ? 0 : navDirRef.current.dir;
  const servicioSheet = useSheetDrag(() => setViewServicePhoto(null));
  const membresiaSheet = useSheetDrag(() => setShowMembershipInfoModal(false));
  const sinCupoSheet = useSheetDrag(() => setShowAvisoSinCupo(false));
  const porVencerSheet = useSheetDrag(() => setShowAvisoPorVencer(false));
  const calendarioSheet = useSheetDrag(() => setMostrarCalendarioFechas(false));
  // ⭐ Hoja de reseñas de Google (se abre desde la píldora debajo de la dirección)
  const [verResenasGoogle, setVerResenasGoogle] = useState(false);
  const googleSheet = useSheetDrag(() => setVerResenasGoogle(false));
  const panelExit = getPanelExit(reduceMotion);
  const revealProps = {
    variants: revealVariants,
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    transition: reduceMotion ? revealTransitionReduced : revealTransition,
    style: { overflow: 'hidden' },
  };
  const requiereSenia = !!selectedBarber?.requiereSeniaTransferencia && !(membershipCoverage?.covered && effectiveTotalPrice === 0);
  const porcentajeSenia = Number(shopSettings?.senaTransferencia?.porcentaje) || 50;
  const montoSenia = Math.round(((membershipCoverage?.covered ? effectiveTotalPrice : totalPrice) * porcentajeSenia) / 100);

  const [socioServiciosAplicados, setSocioServiciosAplicados] = useState(false); // evita re-tildar en cada render
  const [socioEsEmail, setSocioEsEmail] = useState(false); // si lo último que buscó fue email, no teléfono
  // Un CI paraguayo típico tiene menos de 9 dígitos — si no es email pero
  // tampoco parece un teléfono real, no le ofrecemos inscribirse con ese
  // mismo dato (la inscripción necesita un teléfono de verdad).
  const pareceTelefono = !socioEsEmail && /^\d{9,15}$/.test(socioPhoneInput);

  // 🔢 Validación del dato del cuadro "¿Tenés membresía?" antes de buscar:
  // celular (9 a 15 dígitos, para admitir números de Brasil u otros
  // países), CI (5 a 8 dígitos) o email. Antes, un
  // número larguísimo (ej: 0971628824999999999999) se colaba porque el
  // campo lo tomaba como "email a medio escribir".
  const [socioInputError, setSocioInputError] = useState('');
  const validarDatoSocio = (val) => {
    const v = String(val || '').trim();
    if (!v) return 'Ingresá tu celular, CI o email.';
    if (v.includes('@')) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Ese email no es válido.';
    if (!/^\d+$/.test(v)) return 'Ingresá solo números (celular o CI) o un email.';
    if (v.length >= 9 && v.length <= 15) return ''; // celular
    if (v.length >= 5 && v.length <= 8) return ''; // CI
    return 'Ingresá tu celular (ej: 0981234567) o tu CI.';
  };

  const handleVerificarSocio = async () => {
    if (!socioPhoneInput || !selectedLocation?.id || typeof checkMyMembership !== 'function') return;
    const errorDato = validarDatoSocio(socioPhoneInput);
    setSocioInputError(errorDato);
    if (errorDato) { setSocioCheckAttempted(false); return; }
    const esEmail = socioPhoneInput.includes('@');
    setSocioEsEmail(esEmail);
    setCheckingSocio(true);
    setSocioServiciosAplicados(false);
    // Usamos el resultado DIRECTO de la función (no el estado global
    // myMembership) para decidir si abrir el modal — así el modal solo
    // aparece por esta acción puntual, no por otros lugares del archivo
    // que también llaman a checkMyMembership (como "Mis Reservas").
    const resultado = await checkMyMembership(socioPhoneInput, selectedLocation.id);
    if (resultado && sinCupoDelMes(resultado)) {
      // 🚫 Ya usó todas sus visitas: en vez del resumen del plan, el aviso de
      // "esta sería tu quinta visita" (precio normal o renovar). Queda
      // marcado como visto para no repetirlo al confirmar.
      setAvisoSinCupoOrigen('socio');
      setAvisoSinCupoVisto(`${resultado.member?.id}_${resultado.member?.renewalDate || ''}`);
      setShowAvisoSinCupo(true);
    } else if (resultado && diasParaVencer(resultado) !== null) {
      setShowAvisoPorVencer(true);
    } else if (resultado) {
      setShowMembershipInfoModal(true);
    }
    setSocioCheckAttempted(true);
    setCheckingSocio(false);
    // ✅ FIX: antes, cualquier cosa que NO tuviera "@" se guardaba como
    // teléfono sin más — si el cliente se identificaba con su CI, el CI
    // terminaba mal guardado en el campo de teléfono del paso 4 (y como
    // acá no hay campo de CI visible, quedaba pegado ahí sin forma de
    // corregirlo). Ahora usamos los datos REALES que ya están guardados
    // en su ficha de socio (nombre, teléfono, email, CI) — sin importar
    // con cuál de los tres lo encontramos, el teléfono que se completa
    // es siempre su teléfono de verdad.
    if (typeof setClientInfo === 'function') {
      if (resultado?.member) {
        setClientInfo(prev => ({
          ...prev,
          name: prev.name || resultado.member.clientName || '',
          // Formato local (0981...) si es paraguayo; si el guardado no es válido, queda el que escribió
          phone: telefonoValido(claveTelefono(resultado.member.clientPhone)) ? claveTelefono(resultado.member.clientPhone) : prev.phone,
          email: resultado.member.clientEmail || prev.email,
          ci: resultado.member.clientCi || prev.ci,
        }));
      } else if (esEmail) {
        // No lo encontramos, pero guardamos el email igual — si sigue
        // con la reserva (ej. para inscribirse), no hace falta pedírselo
        // de nuevo en el paso 4.
        setClientInfo(prev => ({ ...prev, email: socioPhoneInput }));
      } else if (/^\d{9,15}$/.test(socioPhoneInput)) {
        // No lo encontramos, pero lo que escribió tiene 9+ dígitos — un
        // CI paraguayo normalmente es más corto, así que esto es casi
        // seguro un teléfono real. Si es más corto, no lo tocamos (evita
        // repetir el mismo bug con un CI que tampoco se encontró).
        setClientInfo(prev => ({ ...prev, phone: socioPhoneInput }));
      }
    }
  };

  // Apenas myMembership trae un plan, tildamos la tarjeta del PLAN en sí
  // (el mismo pseudo-servicio "plan_{id}" que se arma más abajo para el
  // selector de Membresía) — así el cliente ve "Black" seleccionado con
  // su precio tachado, no cada servicio suelto por separado. Sin
  // bloquear: el cliente puede sacarlo o agregar cualquier otro servicio
  // igual; lo que no esté cubierto por el plan se suma aparte.
  //
  // 🔧 A pedido: si la membresía está vencida (myMembership.expired) O si
  // ya no tiene cupo de visitas disponible este mes, NO se auto-selecciona
  // nada — el socio queda tratado como cliente normal (precio real) hasta
  // que pague o le llegue el próximo ciclo, sin el descuento ni el
  // pseudo-servicio del plan metiéndose solo en su selección.
  useEffect(() => {
    const cupoDisponible = myMembership?.plan?.visitsPerMonth === null || myMembership?.plan?.visitsPerMonth === undefined
      || (myMembership?.member?.visitsUsedThisPeriod || 0) < myMembership?.plan?.visitsPerMonth;
    if (!myMembership?.plan?.id || myMembership?.expired || !cupoDisponible || socioServiciosAplicados) return;
    if (typeof handleSelectService === 'function') {
      const plan = myMembership.plan;
      const planServiceId = `plan_${plan.id}`;
      const yaSeleccionado = (selectedServices || []).some(s => s.id === planServiceId);
      if (!yaSeleccionado) {
        const serviciosIncluidos = plan.allowedServiceIds && plan.allowedServiceIds.length > 0
          ? (services || []).filter(s => plan.allowedServiceIds.includes(s.id))
          : [];
        // ⏱️ A pedido: si el plan tiene duracionFija cargada (ej: Eco 30
        // min, Black 1 hora, Silver 40 min), esa manda siempre — el socio
        // bloquea ese tiempo fijo sin importar qué servicio elija. Sin
        // esto cargado, sigue el cálculo de siempre (suma la duración de
        // los servicios que el plan cubre, o 30 min por defecto).
        const duracionPlan = (plan.duracionFija !== undefined && plan.duracionFija !== null)
          ? Number(plan.duracionFija)
          : serviciosIncluidos.length > 0
          ? serviciosIncluidos.reduce((sum, s) => sum + Number(s.duration || 0), 0)
          : 30;
        handleSelectService({
          id: planServiceId,
          name: plan.name,
          // 💰 A pedido — bug corregido: antes esto usaba plan.price (la
          // CUOTA MENSUAL que paga el cliente), mezclándola con el valor
          // que se usa para calcular la comisión del barbero en la Caja
          // de Membresías. Son dos montos totalmente distintos — ahora
          // usa comisionPorVisita, el campo separado para esto.
          price: plan.comisionPorVisita || 0,
          duration: duracionPlan,
          category: 'Membresía',
          isMembershipPlan: true,
          planId: plan.id,
          includedServiceNames: serviciosIncluidos.map(s => s.name),
        });
      }
      // 📍 Sin esto, el cliente no tiene forma de saber que ya quedó
      // seleccionado — la tarjeta puede estar bien más abajo en la
      // lista, fuera de lo que se ve sin scrollear. Un toquecito de
      // demora para que el DOM termine de pintar la tarjeta antes de
      // buscarla.
      setTimeout(() => {
        document.getElementById(`service-card-${planServiceId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    setSocioServiciosAplicados(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myMembership]);

  const [viewBarberInfo, setViewBarberInfo] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // --- ESTADOS PARA RESEÑAS ---
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewStars, setReviewStars] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  // -----------------------------

  const slotsNeeded = Math.ceil(totalDuration / 30);

  // 🎫 A pedido: si el cliente eligió reservar CON SU PLAN (la tarjeta
  // "plan_{id}" en selectedServices), y ese plan tiene días permitidos
  // cargados (allowedDays — ej: solo lunes/martes/miércoles), hay que
  // pasárselos a getAvailableDates para que el calendario oculte
  // cualquier otro día. Sin plan elegido, o plan sin esa restricción,
  // queda en null — mismo comportamiento de siempre.
  const planElegidoParaFecha = selectedServices.find(s => s.isMembershipPlan);
  const allowedDaysDelPlanElegido = planElegidoParaFecha
    ? (membershipPlansAll || []).find(p => String(p.id) === String(planElegidoParaFecha.planId))?.allowedDays || null
    : null;

  // 🔧 A pedido — bug de rendimiento: getAvailableDates(selectedBarber)
  // puede recorrer hasta 120 días buscando fechas válidas, y antes se
  // llamaba de nuevo en CADA render (una vez para los chips de fecha,
  // y otra al calcular sugerencias de días alternativos) — incluyendo
  // en cada clic que no cambia el barbero para nada. Se memoriza acá,
  // a nivel del componente (como corresponde un hook), para que solo
  // se recalcule cuando el barbero elegido (o el plan elegido) realmente
  // cambian.
  const availableDatesForBarber = useMemo(
    () => getAvailableDates(selectedBarber, allowedDaysDelPlanElegido),
    [selectedBarber?.id, selectedBarber?.workingDays, selectedBarber?.workStartTime, selectedBarber?.workEndTime, JSON.stringify(allowedDaysDelPlanElegido), modoDomicilio]
  );

  // 📅 A pedido: selector de calendario por mes, para que el cliente
  // pueda saltar directo a una fecha de otro mes (no solo los próximos
  // días que entran en la fila de chips). Vive aparte, como un ícono
  // chico al lado del título "Selecciona el Día" — no reemplaza los
  // chips existentes ni cambia su diseño, solo da una forma extra de
  // llegar a una fecha lejana.
  const [mostrarCalendarioFechas, setMostrarCalendarioFechas] = useState(false);
  const [mesCalendarioVisible, setMesCalendarioVisible] = useState(() => new Date());

  // 🔧 A pedido — bug corregido: el calendario dependía de una función
  // (isDateBookable) pasada como prop nueva desde App.js — por algún
  // motivo esa prop no estaba llegando bien (seguía cayendo al límite
  // viejo de availableDatesForBarber, que corta a los ~8 días). Ahora
  // esta misma función vive ACÁ DENTRO, calculada directo con
  // shopSettings (que ya es una prop de siempre de este componente) —
  // sin depender de que App.js le pase nada nuevo. Mismos 3 filtros de
  // siempre: la barbería abre ese día, el barbero puntual trabaja ese
  // día, y (si corresponde) el plan elegido lo permite.
  const chequearDiaDisponible = (dateStr, barber, allowedDaysPlan) => {
    if (!shopSettings || !shopSettings.openDays) return false;
    const hoyStr = format(new Date(), 'yyyy-MM-dd');
    if (dateStr < hoyStr) return false;
    const dayOfWeek = getDay(parse(dateStr, 'yyyy-MM-dd', new Date()));
    const openDays = Array.isArray(shopSettings.openDays) ? shopSettings.openDays : [];
    const diasDelBarbero = (barber && Array.isArray(barber.workingDays) && barber.workingDays.length > 0) ? barber.workingDays : null;
    const barberiaAbierta = openDays.includes(dayOfWeek);
    const barberoTrabaja = diasDelBarbero === null || diasDelBarbero.includes(dayOfWeek);
    const planPermiteEsteDia = !allowedDaysPlan || allowedDaysPlan.length === 0 || allowedDaysPlan.includes(dayOfWeek);
    return barberiaAbierta && barberoTrabaja && planPermiteEsteDia;
  };

  // 🕐 A pedido: HORARIO DEL BARBERO EN UNA FECHA PUNTUAL. Antes el barbero tenía un solo
  // horario (workStartTime/workEndTime) igual para todos sus días. Ahora, si tiene activado
  // "horario distinto según el día" (hasVariableHours), el horario de ESE día de la semana
  // (workSchedule) reemplaza al general; un día sin horario cargado usa el general. Sin el
  // modo activado, todo funciona igual que siempre.
  const horarioDelBarberoEnFecha = (barber, dateStr) => {
    let start = barber?.workStartTime || '';
    let end = barber?.workEndTime || '';
    if (barber?.hasVariableHours && barber.workSchedule && dateStr) {
      const dia = parse(dateStr, 'yyyy-MM-dd', new Date()).getDay();
      const h = barber.workSchedule[dia] || barber.workSchedule[String(dia)];
      if (h && (h.start || h.end)) { start = h.start || start; end = h.end || end; }
    }
    return { start, end };
  };

  // ¿El barbero puede atender a partir de esa hora, en esa fecha? Además de que la hora de
  // inicio caiga dentro de su horario, el turno COMPLETO (bloques de 30 min) tiene que terminar
  // a más tardar a su hora de salida — antes un turno largo podía arrancar justo antes de que
  // se fuera y terminar después.
  const barberoAtiendeEnHorario = (barber, dateStr, time, bloquesNecesarios) => {
    let { start, end } = horarioDelBarberoEnFecha(barber, dateStr);
    // 🏠 A domicilio: el barbero que trabaja hasta el cierre del local sigue
    // hasta la hora de domicilio (ej: 22:00). Uno que sale antes, no.
    if (modoDomicilio && end && dateStr) {
      const dia = parse(dateStr, 'yyyy-MM-dd', new Date()).getDay();
      const cierreLocal = (shopSettings?.hasVariableDays && shopSettings.variableSchedule?.[dia]?.closeTime) || shopSettings?.closeTime;
      if (cierreLocal && end >= cierreLocal && domicilioConfig.horaFin > end) end = domicilioConfig.horaFin;
    }
    if (start && time < start) return false;
    if (end) {
      if (time >= end) return false;
      const aMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      if (aMin(time) + Math.max(1, Number(bloquesNecesarios) || 1) * 30 > aMin(end)) return false;
    }
    // 🍽️ Almuerzo propio del barbero: el turno completo no puede pisarlo.
    if (barber?.lunchStart && barber?.lunchEnd) {
      const aMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      const ini = aMin(time);
      const fin = ini + Math.max(1, Number(bloquesNecesarios) || 1) * 30;
      if (ini < aMin(barber.lunchEnd) && fin > aMin(barber.lunchStart)) return false;
    }
    return true;
  };

  // 🎫 A pedido: si el cliente ya tenía una fecha elegida y DESPUÉS
  // selecciona el plan (que restringe los días permitidos), esa fecha
  // puede dejar de ser válida — se resetea junto con la hora, para que
  // no quede guardado un turno en un día que el plan no permite.
  useEffect(() => {
    if (selectedDate && allowedDaysDelPlanElegido && !availableDatesForBarber.includes(selectedDate)) {
      setSelectedDate('');
      setSelectedTime('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDatesForBarber]);

  // Función para garantizar que el WhatsApp siempre tenga el formato correcto
  // Función a prueba de balas para números de Paraguay
  const formatearEnlaceWA = (telefono) => {
    if (!telefono) return '#';

    // 1. Quitamos espacios, guiones y el signo +
    let limpio = String(telefono).replace(/\D/g, '');

    // 2. Extraemos SOLO los últimos 9 dígitos (ej: 992594485)
    // Esto borra automáticamente cualquier 0, 595 o 9 extra que esté al principio
    let ultimos9 = limpio.slice(-9);

    // 3. Devolvemos el enlace con el 595 oficial siempre perfecto
    return `https://wa.me/595${ultimos9}`;
  };

  const stepTitles = {
    2: "Elegí tus servicios",
    3: "Elegí día y hora",
    4: "Confirmá tu turno"
  };

  const getBookingSlots = (startTime, slotsCount) => {
    const slots = [];
    if (!startTime) return slots;
    let [hours, minutes] = startTime.split(':').map(Number);
    for (let i = 0; i < slotsCount; i++) {
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
      minutes += 30;
      if (minutes >= 60) { hours += 1; minutes -= 60; }
    }
    return slots;
  };

  const verificarYPasarAlSiguiente = async () => {
    if (!selectedTime) return showAlert('Error', 'Debes seleccionar un horario.');
    if (typeof setClientLoading === 'function') setClientLoading(true);
    try {
      const timeToMins = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      const currentBarberId = String(selectedBarber.id).trim();
      const currentLocId = String(selectedLocation?.id || '').trim();
      const slotsToBook = getBookingSlots(selectedTime, slotsNeeded);

      if (!slotsToBook || slotsToBook.length === 0) {
        if (typeof setClientLoading === 'function') setClientLoading(false);
        return showAlert('Error', 'El horario seleccionado excede el horario de cierre.');
      }

      // ⚡ FIX: antes esto traía TODOS los bloqueos y reservas de TODAS
      // las barberías de la plataforma para esa fecha (filtrando recién
      // después, ya en el navegador), y las dos consultas se esperaban
      // una atrás de la otra. Mismo arreglo que ya hicimos en
      // confirmBooking: filtrar por sucursal directo en la consulta, y
      // lanzar las dos juntas con Promise.all.
      const [blocksSnap, bookingsSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'blocked_times'),
          where('date', '==', selectedDate),
          where('locationId', '==', currentLocId)
        )),
        getDocs(query(
          collection(db, 'bookings'),
          where('date', '==', selectedDate),
          where('locationId', '==', currentLocId)
        ))
      ]);

      let estaBloqueado = false;
      blocksSnap.forEach((docSnap) => {
        const block = docSnap.data();
        if (block.deleted) return; // desbloqueado (queda solo como registro)
        const blockBarbId = block.barberId ? String(block.barberId).trim() : null;
        if (blockBarbId && blockBarbId !== currentBarberId) return;
        const blockStart = timeToMins(block.startTime);
        const blockEnd = timeToMins(block.endTime);
        const choca = slotsToBook.some((slot) => {
          const slotMins = timeToMins(slot);
          return slotMins >= blockStart && slotMins < blockEnd;
        });
        if (choca) estaBloqueado = true;
      });
      if (estaBloqueado) {
        if (typeof setClientLoading === 'function') setClientLoading(false);
        return showAlert('Horario Ocupado', 'Este horario acaba de ser bloqueado. Elige otro.');
      }

      let hayColision = false;
      bookingsSnap.forEach((docSnap) => {
        const b = docSnap.data();
        if (b.status === 'cancelled' || b.status === 'no_show' || b.isManualEntry) return;
        const bBarbId = b.barber?.id ? String(b.barber.id).trim() : null;
        if (bBarbId && bBarbId !== currentBarberId) return;
        if (slotsToBook.includes(b.time || b.startTime)) {
          hayColision = true;
        }
      });
      if (hayColision) {
        if (typeof setClientLoading === 'function') setClientLoading(false);
        return showAlert('Horario Ocupado', 'Alguien más acaba de reservar este horario. Por favor, elige otro.');
      }

      setStep(4);
    } catch (error) {
      console.error(error);
      showAlert('Error', 'No pudimos verificar la disponibilidad. Intenta nuevamente.');
    } finally {
      if (typeof setClientLoading === 'function') setClientLoading(false);
    }
  };

// ✅ Apaga el spinner apenas hay datos usables
  useEffect(() => {
    if (locations && locations.length > 0 &&
        (shopSettings?.brandColor || shopSettings?.logoUrl || shopSettings?.shopName)) {
      setIsReady(true);
    }
  }, [locations, shopSettings]);

  // 🛡️ RED DE SEGURIDAD ABSOLUTA: usa una ref para que NO se reinicie
  // aunque el componente se re-renderice. Apaga el spinner sí o sí a los 3s.
  const readyTimerRef = useRef(null);
  useEffect(() => {
    if (isReady) return;
    if (readyTimerRef.current) return; // ya hay un timer corriendo, no lo reiniciamos
    readyTimerRef.current = setTimeout(() => {
      setIsReady(true);
    }, 3000);
    return () => {
      // solo limpiamos si el componente se desmonta de verdad
    };
  }, [isReady]);

  const brandColor = shopSettings?.brandColor || '#F59E0B';
  const contrastText = getContrastColor(brandColor);

  const getTicketNumber = (id) => {
    if (!id) return '-----';
    let hash = 0;
    for (let i = 0; i < id.length; i++) { hash = Math.imul(31, hash) + id.charCodeAt(i) | 0; }
    return Math.abs(hash % 100000).toString().padStart(5, '0');
  };

  // ✅ Se sacó la conversión a moneda alternativa de la vista del cliente
  // a pedido — todos los precios quedan solo en guaraníes acá. La
  // calculadora del admin (ModernAdminLayout) y el total convertido en
  // el modal de Editar Reserva siguen funcionando igual, sin cambios.

  // =====================================================================
  // 💬 MENSAJE DE ÉXITO DINÁMICO — nuevo flujo, igual para todos los planes
  // - Al reservar NO se manda ningún WhatsApp: esta pantalla (con el
  //   resumen y el ticket) es la confirmación.
  // - Turno en <180 min (3 hs) → el backend lo autoconfirma, sin
  //   recordatorio ni botones → avisamos que está confirmado.
  // - Turno con 3 hs o más → le llega UN recordatorio con botones
  //   "Confirmar" / "Cancelar": 3 hs antes si es el mismo día, 6 hs antes
  //   si es otro día.
  // Esto es SOLO texto de pantalla; la fuente de verdad sigue siendo el
  // backend.
  // =====================================================================
  const minutosHastaTurno = getMinutosHastaTurno(selectedDate, selectedTime);
  const esTurnoInminente = minutosHastaTurno !== null && minutosHastaTurno < 180;
  const horasRecordatorio = selectedDate === format(new Date(), 'yyyy-MM-dd') ? 3 : 6;

  let successTitle = '¡Turno reservado!';
  let successSubtitle = `Esta es la confirmación de tu turno: guardá este resumen. ${horasRecordatorio} horas antes te llega un recordatorio por WhatsApp para confirmar o cancelar.`;
  let successIcon = <CheckCircle size={26} />;
  let successTone = brandColor;

  if (isPaid) {
    successTitle = '¡Pago exitoso!';
    successSubtitle = 'Tu turno está confirmado.';
    successIcon = <CheckCircle size={26} />;
    successTone = OK;
  } else if (requiereSenia) {
    successTitle = 'Falta la seña';
    successSubtitle = 'Tenés 15 minutos para transferir y avisarnos por WhatsApp. Si no llega el comprobante a tiempo, el turno se libera solo.';
    successIcon = <Banknote size={26} />;
    successTone = WARN;
  } else if (esTurnoInminente) {
    successTitle = '¡Confirmado!';
    successSubtitle = 'Tu turno ya está confirmado. ¡Te esperamos!';
    successTone = OK;
  }
  const successToneText = getContrastColor(successTone);

  // Base de color por modo — ver getTokens arriba.
  const t = getTokens(isDarkMode);
  const bg = t.bg;
  const surface = t.surface;
  const surfaceBorder = t.line;
  const textPrimary = t.ink;
  const textSecondary = t.inkSoft;
  const cssVars = getCssVars(t, brandColor);

  const capitalizar = (txt) => (txt ? txt.charAt(0).toUpperCase() + txt.slice(1) : '');
  const fechaLarga = (dateStr) => capitalizar(format(parse(dateStr, 'yyyy-MM-dd', new Date()), "EEEE d 'de' MMMM", { locale: es }));

  // Precio de cada servicio tal como se va a cobrar — mismo criterio en el
  // resumen del paso 4 y en la pantalla de éxito.
  const renderPrecioLinea = (s) => {
    // 🎫 Descuento de socio por servicio, no solo en el total final.
    const planCubreEsteServicio = membershipCoverage?.covered && myMembership?.plan && (
      !myMembership.plan.allowedServiceIds || myMembership.plan.allowedServiceIds.length === 0 || myMembership.plan.allowedServiceIds.includes(s.id)
    );
    const tienePrecioSocio = !s.isMembershipPlan && planCubreEsteServicio && s.memberPrice !== undefined && s.memberPrice !== null && s.memberPrice !== '';
    // El pseudo-servicio del plan tiene price = comisionPorVisita (un
    // valor interno), nunca un cargo real de esta visita → "Incluido".
    if (s.isMembershipPlan) return <span className="font-semibold shrink-0" style={{ color: GOLD }}>Incluido</span>;
    if (tienePrecioSocio) {
      return (
        <span className="shrink-0 flex items-baseline gap-1.5 cv-num">
          <span className="text-[13px] line-through" style={{ color: t.inkFaint }}>{formatPrice(s.price)}</span>
          <span className="font-semibold" style={{ color: GOLD }}>{formatPrice(s.memberPrice)}</span>
        </span>
      );
    }
    return <span className="font-semibold shrink-0 cv-num">{formatPrice(s.price)}</span>;
  };

  // Funciones de render (no componentes) para que la foto no se vuelva a
  // montar en cada render.
  const renderAvatar = (person, size = 44, radius = 12) => (
    <div className="relative shrink-0 overflow-hidden flex items-center justify-center cv-display"
      style={{ width: size, height: size, borderRadius: radius, background: t.sunk, color: textPrimary, fontSize: size * 0.42 }}>
      {getInitials(person?.name)}
      {person?.image && (
        <img src={person.image} alt="" className="absolute inset-0 w-full h-full object-cover object-top"
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      )}
    </div>
  );

  const renderShopMark = (size = 56) => (
    <div className="shrink-0 overflow-hidden flex items-center justify-center"
      style={{ width: size, height: size, borderRadius: size * 0.28, background: t.surface, boxShadow: `0 0 0 1px ${t.line}` }}>
      {shopSettings?.logoUrl ? (
        <img src={shopSettings.logoUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="cv-display" style={{ color: brandColor, fontSize: size * 0.55 }}>
          {shopSettings?.shopName?.charAt(0) || 'B'}
        </span>
      )}
    </div>
  );

  if (!isReady) {
    return (
      <>
        <style>{premiumStyles}</style>
        {/* Misma pantalla de inicio que index.html y App (ver BrandSplash) */}
        <BrandSplash
          shopName={shopSettings?.shopName}
          logoUrl={shopSettings?.logoUrl}
          brandColor={shopSettings?.brandColor ? brandColor : undefined}
          isDarkMode={isDarkMode}
        />
      </>
    );
  }

  const UserAuthButton = ({ isCompact = false, onImage = false }) => {
    const chrome = onImage
      ? {}
      : { background: t.surface, color: textPrimary, boxShadow: `0 0 0 1px ${t.line}` };
    const chromeClass = onImage ? 'cv-glass ' : '';
    if (currentUser) {
      return (
        <motion.button whileTap={{ scale: 0.95 }} onClick={handleClientLogout}
          aria-label="Cerrar sesión"
          className={`${chromeClass}flex items-center gap-2 h-10 rounded-full`}
          style={{ ...chrome, padding: isCompact ? 4 : '4px 12px 4px 4px' }}>
          <span className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold"
            style={{ background: brandColor, color: contrastText }}>
            {currentUser.displayName ? currentUser.displayName[0].toUpperCase() : 'C'}
          </span>
          {!isCompact && <span className="text-[13px] font-semibold">Salir</span>}
        </motion.button>
      );
    }
    return (
      <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowLoginModal(true)}
        aria-label="Iniciar sesión"
        className={`${chromeClass}flex items-center justify-center gap-1.5 h-10 rounded-full text-[14px] font-semibold`}
        style={{ ...chrome, width: isCompact ? 40 : 'auto', padding: isCompact ? 0 : '0 16px' }}>
        <User size={16} />
        {!isCompact && 'Ingresar'}
      </motion.button>
    );
  };


  const pasos = ['Profesional', 'Servicios', 'Horario', 'Tus datos'];

  // Servicios del paso 2 — se calculan acá (y no dentro del paso) porque las
  // categorías se muestran en el encabezado, fuera del contenido con scroll.
  const categoriasPermitidas = selectedBarber?.allowedCategories || [];
  const serviciosDelBarbero = services.filter(s => {
    const catServicio = s.category?.trim() || 'General';
    if (categoriasPermitidas.length > 0) return categoriasPermitidas.includes(catServicio);
    return true;
  });

  // 🎫 Planes de membresía como "servicios" seleccionables —
  // sacados directo de Membresías → Planes, sin duplicar
  // datos en ningún lado. No se filtran por barbero (una
  // suscripción no es específica de quién atiende).
  // 🔒 Solo el plan del socio reconocido (y no vencido): las altas las hace
  // el admin, así que a un cliente sin membresía no se le muestran planes.
  const planesComoServicios = (membershipPlansAll || [])
    .filter(plan => myMembership?.plan?.id === plan.id && !myMembership?.expired && !sinCupoDelMes(myMembership))
    .map(plan => {
    const serviciosIncluidos = plan.allowedServiceIds && plan.allowedServiceIds.length > 0
      ? services.filter(s => plan.allowedServiceIds.includes(s.id))
      : [];
    // ⏱️ A pedido: mismo criterio que en la auto-selección
    // — duracionFija del plan manda si está cargada.
    const duracionPlan = (plan.duracionFija !== undefined && plan.duracionFija !== null)
      ? Number(plan.duracionFija)
      : serviciosIncluidos.length > 0
      ? serviciosIncluidos.reduce((sum, s) => sum + Number(s.duration || 0), 0)
      : 30; // sin restricción de servicio — duración por defecto
    return {
      id: `plan_${plan.id}`,
      name: plan.name,
      // 💰 A pedido — mismo criterio que la
      // auto-selección: comisionPorVisita, no
      // plan.price (eso es la cuota del cliente).
      price: plan.comisionPorVisita || 0,
      duration: duracionPlan,
      category: 'Membresía',
      isMembershipPlan: true,
      planId: plan.id,
      // Nombres de los servicios reales que este plan cubre —
      // se guardan acá para que el panel admin (calendario,
      // "Editar Reserva", etc.) los pueda mostrar sin tener
      // que ir a buscar el plan de nuevo a Firestore.
      includedServiceNames: serviciosIncluidos.map(s => s.name),
    };
  });

  // 🏠 A domicilio: solo los servicios de la categoría a domicilio (con su
  // precio normal); "En el local" los oculta, para que nadie reserve una
  // visita a domicilio sin cargar la dirección. Los planes de membresía no aplican.
  const serviciosDomicilio = ofreceDomicilio ? serviciosDelBarbero.filter(s => esServicioDomicilio(s, domicilioConfig)) : [];
  const mostrarSelectorDomicilio = serviciosDomicilio.length > 0;
  const serviciosDelBarberoConPlanes = (modoDomicilio && mostrarSelectorDomicilio)
    ? serviciosDomicilio
    : [...serviciosDelBarbero.filter(s => !serviciosDomicilio.includes(s)), ...planesComoServicios];
  const categoriasDisponibles = ['Todos', ...new Set(serviciosDelBarberoConPlanes.map(s => s.category?.trim() || 'General'))];
  const serviciosAMostrar = serviciosDelBarberoConPlanes.filter(s =>
    activeCategory === 'Todos' || (s.category?.trim() || 'General') === activeCategory
  );
  const mostrarCategorias = serviciosDelBarberoConPlanes.length > 0
    && (categoriasDisponibles.length > 2 || activeCategory !== 'Todos');
  const elegirCategoria = (cat) => {
    setActiveCategory(cat);
    // La lista cambia entera: se vuelve al principio para verla desde arriba.
    stepScrollRef.current?.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  const sheetStyle = { background: t.sheet, color: textPrimary };
  const goldBox = { background: isDarkMode ? 'rgba(201,162,58,0.1)' : 'rgba(201,162,58,0.09)', border: `1px solid rgba(201,162,58,0.35)` };
  const goldText = isDarkMode ? GOLD : '#8A6A12';

  return (
    <>
      <style>{premiumStyles}</style>
      <div className="cv-root min-h-[100dvh] w-full flex flex-col relative overflow-hidden"
        style={{ ...cssVars, background: bg, color: textPrimary }}>

        {/* La pantalla de inicio queda encima un instante y se desvanece,
            así la página aparece con un fundido y no de golpe. */}
        {splashSaliendo && (
          <motion.div className="fixed inset-0 z-[20000] pointer-events-none"
            initial={{ opacity: 1 }} animate={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0.15 : 0.35, delay: 0.05, ease: [0.23, 1, 0.32, 1] }}
            onAnimationComplete={() => setSplashSaliendo(false)}>
            <BrandSplash
              shopName={shopSettings?.shopName}
              logoUrl={shopSettings?.logoUrl}
              brandColor={shopSettings?.brandColor ? brandColor : undefined}
              isDarkMode={isDarkMode}
            />
          </motion.div>
        )}

        {/* MODAL PERFIL BARBERO */}
        <BarberProfileModal
          barber={viewBarberInfo}
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          brandColor={brandColor}
          isDarkMode={isDarkMode}
          onSelect={(barber) => { setSelectedBarber(barber); setStep(2); }}
          selectedBarber={selectedBarber}
          currentPlan={currentPlan}
          locationId={selectedLocation?.id}
          enabledFeatures={enabledFeatures}
        />

        {/* MODAL INFORMATIVO DE MEMBRESÍA — aparece apenas se reconoce al
            socio por teléfono/CI/email en el cuadro "¿Tenés membresía?".
            Resume el plan completo: qué cubre, cuántas visitas lleva, qué
            días aplica, y el estado de su bonus (si tiene). */}
        <AnimatePresence>
        {showMembershipInfoModal && myMembership?.plan && (
          <motion.div key="membership-info" {...backdropFade}
            className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-4"
            style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowMembershipInfoModal(false)}>
            <motion.div
              initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={membresiaSheet.exit || panelExit}
              transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
              {...membresiaSheet.panelProps}
              role="dialog" aria-modal="true" aria-label="Tu membresía"
              className="w-full sm:max-w-sm rounded-t-[28px] sm:rounded-[28px] overflow-hidden"
              style={sheetStyle} onClick={e => e.stopPropagation()}>
              <div {...membresiaSheet.handleProps} className="relative px-6 pt-6 pb-5 touch-none sm:touch-auto" style={{ background: goldBox.background, borderBottom: `1px solid rgba(201,162,58,0.3)` }}>
                <div className="sm:hidden absolute top-2.5 left-1/2 -translate-x-1/2"><SheetGrabber /></div>
                <p className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: goldText }}>
                  <Ticket size={16} /> Tu membresía
                </p>
                <h3 className="cv-display mt-2" style={{ fontSize: '2.6rem' }}>{myMembership.plan.name}</h3>
              </div>

              <dl className="px-6 pt-5 pb-[calc(env(safe-area-inset-bottom)+20px)] sm:pb-5 flex flex-col gap-4 text-[15px]">
                <div>
                  <dt className="text-[13px] mb-0.5" style={{ color: textSecondary }}>Incluye</dt>
                  <dd className="font-semibold">
                    {myMembership.plan.allowedServiceIds?.length > 0
                      ? services.filter(s => myMembership.plan.allowedServiceIds.includes(s.id)).map(s => s.name).join(' + ')
                      : 'Todos los servicios'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[13px] mb-0.5" style={{ color: textSecondary }}>Visitas este mes</dt>
                  <dd className="font-semibold cv-num">
                    {myMembership.plan.visitsPerMonth
                      ? `${myMembership.member.visitsUsedThisPeriod || 0} de ${myMembership.plan.visitsPerMonth} usadas`
                      : `${myMembership.member.visitsUsedThisPeriod || 0} usadas, sin límite`}
                  </dd>
                </div>
                {myMembership.plan.allowedDays?.length > 0 && (
                  <div>
                    <dt className="text-[13px] mb-0.5" style={{ color: textSecondary }}>Válido los días</dt>
                    <dd className="font-semibold">
                      {myMembership.plan.allowedDays.map(d => ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][d]).join(', ')}
                    </dd>
                  </div>
                )}
                {myMembership.plan.bonusServiceIds?.length > 0 && (() => {
                  const bonusNombres = services.filter(s => myMembership.plan.bonusServiceIds.includes(s.id)).map(s => s.name).join(' + ');
                  const bonusDisponible = (myMembership.member.bonusVisitsUsedThisPeriod || 0) < (myMembership.plan.bonusVisitsPerMonth || 1);
                  return (
                    <div className="p-3.5 rounded-2xl" style={bonusDisponible ? goldBox : { background: t.sunk }}>
                      <dt className="flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: bonusDisponible ? goldText : textSecondary }}>
                        <Gift size={14} /> Bonus {myMembership.plan.bonusVisitsPerMonth || 1} vez por mes
                      </dt>
                      <dd className="font-semibold mt-1">{bonusNombres}</dd>
                      <dd className="text-[13px] mt-0.5" style={{ color: bonusDisponible ? goldText : textSecondary }}>
                        {bonusDisponible ? 'Disponible este mes' : 'Ya lo usaste este mes'}
                      </dd>
                    </div>
                  );
                })()}

                <button
                  onClick={() => setShowMembershipInfoModal(false)}
                  className="w-full h-12 mt-1 rounded-xl font-bold text-[15px] active:scale-[0.98] transition-transform"
                  style={{ background: GOLD, color: '#1A1608' }}
                >
                  Seguir con mi reserva
                </button>
              </dl>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* AVISO DE CUPO AGOTADO — socio que ya usó todas las visitas del
            mes: este turno se cobra al precio normal, o renueva el plan. */}
        <AnimatePresence>
        {showAvisoSinCupo && (avisoSinCupoOrigen === 'socio' ? sinCupoDelMes(myMembership) : membershipCoverage?.sinCupo) && (() => {
          const datosSC = avisoSinCupoOrigen === 'socio' ? myMembership : membershipCoverage;
          const desdeSocio = avisoSinCupoOrigen === 'socio';
          const planSC = datosSC.plan || {};
          const socioSC = datosSC.member || {};
          const numeroVisita = (planSC.visitsPerMonth || 0) + 1;
          const ordinalVisita = ['primera', 'segunda', 'tercera', 'cuarta', 'quinta', 'sexta', 'séptima', 'octava', 'novena', 'décima', 'undécima', 'duodécima'][numeroVisita - 1] || `${numeroVisita}ª`;
          const vence = socioSC.renewalDate ? socioSC.renewalDate.split('-').reverse().join('/') : null;
          const textoWA = `Hola, soy ${clientInfo.name || socioSC.clientName || ''}. Ya usé las ${planSC.visitsPerMonth} visitas de mi plan ${planSC.name || ''} de este mes y quiero renovarlo.`;
          return (
            <motion.div key="aviso-sin-cupo" {...backdropFade}
              className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-4"
              style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowAvisoSinCupo(false)}>
              <motion.div
                initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={sinCupoSheet.exit || panelExit}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                {...sinCupoSheet.panelProps}
                role="dialog" aria-modal="true" aria-label="Ya usaste tus visitas del mes"
                className="w-full sm:max-w-sm rounded-t-[28px] sm:rounded-[28px] overflow-hidden"
                style={sheetStyle} onClick={e => e.stopPropagation()}>
                <div {...sinCupoSheet.handleProps} className="relative px-6 pt-6 pb-5 touch-none sm:touch-auto" style={{ background: goldBox.background, borderBottom: `1px solid rgba(201,162,58,0.3)` }}>
                  <div className="sm:hidden absolute top-2.5 left-1/2 -translate-x-1/2"><SheetGrabber /></div>
                  <p className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: goldText }}>
                    <Ticket size={16} /> {planSC.name || 'Tu membresía'}
                  </p>
                  <h3 className="text-[22px] font-bold leading-tight mt-2">Esta sería tu {ordinalVisita} visita del mes</h3>
                </div>

                <div className="px-6 pt-5 pb-[calc(env(safe-area-inset-bottom)+20px)] sm:pb-5 flex flex-col gap-4 text-[15px]">
                  <p style={{ color: textSecondary }}>
                    Tu plan incluye <strong className="cv-num" style={{ color: 'inherit' }}>{planSC.visitsPerMonth} visitas por mes</strong> y ya las usaste todas
                    {' '}(<span className="cv-num">{socioSC.visitsUsedThisPeriod || 0} de {planSC.visitsPerMonth}</span>).
                  </p>
                  {desdeSocio ? (
                    <div className="p-3.5 rounded-2xl text-[14px]" style={{ background: t.sunk }}>
                      Esta visita <strong>se cobra al precio normal</strong> del servicio. Elegí el servicio que querés hacerte.
                    </div>
                  ) : (
                    <div className="p-3.5 rounded-2xl flex items-center justify-between gap-3" style={{ background: t.sunk }}>
                      <span className="text-[14px]" style={{ color: textSecondary }}>Este turno se cobra al precio normal</span>
                      <span className="text-[17px] font-bold cv-num">{formatPrice(totalPrice)}</span>
                    </div>
                  )}
                  <p className="text-[14px]" style={{ color: textSecondary }}>
                    Si preferís seguir usando tu plan, podés pagar la renovación en el local
                    {vence ? <> (tu plan vence el <span className="cv-num">{vence}</span>)</> : null}.
                    Apenas se registre el pago, tus visitas vuelven a empezar.
                  </p>

                  <button
                    onClick={aceptarAvisoSinCupo}
                    className="w-full h-12 rounded-xl font-bold text-[15px] active:scale-[0.98] transition-transform"
                    style={{ background: brandColor, color: getContrastColor(brandColor) }}
                  >
                    {desdeSocio ? 'Elegir mi servicio' : `Continuar y pagar ${formatPrice(totalPrice)}`}
                  </button>
                  {shopSettings?.whatsapp ? (
                    <a
                      href={`${formatearEnlaceWA(shopSettings.whatsapp)}?text=${encodeURIComponent(textoWA)}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={() => setShowAvisoSinCupo(false)}
                      className="w-full h-12 rounded-xl font-bold text-[15px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      style={{ background: GOLD, color: '#1A1608' }}
                    >
                      <MessageCircle size={17} /> Quiero renovar mi plan
                    </a>
                  ) : null}
                  <button
                    onClick={() => setShowAvisoSinCupo(false)}
                    className="w-full h-11 rounded-xl font-semibold text-[15px]"
                    style={{ color: textSecondary }}
                  >
                    Volver
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
        </AnimatePresence>

        {/* AVISO DE PLAN POR VENCER — socio al día al que le quedan 5 días
            o menos para renovar. Puede renovar por WhatsApp o seguir. */}
        <AnimatePresence>
        {showAvisoPorVencer && myMembership?.plan && diasParaVencer(myMembership) !== null && (() => {
          const planPV = myMembership.plan;
          const socioPV = myMembership.member || {};
          const dias = diasParaVencer(myMembership);
          const vencePV = socioPV.renewalDate.split('-').reverse().join('/');
          const textoDias = dias === 0 ? 'Vence hoy' : dias === 1 ? 'Te queda 1 día' : `Te quedan ${dias} días`;
          const textoWA = `Hola, soy ${clientInfo.name || socioPV.clientName || ''}. Mi plan ${planPV.name || ''} vence el ${vencePV} y quiero renovarlo.`;
          return (
            <motion.div key="aviso-por-vencer" {...backdropFade}
              className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-4"
              style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowAvisoPorVencer(false)}>
              <motion.div
                initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={porVencerSheet.exit || panelExit}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                {...porVencerSheet.panelProps}
                role="dialog" aria-modal="true" aria-label="Tu plan está por vencer"
                className="w-full sm:max-w-sm rounded-t-[28px] sm:rounded-[28px] overflow-hidden"
                style={sheetStyle} onClick={e => e.stopPropagation()}>
                <div {...porVencerSheet.handleProps} className="relative px-6 pt-6 pb-5 touch-none sm:touch-auto" style={{ background: goldBox.background, borderBottom: `1px solid rgba(201,162,58,0.3)` }}>
                  <div className="sm:hidden absolute top-2.5 left-1/2 -translate-x-1/2"><SheetGrabber /></div>
                  <p className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: goldText }}>
                    <Ticket size={16} /> {planPV.name || 'Tu membresía'}
                  </p>
                  <h3 className="text-[22px] font-bold leading-tight mt-2">Tu plan está por vencer</h3>
                </div>

                <div className="px-6 pt-5 pb-[calc(env(safe-area-inset-bottom)+20px)] sm:pb-5 flex flex-col gap-4 text-[15px]">
                  <div className="p-3.5 rounded-2xl flex items-center justify-between gap-3" style={{ background: t.sunk }}>
                    <span className="text-[14px]" style={{ color: textSecondary }}>Vence el <span className="cv-num">{vencePV}</span></span>
                    <span className="text-[15px] font-bold" style={{ color: WARN }}>{textoDias}</span>
                  </div>
                  {planPV.visitsPerMonth ? (
                    <p className="text-[14px] cv-num" style={{ color: textSecondary }}>
                      Este mes usaste {socioPV.visitsUsedThisPeriod || 0} de {planPV.visitsPerMonth} visitas.
                    </p>
                  ) : null}
                  <p className="text-[14px]" style={{ color: textSecondary }}>
                    Renovalo antes del vencimiento para no perder los beneficios de tu plan. El pago se hace en el local.
                  </p>

                  {shopSettings?.whatsapp ? (
                    <a
                      href={`${formatearEnlaceWA(shopSettings.whatsapp)}?text=${encodeURIComponent(textoWA)}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={() => setShowAvisoPorVencer(false)}
                      className="w-full h-12 rounded-xl font-bold text-[15px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      style={{ background: GOLD, color: '#1A1608' }}
                    >
                      <MessageCircle size={17} /> Quiero renovar mi plan
                    </a>
                  ) : null}
                  <button
                    onClick={() => setShowAvisoPorVencer(false)}
                    className="w-full h-12 rounded-xl font-bold text-[15px] active:scale-[0.98] transition-transform"
                    style={shopSettings?.whatsapp
                      ? { color: textPrimary, boxShadow: `inset 0 0 0 1px ${t.lineStrong}` }
                      : { background: brandColor, color: getContrastColor(brandColor) }}
                  >
                    Seguir con mi reserva
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
        </AnimatePresence>

        {/* MODAL DE RESEÑAS (CALIFICAR BARBERO) */}
        <AnimatePresence>
        {showReviewModal && reviewBooking && (
          <motion.div key="review-modal" {...backdropFade}
            className="fixed inset-0 z-[12000] flex items-end sm:items-center justify-center sm:p-4"
            style={{ background: 'rgba(0,0,0,0.6)' }}>
            <motion.div
              initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={panelExit}
              transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
              role="dialog" aria-modal="true" aria-label="Calificar atención"
              className="w-full sm:max-w-sm rounded-t-[28px] sm:rounded-[28px] p-6 pb-[calc(env(safe-area-inset-bottom)+24px)] sm:pb-6"
              style={sheetStyle}>

              <h3 className="cv-display" style={{ fontSize: '2.4rem' }}>¿Qué tal tu corte?</h3>
              <p className="text-[15px] mt-2 mb-6" style={{ color: textSecondary }}>
                Contanos cómo te atendió {reviewBooking.barber?.name?.split(' ')[0] || 'el profesional'}.
              </p>

              {/* Estrellas Interactivas */}
              <div className="flex gap-1 mb-6" role="radiogroup" aria-label="Calificación">
                {[1, 2, 3, 4, 5].map(num => (
                  <motion.button key={num} whileTap={{ scale: 0.85 }} onClick={() => setReviewStars(num)}
                    role="radio" aria-checked={reviewStars === num} aria-label={`${num} estrella${num > 1 ? 's' : ''}`}
                    className="w-12 h-12 flex items-center justify-center">
                    <Star size={34}
                      fill={num <= reviewStars ? brandColor : 'transparent'}
                      color={num <= reviewStars ? brandColor : t.lineStrong}
                      style={{ transition: 'fill 0.15s, color 0.15s' }}
                    />
                  </motion.button>
                ))}
              </div>

              <label className="block text-[13px] font-semibold mb-1.5" htmlFor="cv-review-comment" style={{ color: textSecondary }}>
                Comentario (opcional)
              </label>
              <textarea
                id="cv-review-comment"
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                className="cv-input mb-6"
                style={{ minHeight: 100, resize: 'none' }}
              />

              <div className="flex gap-3">
                <button onClick={() => setShowReviewModal(false)}
                  className="flex-1 h-12 rounded-xl font-semibold text-[15px]"
                  style={{ color: textPrimary, border: `1px solid ${t.lineStrong}` }}>
                  Ahora no
                </button>
                <button
                  onClick={async () => {
                    // 1. Enviar la reseña a Firebase
                    await submitReview(
                      reviewBooking.locationId,
                      reviewBooking.barber?.id,
                      currentUser?.uid || reviewBooking.userId,
                      currentUser?.displayName || reviewBooking.client?.name || 'Cliente',
                      reviewStars,
                      reviewComment,
                      reviewBooking.id
                    );

                    // 2. Actualización dinámica (Optimista)
                    if (typeof setBarbers === 'function' && reviewBooking.barber?.id) {
                      setBarbers(prevBarbers => prevBarbers.map(barber => {
                        if (barber.id === reviewBooking.barber.id) {
                          // Leer los valores de donde sea que estén (nuevo formato o viejo formato)
                          const currentCount = barber.reviewsCount || barber.ratingSummary?.count || 0;
                          const currentAverage = parseFloat(barber.rating || barber.ratingSummary?.average || 0);

                          const newCount = currentCount + 1;
                          const newAverage = ((currentAverage * currentCount) + reviewStars) / newCount;

                          return {
                            ...barber,
                            // Guardamos en el formato que espera Firebase
                            rating: parseFloat(newAverage.toFixed(1)),
                            reviewsCount: newCount,
                            // Mantenemos el formato viejo por si otra pantalla de la app lo necesita
                            ratingSummary: {
                              average: newAverage.toFixed(1),
                              count: newCount
                            }
                          };
                        }
                        return barber;
                      }));
                    }

                    setShowReviewModal(false);
                  }}
                  className="flex-1 h-12 rounded-xl font-bold text-[15px]"
                  style={{ background: brandColor, color: contrastText }}>
                  Enviar reseña
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* MODALES GLOBALES */}
        {showPaymentModal && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}>
            <BancardPaymentModal ticketId={lastBookingId} total={effectiveTotalPrice ?? totalPrice}
              onClose={() => { setShowPaymentModal(false); setShowSuccess(true); }}
              onCancel={async () => {
                if (window.confirm('⚠️ ¿Cancelar pago y liberar turno?')) {
                  if (lastBookingId) await deleteBooking(lastBookingId, true);
                  setShowPaymentModal(false); setLastBookingId(null); setShowSuccess(false);
                  setIsPaid(false); setSelectedDate(''); setSelectedTime('');
                  setSelectedServices([]); setClientInfo({ name: '', phone: '', email: '', observaciones: '' });
                  resetDomicilio();
                  setStep(locations.length > 1 ? 0 : 1);
                  showAlert('Cancelado', 'El turno ha sido liberado.');
                }
              }}
              onPayLater={() => {
                setIsPaid(false); setShowPaymentModal(false); setShowSuccess(true);
                setTimeout(() => {
                  setStep(locations.length > 1 ? 0 : 1); setSelectedServices([]); setSelectedDate('');
                  setSelectedTime(''); setSelectedBarber(null);
                  setClientInfo({ name: '', email: '', phone: '', observaciones: '' });
                  setShowSuccess(false); setLastBookingId(null); setIsPaid(false);
                }, 10000);
              }}
              allowPayLater={!shopSettings.requirePayment}
            />
          </div>
        )}

        {/* PANTALLA DE ÉXITO — el ticket del turno */}
        <AnimatePresence>
        {showSuccess && (
          <motion.div key="success-screen" {...backdropFade}
            className="cv-scroll fixed inset-0 z-[9000] overflow-y-auto"
            style={{ background: bg, paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* 📱 El de afuera tiene scroll y este de adentro ("min-h-full")
                centra todo cuando entra, pero deja desplazarse si el
                cliente eligió muchos servicios y el ticket no entra. */}
            <div className="min-h-full w-full max-w-md mx-auto flex flex-col justify-center px-4 py-10">

              <div className="px-2 mb-7">
                <motion.div
                  initial={reduceMotion ? { opacity: 0 } : { scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', damping: 16, stiffness: 260 }}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: successTone, color: successToneText }}>
                  {successIcon}
                </motion.div>
                <h2 className="cv-display" style={{ fontSize: 'clamp(3rem, 13vw, 4rem)' }}>
                  {successTitle}
                </h2>
                <p className="text-[16px] mt-3 leading-relaxed" style={{ color: textSecondary, maxWidth: '36ch' }}>
                  {successSubtitle}
                </p>
              </div>

              {/* 📋 Resumen completo del turno — la hora es lo más grande
                  (lo primero que el cliente quiere confirmar), después
                  barbero, servicios y total. El número de ticket queda
                  chico, al pie, como referencia. */}
              {selectedServices?.length > 0 && (
                <motion.div
                  initial={reduceMotion ? { opacity: 0 } : { y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.12, duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                  className="w-full mb-6 rounded-[24px] overflow-hidden"
                  style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>

                  <div className="h-2" style={{ background: brandColor }} />
                  <div className="px-5 pt-5 pb-5">
                    {selectedDate && (
                      <p className="text-[15px] font-semibold" style={{ color: textSecondary }}>{fechaLarga(selectedDate)}</p>
                    )}
                    <p className="cv-display cv-num" style={{ fontSize: '5.5rem', lineHeight: 0.85, marginTop: 6 }}>
                      {selectedTime}
                    </p>
                    {selectedBarber?.name && (
                      <div className="flex items-center gap-3 mt-5">
                        {renderAvatar(selectedBarber, 40, 10)}
                        <p className="text-[15px]">Con <strong className="font-semibold">{selectedBarber.name}</strong></p>
                      </div>
                    )}
                  </div>

                  <div className="cv-perf" aria-hidden="true" />

                  <div className="px-5 pt-5 pb-5 text-[15px]">
                    <ul className="space-y-2.5">
                      {selectedServices.map((s) => (
                        <li key={s.id} className="flex justify-between items-baseline gap-3">
                          <span className="truncate">{s.name}</span>
                          {renderPrecioLinea(s)}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-between items-baseline pt-4 mt-4" style={{ borderTop: `1px solid ${t.line}` }}>
                      <span className="font-semibold">Total</span>
                      <span className="cv-display cv-num" style={{ fontSize: '2rem' }}>{formatPrice(effectiveTotalPrice ?? totalPrice ?? 0)}</span>
                    </div>
                    {lastBookingId && (
                      <p className="flex items-center justify-between mt-3 text-[13px]" style={{ color: textSecondary }}>
                        <span className="flex items-center gap-1.5"><ClipboardList size={13} /> Ticket</span>
                        <span className="font-semibold cv-num" style={{ color: textPrimary }}>#{lastBookingId.slice(-5)}</span>
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

              <div className="flex flex-col gap-2 w-full">
                <a href={`${formatearEnlaceWA(shopSettings?.whatsapp)}?text=${encodeURIComponent(
                    requiereSenia
                      ? `¡Hola! Acabo de reservar un turno en ${shopSettings?.shopName || 'la barbería'} (ticket #${lastBookingId?.slice(-5)}) y ya voy a hacer la transferencia de la seña por ${formatPrice(montoSenia)}. Les mando el comprobante en cuanto la haga para confirmar.`
                      : `¡Hola! Tengo un turno en ${shopSettings?.shopName || 'la barbería'}${selectedDate ? ` el ${fechaLarga(selectedDate).toLowerCase()}` : ''}${selectedTime ? ` a las ${selectedTime}` : ''}. Mi ticket es #${lastBookingId?.slice(-5)}.`
                  )}`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-full h-14 rounded-2xl font-bold text-[16px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  style={{ background: '#25D366', color: '#06290F' }}>
                  <MessageCircle size={19} /> {requiereSenia ? 'Enviar comprobante por WhatsApp' : 'Consultar por WhatsApp'}
                </a>
                <button onClick={() => window.location.reload()}
                  className="w-full h-12 rounded-2xl font-semibold text-[15px]"
                  style={{ color: textSecondary }}>
                  Volver al inicio
                </button>
              </div>
            </div>
          </motion.div>
        )}
        </AnimatePresence>

        <div className="flex-1 w-full max-w-md mx-auto md:max-w-4xl relative flex flex-col h-[100dvh]">

          {/* ============================= */}
          {/* PASO 0: ELEGIR LOCAL */}
          {/* ============================= */}
          {view === 'booking' && step === 0 && !showSuccess && (
            <div className="flex flex-col min-h-[100dvh] w-full relative">
              <div className="absolute right-4 z-50" style={{ top: 'calc(env(safe-area-inset-top) + 16px)' }}>
                <UserAuthButton onImage={!!shopSettings?.bannerUrl} />
              </div>
              <div className="relative z-10 flex-1 flex flex-col w-full h-full">
                <LocationSelector
                  locations={locations}
                  handleSelectLocation={(loc) => { handleSelectLocation(loc); setStep(1); }}
                  loading={loadingLocations}
                  isDarkMode={isDarkMode}
                  shopSettings={shopSettings}
                  logoVersion={logoVersion}
                />
              </div>
            </div>
          )}

          {/* ============================= */}
          {/* PASO 1: EL LOCAL + PROFESIONAL */}
          {/* ============================= */}
          {view === 'booking' && step === 1 && !showSuccess && (
            <div className="cv-scroll h-[100dvh] w-full overflow-y-auto overflow-x-hidden no-scrollbar"
              onScroll={(e) => {
                const alto = heroRef.current?.offsetHeight || 0;
                const y = e.currentTarget.scrollTop;
                heroAltoRef.current = alto || 1;
                // (en iOS, scrollTop es negativo durante el rebote de arriba)
                heroScrollY.set(y);
                // Aparece justo cuando el panel llega debajo de la barra.
                setHeroCompacto(y > alto - 96);
              }}>

              {/* Encabezado compacto: cuando la foto sale de pantalla, el nombre
                  del local y "Mis turnos" siguen a mano arriba. */}
              <AnimatePresence>
                {heroCompacto && (
                  <motion.div key="hero-compacto"
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, transition: { duration: 0.12 } }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    className="cv-material fixed top-0 left-0 right-0 z-40"
                    style={{ borderBottom: `1px solid ${surfaceBorder}`, paddingTop: 'env(safe-area-inset-top)' }}>
                    <div className="max-w-md md:max-w-4xl mx-auto h-14 px-4 flex items-center gap-3">
                      {renderShopMark(32)}
                      <p className="flex-1 min-w-0 truncate text-[16px] font-bold">{shopSettings?.shopName || 'Barbería'}</p>
                      <button onClick={() => setView('myBookings')} aria-label="Mis turnos"
                        className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                        style={{ boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}>
                        <History size={17} />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* HERO — la fachada del local con el nombre encima. Queda casi
                  fija detrás y el panel de contenido sube por encima. */}
              <div ref={heroRef} className="relative w-full"
                style={{ minHeight: shopSettings?.bannerUrl ? 'min(58svh, 480px)' : 'auto', background: shopSettings?.bannerUrl ? '#000' : 'transparent' }}>
                {shopSettings?.bannerUrl && (
                  <motion.div className="absolute inset-0 origin-bottom"
                    style={reduceMotion ? undefined : { y: heroFotoY, scale: heroFotoScale, willChange: 'transform' }}>
                    <motion.img
                      initial={reduceMotion ? { opacity: 0 } : { scale: 1.08, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      src={shopSettings.bannerUrl}
                      alt={`Fachada de ${shopSettings?.shopName || 'la barbería'}`}
                      draggable={false}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.82) 100%)' }} />
                    {!reduceMotion && (
                      <motion.div className="absolute inset-0" style={{ background: '#000', opacity: heroSombra }} />
                    )}
                  </motion.div>
                )}

                {/* Barra superior */}
                <div className="relative z-10 flex items-center justify-between px-4"
                  style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}>
                  {locations.length > 1 ? (
                    <motion.button whileTap={{ scale: 0.95 }}
                      onClick={() => { setSelectedBarber(null); volverPaso(0); }}
                      className={`${shopSettings?.bannerUrl ? 'cv-glass ' : ''}flex items-center gap-1 h-10 pl-2.5 pr-4 rounded-full text-[14px] font-semibold`}
                      style={shopSettings?.bannerUrl
                        ? undefined
                        : { background: t.surface, color: textPrimary, boxShadow: `0 0 0 1px ${t.line}` }}>
                      <ChevronLeft size={18} /> Locales
                    </motion.button>
                  ) : <span />}
                  <UserAuthButton onImage={!!shopSettings?.bannerUrl} />
                </div>

                {/* Nombre del local */}
                <motion.div className={`z-10 px-5 ${shopSettings?.bannerUrl ? 'absolute bottom-0 left-0 right-0 pb-[52px]' : 'relative pt-10 pb-2'}`}
                  style={{ color: shopSettings?.bannerUrl ? '#fff' : textPrimary, opacity: shopSettings?.bannerUrl && !reduceMotion ? heroNombreOpacity : 1 }}>
                  {renderShopMark(56)}
                  <h1 className="cv-display mt-4" style={{ fontSize: 'clamp(3.25rem, 15vw, 5.5rem)', textWrap: 'balance' }}>
                    {shopSettings?.shopName || 'Barbería'}
                  </h1>
                </motion.div>
              </div>

              {/* PANEL — con foto, se monta sobre el borde de abajo del hero y
                  al hacer scroll sube tapándola, siempre sobre fondo sólido. */}
              <div className={shopSettings?.bannerUrl ? 'relative z-10 -mt-7 rounded-t-[28px] md:rounded-t-[32px]' : ''}
                style={shopSettings?.bannerUrl ? { background: bg, boxShadow: '0 -10px 30px rgba(0,0,0,0.22)' } : undefined}>

              {/* Datos del local */}
              {selectedLocation && (
                <div className={`px-5 flex flex-col gap-2 text-[15px] ${shopSettings?.bannerUrl ? 'pt-6' : 'pt-5'}`}>
                  <p className="flex items-start gap-2.5">
                    <MapPin size={17} className="shrink-0 mt-0.5" style={{ color: textSecondary }} />
                    <span>{selectedLocation?.address || shopSettings?.address}</span>
                  </p>
                  {enabledFeatures?.googleReviews && shopSettings?.showGoogleReviews && selectedLocation?.googleReviews?.rating && (
                    <GoogleRatingPill data={selectedLocation.googleReviews} t={t} textPrimary={textPrimary} textSecondary={textSecondary} onClick={() => setVerResenasGoogle(true)} />
                  )}
                  <p className="flex items-start gap-2.5">
                    <Clock size={17} className="shrink-0 mt-0.5" style={{ color: textSecondary }} />
                    <span>
                      {formatOpenDays(shopSettings?.openDays || [1, 2, 3, 4, 5, 6])}, de{' '}
                      <strong className="font-semibold cv-num">{shopSettings?.openTime || '09:00'} a {shopSettings?.closeTime || '21:00'}</strong>
                    </span>
                  </p>

                  {/* REDES SOCIALES */}
                  {(shopSettings?.whatsapp || shopSettings?.instagram || shopSettings?.facebook ||
                    shopSettings?.tiktok || shopSettings?.website) && (
                      <div className="flex items-center gap-2 mt-2">
                        {[
                          shopSettings.whatsapp && { href: formatearEnlaceWA(shopSettings.whatsapp), label: 'WhatsApp', icon: <MessageCircle size={18} /> },
                          shopSettings.instagram && { href: shopSettings.instagram, label: 'Instagram', icon: <Instagram size={18} /> },
                          shopSettings.facebook && { href: shopSettings.facebook, label: 'Facebook', icon: <Facebook size={18} /> },
                          shopSettings.tiktok && {
                            href: shopSettings.tiktok, label: 'TikTok', icon: (
                              <svg viewBox="0 0 24 24" fill="currentColor" width={17} height={17} aria-hidden="true">
                                <path d="M19.589 6.686a4.793 4.793 0 0 1-3.97-1.561 4.765 4.765 0 0 1-1.074-3.125h-3.328v10.96c0 1.258-.696 2.38-1.782 2.872a3.149 3.149 0 0 1-4.047-1.144 3.141 3.141 0 0 1-.365-2.27 3.155 3.155 0 0 1 2.355-2.316 3.158 3.158 0 0 1 2.502.261V6.995a6.452 6.452 0 0 0-3.344-.19A6.47 6.47 0 0 0 2.296 11.5a6.485 6.485 0 0 0 .546 5.093 6.482 6.482 0 0 0 4.67 3.256 6.46 6.46 0 0 0 5.485-1.71 6.444 6.444 0 0 0 1.93-4.57V8.67a8.04 8.04 0 0 0 4.662 1.488v-3.472Z" />
                              </svg>
                            )
                          },
                          shopSettings.website && { href: shopSettings.website, label: 'Sitio web', icon: <Globe size={18} /> },
                        ].filter(Boolean).map(red => (
                          <a key={red.label} href={red.href} target="_blank" rel="noopener noreferrer"
                            aria-label={red.label} title={red.label}
                            className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                            style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}>
                            {red.icon}
                          </a>
                        ))}
                      </div>
                    )}
                </div>
              )}

              {/* ⭐ Hoja de reseñas de Google — en un portal al <body>: acá adentro
                  quedaba atrapada en la capa del panel (z-10) y el botón fijo de
                  "Elegí profesional" le pasaba por encima. */}
              {createPortal(<div className="cv-root" style={cssVars}><AnimatePresence>
                {verResenasGoogle && selectedLocation?.googleReviews && (
                  <motion.div {...backdropFade} onClick={() => setVerResenasGoogle(false)}
                    className="fixed inset-0 z-[100000] flex items-end sm:items-center justify-center sm:p-5"
                    style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <motion.div
                      initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={googleSheet.exit || panelExit}
                      transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                      {...googleSheet.panelProps}
                      onClick={(e) => e.stopPropagation()}
                      role="dialog" aria-modal="true" aria-label="Reseñas de Google"
                      className="w-full sm:max-w-[420px] max-h-[85dvh] flex flex-col rounded-t-[28px] sm:rounded-[28px] p-5 pb-[calc(env(safe-area-inset-bottom)+16px)] sm:pb-5"
                      style={sheetStyle}>
                      <div {...googleSheet.handleProps} className="sm:hidden -mt-5 -mx-5 mb-1 h-7 flex items-center justify-center touch-none shrink-0">
                        <SheetGrabber />
                      </div>
                      <GoogleReviewsContent data={selectedLocation.googleReviews} t={t} textPrimary={textPrimary} textSecondary={textSecondary}
                        brandColor={brandColor} contrastText={contrastText} onClose={() => setVerResenasGoogle(false)} />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence></div>, document.body)}

              {/* Profesionales */}
              <div className="px-4 pt-8 pb-[132px]">
                <div className="flex items-end justify-between gap-3 mb-5 px-1">
                  <h2 className="cv-display" style={{ fontSize: '2.4rem' }}>¿Quién te atiende?</h2>
                  <button onClick={() => setView('myBookings')}
                    className="shrink-0 flex items-center gap-1.5 h-10 px-4 rounded-full text-[14px] font-semibold"
                    style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}>
                    <History size={15} /> Mis turnos
                  </button>
                </div>

                {barbers.length === 0 && barberosSinTurno.length > 0 ? null : barbers.length === 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" aria-label="Cargando profesionales">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="rounded-[20px] overflow-hidden" style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                        <div className="aspect-[4/5] animate-pulse" style={{ background: t.sunk }} />
                        <div className="p-3"><div className="h-5 w-2/3 rounded animate-pulse" style={{ background: t.sunk }} /></div>
                      </div>
                    ))}
                  </div>
                ) : barbers.length === 1 ? (
                  <BarberCard
                    barber={barbers[0]}
                    isSelected={selectedBarber?.id === barbers[0].id}
                    brandColor={brandColor} isDarkMode={isDarkMode} openDays={shopSettings?.openDays}
                    onSelect={setSelectedBarber}
                    onViewProfile={(b) => { setViewBarberInfo(b); setIsProfileModalOpen(true); }}
                    isSingleBarber={true} currentPlan={currentPlan}
                    enabledFeatures={enabledFeatures}
                  />
                ) : currentPlan?.level === 'empresarial' ? (
                  <div className="flex flex-col gap-8 w-full">
                    {(() => {
                      // Los profesionales se agrupan SOLO por "Nivel de Servicio", en dos grupos
                      // fijos: "Servicio Especializado" primero y "Servicio General" debajo.
                      // Cualquier otro valor guardado (ej: "Barbero", el valor viejo por defecto,
                      // o "Manicurista", de cuando existía esa opción en el selector) se agrupa en
                      // "Servicio General", para que nunca aparezca un grupo aparte con ese título.
                      // (El "Cargo" del profesional NO cambia su grupo: solo cambia el texto de
                      // su tarjeta.)
                      const barberosAgrupados = barbers.reduce((acc, b) => {
                        const esEspecializado = (b.category || '').trim().toLowerCase() === 'servicio especializado';
                        const etiqueta = esEspecializado ? 'Servicio Especializado' : 'Servicio General';
                        if (!acc[etiqueta]) acc[etiqueta] = { etiqueta, lista: [] };
                        acc[etiqueta].lista.push(b);
                        return acc;
                      }, {});

                      return Object.values(barberosAgrupados)
                        .sort((a, b) => (a.etiqueta === 'Servicio Especializado' ? 0 : 1) - (b.etiqueta === 'Servicio Especializado' ? 0 : 1))
                        .map(({ etiqueta: categoria, lista: listaBarberos }) => (
                          <section key={categoria} className="w-full">
                            <h3 className="flex items-baseline gap-2 text-[16px] font-bold mb-3 px-1">
                              {categoria === 'Servicio Especializado' ? 'Servicio especializado' : 'Servicio general'}
                              <span className="text-[14px] font-medium cv-num" style={{ color: textSecondary }}>{listaBarberos.length}</span>
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {listaBarberos.map(barber => (
                                <BarberCard key={barber.id} barber={barber}
                                  isSelected={selectedBarber?.id === barber.id}
                                  brandColor={brandColor} isDarkMode={isDarkMode} openDays={shopSettings?.openDays}
                                  onSelect={setSelectedBarber}
                                  onViewProfile={(b) => { setViewBarberInfo(b); setIsProfileModalOpen(true); }}
                                  currentPlan={currentPlan} enabledFeatures={enabledFeatures} />
                              ))}
                            </div>
                          </section>
                        ));
                    })()}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full">
                    {barbers.map((barber) => (
                      <BarberCard key={barber.id} barber={barber}
                        isSelected={selectedBarber?.id === barber.id}
                        brandColor={brandColor} isDarkMode={isDarkMode} openDays={shopSettings?.openDays}
                        onSelect={setSelectedBarber}
                        onViewProfile={(b) => { setViewBarberInfo(b); setIsProfileModalOpen(true); }}
                        currentPlan={currentPlan} enabledFeatures={enabledFeatures} />
                    ))}
                  </div>
                )}

                {/* 🚶 Aviso: profesionales que atienden sin turno */}
                {barberosSinTurno.length > 0 && (
                  <div className="mt-6 flex items-start gap-3 p-4 rounded-[20px]" style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                    {/* Fotito de cada profesional sin turno (superpuestas si son varios) */}
                    <div className="flex shrink-0 -space-x-3">
                      {barberosSinTurno.slice(0, 3).map(b => (
                        <span key={b.id} className="relative w-11 h-11 rounded-full overflow-hidden flex items-center justify-center text-[15px] font-bold"
                          style={{ background: `${brandColor}22`, color: brandColor, boxShadow: `0 0 0 2px ${t.surface}` }}>
                          {(b.name || '?').charAt(0).toUpperCase()}
                          {b.image && !String(b.image).includes('default-barber') && (
                            <img src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[15px] font-bold" style={{ color: textPrimary }}>También atendemos sin turno</p>
                      <p className="text-[14px] mt-0.5 leading-snug" style={{ color: textSecondary }}>
                        Por orden de llegada con {barberosSinTurno.map(b => b.name?.split(' ')[0]).filter(Boolean).join(', ').replace(/, ([^,]*)$/, ' y $1')}. Acercate al local, no hace falta reservar.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          )}

          {/* ============================= */}
          {/* PASOS 2, 3, 4 */}
          {/* ============================= */}
          {view === 'booking' && step > 1 && !showSuccess && (
            <div className="relative w-full flex flex-col h-[100dvh]">

              {/* Encabezado con progreso — flota sobre el contenido */}
              <header ref={pasoHeaderRef} className="cv-material absolute top-0 left-0 right-0 z-40 px-4 pb-4 transition-[border-color] duration-200"
                style={{
                  borderBottom: `1px solid ${pasoConScroll ? surfaceBorder : 'transparent'}`,
                  paddingTop: 'calc(env(safe-area-inset-top) + 12px)',
                }}>
                <div className="flex items-center justify-between">
                  <button onClick={() => volverPaso()} aria-label="Volver al paso anterior"
                    className="w-10 h-10 -ml-1 rounded-full flex items-center justify-center"
                    style={{ color: textPrimary }}>
                    <ChevronLeft size={24} />
                  </button>
                  <UserAuthButton isCompact />
                </div>

                <div className="flex items-end justify-between gap-3 mt-2 px-1">
                  {/* truncate recorta lo que sobresale de la línea, y con el interlineado
                      apretado de la tipografía se comía la cola de la "g" y la "p": el
                      padding de abajo le da lugar, y el margen negativo no mueve nada. */}
                  <h2 className="cv-display truncate pb-[0.14em] -mb-[0.14em]" style={{ fontSize: '2.25rem' }}>
                    {stepTitles[step]}
                  </h2>
                  <span className="shrink-0 text-[13px] font-semibold cv-num pb-1" style={{ color: textSecondary }}>
                    Paso {step} de 4
                  </span>
                </div>

                {/* Barra segmentada: un tramo por paso */}
                <ol className="flex gap-1.5 mt-3 px-1" aria-label="Progreso de la reserva">
                  {pasos.map((nombre, i) => {
                    const n = i + 1;
                    return (
                      <li key={nombre} className="flex-1" aria-current={n === step ? 'step' : undefined}>
                        <div className="h-1 rounded-full overflow-hidden" style={{ background: t.line }}>
                          <motion.div
                            initial={false}
                            animate={{ width: n <= step ? '100%' : '0%' }}
                            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                            className="h-full"
                            style={{ background: n === step ? brandColor : textPrimary, opacity: n === step ? 1 : 0.55 }}
                          />
                        </div>
                        <span className="hidden sm:block text-[12px] mt-1.5 font-medium" style={{ color: n === step ? textPrimary : textSecondary }}>
                          {nombre}
                        </span>
                      </li>
                    );
                  })}
                </ol>

                {/* Categorías de servicios: fijas en el encabezado, nunca flotan */}
                {step === 2 && mostrarCategorias && (
                  <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 mt-4" role="tablist" aria-label="Categorías"
                    style={{ scrollPaddingInline: 16 }}>
                    {categoriasDisponibles.map(cat => {
                      const isActive = activeCategory === cat;
                      return (
                        <button key={cat} onClick={() => elegirCategoria(cat)}
                          role="tab" aria-selected={isActive}
                          className="whitespace-nowrap shrink-0 h-9 px-4 rounded-full text-[14px] font-semibold transition-colors active:scale-95"
                          style={{
                            background: isActive ? textPrimary : surface,
                            color: isActive ? bg : textPrimary,
                            boxShadow: isActive ? 'none' : `0 0 0 1px ${t.line}`,
                          }}>
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                )}
              </header>

              {/* Contenido con scroll */}
              <div ref={stepScrollRef} className="cv-scroll relative flex-1 overflow-y-auto overflow-x-hidden px-4 pb-[150px] no-scrollbar"
                style={{ paddingTop: pasoHeaderAlto + 16 }}
                onScroll={(e) => setPasoConScroll(e.currentTarget.scrollTop > 2)}>
                {/* popLayout: el paso que sale deja de ocupar lugar, así el nuevo
                    no aparece debajo y después salta hacia arriba. */}
                <AnimatePresence mode="popLayout" initial={false} custom={pasoCustom}>

                  {/* PASO 2: SERVICIOS */}
                  {step === 2 && (
                    <motion.div key="step2" custom={pasoCustom} variants={screenVariants} initial="hidden" animate="visible" exit="exit"
                      className="w-full">
                      {(() => {
                        if (serviciosDelBarberoConPlanes.length === 0) {
                          return (
                            <div className="text-center py-12 px-6 rounded-[20px]" style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                              <Scissors size={28} style={{ margin: '0 auto 12px', color: textSecondary }} />
                              <p className="text-[16px] font-semibold">Este profesional todavía no tiene servicios cargados.</p>
                              <button onClick={() => setStep(1)} className="mt-4 text-[15px] font-semibold underline underline-offset-4">
                                Elegir otro profesional
                              </button>
                            </div>
                          );
                        }

                        const inputSocioStyle = { padding: '11px 14px', fontSize: 16 };

                        return (
                          <>
                            {/* 🏠 ¿Dónde te atendemos? — solo si la barbería ofrece domicilio
                                y este profesional tiene servicios a domicilio */}
                            {mostrarSelectorDomicilio && (
                              <div className="mb-5">
                                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Dónde te atendemos">
                                  {[[false, 'En el local', Store], [true, 'A domicilio', Home]].map(([valor, label, Icon]) => {
                                    const activo = modoDomicilio === valor;
                                    return (
                                      <button key={label} onClick={() => elegirModalidad(valor)}
                                        role="radio" aria-checked={activo}
                                        className="flex items-center justify-center gap-2 h-12 rounded-xl text-[15px] font-semibold transition-colors active:scale-[0.98]"
                                        style={{
                                          background: activo ? textPrimary : surface,
                                          color: activo ? bg : textPrimary,
                                          boxShadow: activo ? 'none' : `0 0 0 1px ${t.lineStrong}`,
                                        }}>
                                        <Icon size={17} /> {label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {modoDomicilio && (
                                  <p className="text-[14px] mt-2.5 px-1" style={{ color: textSecondary }}>
                                    Visita a domicilio por Gran Asunción, hasta las {domicilioConfig.horaFin}.
                                  </p>
                                )}
                              </div>
                            )}

                            {/* 🎫 "¿Tenés membresía?" — solo si esta barbería vende membresías */}
                            {shopSettings?.enableMemberships && !modoDomicilio && (
                              <div className="mb-5 p-4 rounded-[20px]" style={goldBox}>
                                {myMembership?.expired ? (
                                  <div className="flex items-start gap-3">
                                    <AlertTriangle size={18} style={{ color: DANGER, flexShrink: 0, marginTop: 2 }} />
                                    <div>
                                      <p className="text-[15px] font-semibold" style={{ color: DANGER }}>
                                        Tu membresía {myMembership.plan?.name || ''} está vencida.
                                      </p>
                                      <p className="text-[14px] mt-0.5" style={{ color: textSecondary }}>
                                        Hablá con el local para renovarla. Mientras tanto, este turno se cobra normal.
                                      </p>
                                    </div>
                                  </div>
                                ) : myMembership ? (
                                  <div className="flex items-start gap-3">
                                    <Ticket size={18} style={{ color: goldText, flexShrink: 0, marginTop: 2 }} />
                                    <div>
                                      <p className="text-[15px] font-semibold" style={{ color: goldText }}>
                                        ¡Hola de nuevo! Tenés la membresía {myMembership.plan?.name || 'activa'}.
                                      </p>
                                      <p className="text-[14px] mt-0.5 cv-num" style={{ color: textSecondary }}>
                                        {myMembership.plan?.visitsPerMonth
                                          ? `Esta es tu visita ${(myMembership.member?.visitsUsedThisPeriod || 0) + 1} de ${myMembership.plan.visitsPerMonth} este mes.`
                                          : `Esta es tu visita ${(myMembership.member?.visitsUsedThisPeriod || 0) + 1} este mes, sin límite.`}
                                      </p>
                                      {myMembership.plan?.bonusServiceIds?.length > 0 && (
                                        <p className="text-[14px] font-medium mt-1 flex items-center gap-1.5" style={{ color: goldText }}>
                                          <Gift size={13} style={{ flexShrink: 0 }} />
                                          {(myMembership.member?.bonusVisitsUsedThisPeriod || 0) < (myMembership.plan.bonusVisitsPerMonth || 1)
                                            ? 'Todavía tenés tu bonus del mes'
                                            : 'Ya usaste tu bonus de este mes'}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <label htmlFor="cv-socio" className="flex items-center gap-2 text-[15px] font-semibold mb-1" style={{ color: goldText }}>
                                      <Ticket size={16} style={{ flexShrink: 0 }} /> ¿Tenés membresía?
                                    </label>
                                    <p className="text-[14px] mb-3" style={{ color: textSecondary }}>
                                      Ingresá tu teléfono, CI o email y te marcamos tu plan.
                                    </p>
                                    <div className="flex gap-2">
                                      <input
                                        id="cv-socio"
                                        type="text"
                                        inputMode="email"
                                        className="cv-input"
                                        value={socioPhoneInput}
                                        onChange={(e) => {
                                          const val = e.target.value.trim();
                                          setSocioInputError('');
                                          // Solo números → celular o CI, hasta 15 dígitos (no se
                                          // puede escribir uno más). Con alguna letra o "@" → email.
                                          if (val === '' || (/^\d*$/.test(val) && val.length <= 15)) {
                                            setSocioPhoneInput(val);
                                          } else if (/[a-zA-Z@]/.test(val) && /^[a-zA-Z0-9.@_+-]*$/.test(val) && val.length <= 80) {
                                            setSocioPhoneInput(val);
                                          }
                                        }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleVerificarSocio(); }}
                                        placeholder="0981 123 456"
                                        disabled={checkingSocio}
                                        style={{ ...inputSocioStyle, flex: 1 }}
                                      />
                                      <button
                                        onClick={handleVerificarSocio}
                                        disabled={!socioPhoneInput || checkingSocio}
                                        className="shrink-0 flex items-center justify-center min-w-[96px] px-4 rounded-xl font-bold text-[14px] disabled:opacity-50"
                                        style={{ background: GOLD, color: '#1A1608' }}
                                      >
                                        {checkingSocio ? <Loader2 size={16} className="animate-spin" /> : (socioCheckAttempted ? 'Buscar de nuevo' : 'Buscar')}
                                      </button>
                                    </div>
                                    {socioInputError && (
                                      <p role="alert" className="text-[13px] font-medium mt-2 flex items-center gap-1.5 text-rose-500">
                                        <AlertTriangle size={13} style={{ flexShrink: 0 }} /> {socioInputError}
                                      </p>
                                    )}

                                    {/* No lo encontró — las membresías las da de alta solo el
                                        admin desde el panel; acá solo se avisa. */}
                                    {socioCheckAttempted && !checkingSocio && (
                                      <div className="mt-4 pt-4" style={{ borderTop: '1px dashed rgba(201,162,58,0.4)' }}>
                                        <p className="text-[14px]" style={{ color: textSecondary }}>
                                          {socioEsEmail
                                            ? 'No encontramos una membresía activa con ese email. Probá con tu teléfono.'
                                            : (pareceTelefono
                                                ? 'No encontramos una membresía activa con ese número.'
                                                : 'No encontramos una membresía activa con ese CI. Probá con tu teléfono.')}
                                        </p>

                                        {/* 📋 Planes disponibles — el cliente no se inscribe solo:
                                            al elegir uno se abre el WhatsApp de la barbería con el
                                            plan elegido, y el admin lo da de alta desde Membresías. */}
                                        {membershipPlansAll.length > 0 ? (
                                          <>
                                            <p className="text-[15px] font-semibold mt-3 mb-2">¿Querés sumarte a un plan?</p>
                                            <div className="flex flex-col gap-2">
                                              {membershipPlansAll.map(plan => {
                                                const cubreServicios = plan.allowedServiceIds && plan.allowedServiceIds.length > 0
                                                  ? services.filter(s => plan.allowedServiceIds.includes(s.id)).map(s => s.name).join(' + ')
                                                  : 'Todos los servicios';
                                                const bonusNombres = plan.bonusServiceIds && plan.bonusServiceIds.length > 0
                                                  ? services.filter(s => plan.bonusServiceIds.includes(s.id)).map(s => s.name).join(' + ')
                                                  : null;
                                                const textoWA = `Hola! Quiero activar el plan ${plan.name} (${formatPrice(plan.price)}/mes)${selectedLocation?.name ? ` en ${selectedLocation.name}` : ''}.${clientInfo?.name ? ` Mi nombre es ${clientInfo.name}.` : ''}${pareceTelefono ? ` Mi número es ${socioPhoneInput}.` : ''}`;
                                                const contenido = (
                                                  <>
                                                    <div className="min-w-0">
                                                      <p className="text-[15px] font-bold">{plan.name}</p>
                                                      <p className="text-[14px] mt-0.5" style={{ color: textSecondary }}>{cubreServicios}</p>
                                                      <p className="text-[13px] mt-0.5" style={{ color: textSecondary }}>
                                                        {plan.visitsPerMonth ? `${plan.visitsPerMonth} veces al mes` : 'Sin límite de visitas'}
                                                      </p>
                                                      {bonusNombres && (
                                                        <p className="text-[13px] mt-1 flex items-center gap-1" style={{ color: goldText }}>
                                                          <Gift size={12} style={{ flexShrink: 0 }} />
                                                          Bonus {plan.bonusVisitsPerMonth || 1} vez por mes: {bonusNombres}
                                                        </p>
                                                      )}
                                                      {shopSettings?.whatsapp && (
                                                        <p className="text-[13px] font-semibold mt-1.5 flex items-center gap-1" style={{ color: goldText }}>
                                                          <MessageCircle size={13} style={{ flexShrink: 0 }} /> Activar por WhatsApp
                                                        </p>
                                                      )}
                                                    </div>
                                                    <p className="text-[15px] font-bold shrink-0 cv-num">{formatPrice(plan.price)}<span className="font-medium" style={{ color: textSecondary }}>/mes</span></p>
                                                  </>
                                                );
                                                return shopSettings?.whatsapp ? (
                                                  <a
                                                    key={plan.id}
                                                    href={`${formatearEnlaceWA(shopSettings.whatsapp)}?text=${encodeURIComponent(textoWA)}`}
                                                    target="_blank" rel="noopener noreferrer"
                                                    className="cv-row flex items-start justify-between gap-3 p-3.5 rounded-xl text-left transition-colors"
                                                    style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}
                                                  >
                                                    {contenido}
                                                  </a>
                                                ) : (
                                                  <div key={plan.id} className="flex items-start justify-between gap-3 p-3.5 rounded-xl"
                                                    style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                                                    {contenido}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            <p className="text-[13px] mt-2" style={{ color: textSecondary }}>
                                              {shopSettings?.whatsapp
                                                ? 'Elegí un plan y te comunicamos con la barbería para activarlo. Se paga en el local.'
                                                : 'Para activar un plan, consultalo en el local.'}
                                            </p>
                                          </>
                                        ) : (
                                          <p className="text-[14px] mt-1.5" style={{ color: textSecondary }}>
                                            Si querés sumarte a un plan, consultalo en el local.
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            {/* Lista de servicios */}
                            <ul className="flex flex-col gap-3 pb-2">
                              {serviciosAMostrar.map((s) => {
                                const isSelected = selectedServices.some(item => item.id === s.id);
                                const hasImage = !!s.image;

                                // 🎫 "Marcar" los servicios que el plan del socio
                                // cubre. Respeta el cupo de visitas: si ya se
                                // acabó, no se marca nada (el precio real es lo
                                // que se va a cobrar).
                                //
                                // 🔧 Si el plan tiene duracionFija cargada (modelo
                                // tipo Cabo: el socio reserva "el plan" directo)
                                // NO se marca ningún servicio real — solo el
                                // pseudo-servicio del plan en sí. El precio de
                                // socio por servicio (memberPrice) sigue para
                                // planes SIN duración fija (modelo tipo Capelli).
                                const tieneCupoDisponibleServ = myMembership?.plan?.visitsPerMonth === null || myMembership?.plan?.visitsPerMonth === undefined
                                  || (myMembership?.member?.visitsUsedThisPeriod || 0) < myMembership?.plan?.visitsPerMonth;
                                const planUsaDuracionFija = myMembership?.plan?.duracionFija !== undefined && myMembership?.plan?.duracionFija !== null;
                                const esServicioDelPlanCubierto = !s.isMembershipPlan && !myMembership?.expired && tieneCupoDisponibleServ && !planUsaDuracionFija && myMembership?.plan && (
                                  !myMembership.plan.allowedServiceIds || myMembership.plan.allowedServiceIds.length === 0 || myMembership.plan.allowedServiceIds.includes(s.id)
                                ) && s.memberPrice !== undefined && s.memberPrice !== null && s.memberPrice !== '';

                                const precio = (() => {
                                  // 💰 El pseudo-servicio del plan tiene s.price =
                                  // comisionPorVisita (valor interno para la comisión
                                  // del barbero) — NUNCA lo que paga el cliente, que
                                  // siempre es Gs 0 (su cuota mensual ya cubre la visita).
                                  if (s.isMembershipPlan) {
                                    return <span className="text-[16px] font-bold cv-num" style={{ color: goldText }}>Gs 0</span>;
                                  }
                                  // Placeholder histórico: nunca debería llegar acá
                                  // para el pseudo-servicio del plan (ya se resuelve arriba).
                                  if (myMembership?.plan?.id && s.id === `plan_${myMembership.plan.id}`) {
                                    return (
                                      <>
                                        <span className="block text-[13px] line-through cv-num" style={{ color: t.inkFaint }}>{formatPrice(s.price)}</span>
                                        <span className="text-[14px] font-bold" style={{ color: goldText }}>Ya sos socio</span>
                                      </>
                                    );
                                  }
                                  // Servicio real que el plan del socio cubre, con precio de socio cargado.
                                  if (esServicioDelPlanCubierto) {
                                    return (
                                      <>
                                        <span className="block text-[13px] line-through cv-num" style={{ color: t.inkFaint }}>{formatPrice(s.price)}</span>
                                        <span className="text-[16px] font-bold cv-num" style={{ color: goldText }}>{formatPrice(s.memberPrice)}</span>
                                      </>
                                    );
                                  }
                                  // Precio normal, sin ningún descuento de socio.
                                  return <span className="cv-display cv-num whitespace-nowrap" style={{ fontSize: '1.85rem' }}>{formatPrice(s.price)}</span>;
                                })();

                                return (
                                  <li key={s.id}>
                                    <motion.div
                                      id={`service-card-${s.id}`}
                                      role="button"
                                      tabIndex={0}
                                      aria-pressed={isSelected}
                                      whileTap={{ scale: 0.985 }}
                                      onClick={() => handleSelectService(s)}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectService(s); } }}
                                      className="relative overflow-hidden rounded-[22px] cursor-pointer transition-[box-shadow] duration-150"
                                      style={{
                                        background: surface,
                                        boxShadow: isSelected ? `0 0 0 2.5px ${brandColor}` : `0 0 0 1px ${t.line}`,
                                      }}>

                                      {/* FOTO GRANDE con el nombre encima (si el servicio tiene foto) */}
                                      {hasImage && (
                                        <div className="relative h-[150px] overflow-hidden">
                                          <img src={s.image} alt={s.name} loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover transition-[filter] duration-300"
                                            style={{ filter: isSelected ? 'none' : 'brightness(0.85)' }} />
                                          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.05) 100%)' }} />

                                          {(s.image || s.description) && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setViewServicePhoto(s); }}
                                              aria-label={`Ver detalle de ${s.name}`}
                                              className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center text-white"
                                              style={{ background: 'rgba(0,0,0,0.5)' }}>
                                              <Info size={16} />
                                            </button>
                                          )}

                                          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 text-white">
                                            {esServicioDelPlanCubierto && (
                                              <span className="inline-flex items-center gap-1 text-[12px] font-bold mb-1.5 px-2 py-0.5 rounded-full"
                                                style={{ background: GOLD, color: '#1A1608' }}>
                                                <Ticket size={11} /> Incluido en tu plan
                                              </span>
                                            )}
                                            <h4 className="cv-display" style={{ fontSize: '2rem', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                                              {s.name}
                                            </h4>
                                          </div>
                                        </div>
                                      )}

                                      {/* FILA DE ABAJO: selección, duración y precio */}
                                      <div className={`flex items-center justify-between gap-3 ${hasImage ? 'px-4 py-3' : 'p-4'}`}>
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <motion.div
                                            animate={{ scale: isSelected && !reduceMotion ? [1, 1.15, 1] : 1 }}
                                            transition={{ duration: 0.22 }}
                                            className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center transition-colors duration-150"
                                            style={{
                                              background: isSelected ? brandColor : 'transparent',
                                              boxShadow: isSelected ? 'none' : `inset 0 0 0 1.5px ${t.lineStrong}`,
                                              color: contrastText,
                                            }}>
                                            <AnimatePresence>
                                              {isSelected && (
                                                <motion.span initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }}
                                                  transition={{ duration: 0.14 }}>
                                                  <Check size={15} strokeWidth={3.2} />
                                                </motion.span>
                                              )}
                                            </AnimatePresence>
                                          </motion.div>

                                          {hasImage ? (
                                            <span className="flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-semibold whitespace-nowrap"
                                              style={{ background: t.sunk, color: textSecondary }}>
                                              <Clock size={13} /> {getServiceDurationDisplay(s)} min
                                            </span>
                                          ) : (
                                            <div className="min-w-0">
                                              {esServicioDelPlanCubierto && (
                                                <span className="inline-flex items-center gap-1 text-[12px] font-bold mb-1 px-2 py-0.5 rounded-full"
                                                  style={{ background: GOLD, color: '#1A1608' }}>
                                                  <Ticket size={11} /> Incluido en tu plan
                                                </span>
                                              )}
                                              <h4 className="text-[16px] font-bold leading-snug flex items-center gap-1.5" style={{ wordBreak: 'break-word' }}>
                                                {s.isMembershipPlan && <Ticket size={15} className="shrink-0" style={{ color: goldText }} />}
                                                {s.name}
                                              </h4>
                                              {s.isMembershipPlan && (
                                                <p className="text-[14px] font-medium mt-0.5" style={{ color: goldText }}>
                                                  Incluye: {s.includedServiceNames?.length ? s.includedServiceNames.join(' + ') : 'todos los servicios'}
                                                </p>
                                              )}
                                              <p className="flex flex-wrap items-center gap-x-3 mt-0.5 text-[14px]" style={{ color: textSecondary }}>
                                                <span className="flex items-center gap-1 whitespace-nowrap"><Clock size={13} /> {getServiceDurationDisplay(s)} min</span>
                                                {s.description && (
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); setViewServicePhoto(s); }}
                                                    className="font-semibold underline underline-offset-4 whitespace-nowrap"
                                                    style={{ color: textSecondary, textDecorationColor: t.lineStrong }}>
                                                    Ver detalle
                                                  </button>
                                                )}
                                              </p>
                                            </div>
                                          )}
                                        </div>

                                        <div className="text-right shrink-0">{precio}</div>
                                      </div>
                                    </motion.div>
                                  </li>
                                );
                              })}
                            </ul>

                            {/* 👥 ¿Alguien más se atiende en la misma visita? */}
                            {modoDomicilio && domicilioConfig.adicionales.length > 0 && (
                              <section className="mt-6 p-4 rounded-[20px]" style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}
                                aria-labelledby="cv-adicionales-titulo">
                                <h3 id="cv-adicionales-titulo" className="text-[16px] font-bold flex items-center gap-2">
                                  <Users size={17} style={{ color: textSecondary }} /> ¿Alguien más se atiende en la misma visita?
                                </h3>
                                <p className="text-[14px] mt-0.5 mb-3" style={{ color: textSecondary }}>
                                  {adicionalesElegidos.length === 0 && !(selectedServices || []).length
                                    ? 'Primero elegí tu servicio de arriba.'
                                    : `Son ${personasEnVisita} ${personasEnVisita === 1 ? 'persona' : 'personas'} en total (máximo ${maxPersonas}).`}
                                </p>
                                <ul className="flex flex-col">
                                  {domicilioConfig.adicionales.map((op, k) => {
                                    const cant = cantidadAdicional(op.id);
                                    const sinBase = !(selectedServices || []).some(s => !s.adicionalDomicilioId);
                                    const btn = (deshabilitado) => ({
                                      className: 'w-10 h-10 rounded-full flex items-center justify-center transition-colors active:scale-90 disabled:opacity-35 disabled:active:scale-100',
                                      style: { boxShadow: `inset 0 0 0 1.5px ${t.lineStrong}`, color: textPrimary },
                                      disabled: deshabilitado,
                                    });
                                    return (
                                      <li key={op.id} className="flex items-center justify-between gap-3 py-3" style={k > 0 ? { borderTop: `1px solid ${t.line}` } : undefined}>
                                        <div className="min-w-0">
                                          <p className="text-[15px] font-semibold leading-snug">{op.nombre}</p>
                                          <p className="text-[14px] cv-num" style={{ color: textSecondary }}>
                                            {formatPrice(Number(op.precio) || 0)} · +{Number(op.duracion) || 0} min
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                          <button type="button" aria-label={`Quitar una persona: ${op.nombre}`} onClick={() => restarAdicional(op.id)} {...btn(cant === 0)}>
                                            <Minus size={16} />
                                          </button>
                                          <span className="w-6 text-center text-[17px] font-bold cv-num" aria-live="polite">{cant}</span>
                                          <button type="button" aria-label={`Sumar una persona: ${op.nombre}`} onClick={() => sumarAdicional(op)} {...btn(sinBase || personasEnVisita >= maxPersonas)}>
                                            <Plus size={16} />
                                          </button>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                                {personasEnVisita >= maxPersonas && (
                                  <p className="text-[14px] mt-2 font-medium" style={{ color: textSecondary }}>
                                    ¿Son más de {maxPersonas}?{' '}
                                    {shopSettings?.whatsapp
                                      ? <a href={`${formatearEnlaceWA(shopSettings.whatsapp)}?text=${encodeURIComponent('Hola, quiero consultar por un servicio a domicilio para un grupo.')}`}
                                          target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-4" style={{ color: textPrimary }}>
                                          Escribinos por WhatsApp
                                        </a>
                                      : 'Escribinos para coordinar.'}
                                  </p>
                                )}
                              </section>
                            )}
                          </>
                        );
                      })()}
                    </motion.div>
                  )}

                  {/* PASO 3: FECHA Y HORA */}
                  {step === 3 && (
                    <motion.div key="step3" custom={pasoCustom} variants={screenVariants} initial="hidden" animate="visible" exit="exit" className="w-full">

                      {/* Con quién y cuánto dura */}
                      <div className="flex items-center gap-3 p-3 mb-6 rounded-[18px]"
                        style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                        {renderAvatar(selectedBarber, 44, 12)}
                        <p className="flex-1 min-w-0 text-[15px] truncate">
                          Con <strong className="font-semibold">{selectedBarber?.name}</strong>
                        </p>
                        <span className="shrink-0 flex items-center gap-1 text-[14px] font-semibold cv-num" style={{ color: textSecondary }}>
                          <Clock size={14} /> {formatDuration(totalDuration)}
                        </span>
                      </div>

                      {/* Día */}
                      <div className="flex items-center justify-between mb-3 px-1">
                        <h3 className="text-[17px] font-bold">Día</h3>
                        <button
                          onClick={() => { setMesCalendarioVisible(selectedDate ? parse(selectedDate, 'yyyy-MM-dd', new Date()) : new Date()); setMostrarCalendarioFechas(true); }}
                          className="flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[14px] font-semibold"
                          style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}
                        >
                          <Calendar size={15} /> Ver calendario
                        </button>
                      </div>

                      {/* 📅 Calendario por mes — para saltar directo a una
                          fecha de otro mes (no solo los próximos días de la fila). */}
                      <AnimatePresence>
                        {mostrarCalendarioFechas && (
                          <motion.div
                            {...backdropFade}
                            onClick={() => setMostrarCalendarioFechas(false)}
                            className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-5"
                            style={{ background: 'rgba(0,0,0,0.6)' }}
                          >
                            <motion.div
                              initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              exit={calendarioSheet.exit || panelExit}
                              transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                              {...calendarioSheet.panelProps}
                              onClick={(e) => e.stopPropagation()}
                              role="dialog" aria-modal="true" aria-label="Elegir fecha"
                              className="w-full sm:max-w-[360px] rounded-t-[28px] sm:rounded-[28px] p-5 pb-[calc(env(safe-area-inset-bottom)+20px)] sm:pb-5"
                              style={sheetStyle}
                            >
                              <div {...calendarioSheet.handleProps}
                                className="sm:hidden -mt-5 -mx-5 mb-1 h-7 flex items-center justify-center touch-none">
                                <SheetGrabber />
                              </div>
                              {/* Título + cerrar */}
                              <div className="flex items-center justify-between gap-3 mb-3">
                                <span className="text-[15px] font-semibold" style={{ color: textSecondary }}>Elegí una fecha</span>
                                <motion.button
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => setMostrarCalendarioFechas(false)}
                                  aria-label="Cerrar calendario"
                                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                                  style={{ boxShadow: `0 0 0 1px ${t.line}`, color: textPrimary }}
                                >
                                  <X size={18} />
                                </motion.button>
                              </div>
                              <div className="flex items-center justify-between mb-4">
                                <button onClick={() => setMesCalendarioVisible(prev => subMonths(prev, 1))} aria-label="Mes anterior"
                                  className="w-10 h-10 rounded-full flex items-center justify-center" style={{ boxShadow: `0 0 0 1px ${t.line}` }}>
                                  <ChevronLeft size={18} />
                                </button>
                                <span className="cv-display capitalize" style={{ fontSize: '1.9rem' }}>
                                  {format(mesCalendarioVisible, 'MMMM yyyy', { locale: es })}
                                </span>
                                <button onClick={() => setMesCalendarioVisible(prev => addMonths(prev, 1))} aria-label="Mes siguiente"
                                  className="w-10 h-10 rounded-full flex items-center justify-center" style={{ boxShadow: `0 0 0 1px ${t.line}` }}>
                                  <ChevronRight size={18} />
                                </button>
                              </div>
                              <div className="grid grid-cols-7 gap-1 mb-1">
                                {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => (
                                  <div key={i} className="text-center text-[12px] font-semibold" style={{ color: t.inkFaint }}>{d}</div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-1">
                                {(() => {
                                  const inicioMes = startOfMonth(mesCalendarioVisible);
                                  const finMes = endOfMonth(mesCalendarioVisible);
                                  const diasDelMes = eachDayOfInterval({ start: inicioMes, end: finMes });
                                  // Los últimos días del mes anterior se muestran tenues y
                                  // sin click — así se leen como "otro mes" y no como
                                  // fechas rotas.
                                  const relleno = getDay(inicioMes);
                                  const diasDelMesAnterior = relleno > 0
                                    ? eachDayOfInterval({ start: subDays(inicioMes, relleno), end: subDays(inicioMes, 1) })
                                    : [];
                                  const celdas = [...diasDelMesAnterior.map(d => ({ dia: d, deEsteMes: false })), ...diasDelMes.map(d => ({ dia: d, deEsteMes: true }))];
                                  const hoyStr = format(new Date(), 'yyyy-MM-dd');
                                  return celdas.map(({ dia, deEsteMes }, idx) => {
                                    const dateStr = format(dia, 'yyyy-MM-dd');
                                    // Un día de otro mes nunca es clickeable acá — para
                                    // ir a ese mes, están las flechas de arriba.
                                    const disponible = deEsteMes && chequearDiaDisponible(dateStr, selectedBarber, allowedDaysDelPlanElegido);
                                    const esSeleccionado = deEsteMes && selectedDate === dateStr;
                                    return (
                                      <button
                                        key={`${dateStr}-${idx}`}
                                        disabled={!disponible}
                                        aria-label={format(dia, "EEEE d 'de' MMMM", { locale: es })}
                                        aria-pressed={esSeleccionado}
                                        onClick={() => {
                                          if (!deEsteMes) return;
                                          setSelectedDate(dateStr); setSelectedTime(''); setMostrarCalendarioFechas(false);
                                          // Si el día elegido es uno de los chips, se centra en la fila; y
                                          // en cualquier caso se baja hasta los horarios, para que el
                                          // cliente vea enseguida qué sigue.
                                          setTimeout(() => {
                                            try {
                                              document.getElementById(`chip-fecha-${dateStr}`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                                              document.getElementById('seccion-horarios')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            } catch (e) { /* navegador sin soporte: no pasa nada */ }
                                          }, 450);
                                        }}
                                        className="aspect-square rounded-xl text-[15px] font-semibold cv-num"
                                        style={{
                                          background: esSeleccionado ? brandColor : 'transparent',
                                          color: esSeleccionado ? contrastText : textPrimary,
                                          boxShadow: !esSeleccionado && dateStr === hoyStr && deEsteMes ? `inset 0 0 0 1.5px ${t.lineStrong}` : 'none',
                                          opacity: !deEsteMes ? 0.18 : disponible ? 1 : 0.3,
                                          textDecoration: deEsteMes && !disponible ? 'line-through' : 'none',
                                          cursor: disponible ? 'pointer' : 'default',
                                        }}
                                      >
                                        {format(dia, 'd')}
                                      </button>
                                    );
                                  });
                                })()}
                              </div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* 📅 Si el cliente eligió un día desde el calendario que NO
                          está entre los próximos días de la fila (ej: otro mes), la
                          fila se oculta y se muestra el día elegido completo, con la
                          opción de cambiarlo o de volver a los próximos días. */}
                      {selectedDate && !availableDatesForBarber.includes(selectedDate) ? (() => {
                        const fechaElegida = parse(selectedDate, 'yyyy-MM-dd', new Date());
                        const textoFecha = format(fechaElegida, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
                        return (
                          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 mb-7 p-4 rounded-[18px]"
                            style={{ background: `${brandColor}14`, boxShadow: `0 0 0 2px ${brandColor}` }}>
                            <div className="flex-1 min-w-0">
                              <p className="text-[16px] font-bold leading-snug">{capitalizar(textoFecha)}</p>
                              <button
                                onClick={() => { setSelectedDate(''); setSelectedTime(''); }}
                                className="mt-1 text-[14px] font-semibold underline underline-offset-4"
                                style={{ color: textSecondary }}
                              >
                                Ver los próximos días
                              </button>
                            </div>
                            <button
                              onClick={() => { setMesCalendarioVisible(fechaElegida); setMostrarCalendarioFechas(true); }}
                              className="shrink-0 h-10 px-4 rounded-full text-[14px] font-semibold"
                              style={{ background: surface, boxShadow: `0 0 0 1px ${t.lineStrong}`, color: textPrimary }}
                            >
                              Cambiar
                            </button>
                          </motion.div>
                        );
                      })() : (
                      <div className="flex gap-2 overflow-x-auto pt-1 pb-2 px-4 -mx-4 mb-6 no-scrollbar" style={{ scrollSnapType: 'x proximity', scrollPaddingInline: 16 }}>
                        {availableDatesForBarber.map(date => {
                          const isSelected = selectedDate === date;
                          const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
                          const esHoy = date === format(new Date(), 'yyyy-MM-dd');
                          return (
                            <button key={date} id={`chip-fecha-${date}`} onClick={() => setSelectedDate(date)}
                              className="flex flex-col items-center shrink-0 w-[4.25rem] py-2.5 rounded-[16px] transition-[background-color,color,box-shadow] duration-150 active:scale-95 motion-reduce:transform-none"
                              aria-pressed={isSelected}
                              aria-label={format(parsedDate, "EEEE d 'de' MMMM", { locale: es })}
                              style={{
                                scrollSnapAlign: 'start',
                                background: isSelected ? brandColor : surface,
                                boxShadow: isSelected ? 'none' : `0 0 0 1px ${t.line}`,
                                color: isSelected ? contrastText : textPrimary,
                              }}>
                              <span className="text-[13px] font-semibold capitalize" style={{ opacity: isSelected ? 0.85 : 0.65 }}>
                                {esHoy ? 'Hoy' : format(parsedDate, 'EEE', { locale: es }).replace('.', '')}
                              </span>
                              <span className="cv-display cv-num" style={{ fontSize: '2rem', lineHeight: 1, margin: '3px 0 2px' }}>
                                {format(parsedDate, 'd')}
                              </span>
                              <span className="text-[12px] font-medium capitalize" style={{ opacity: isSelected ? 0.85 : 0.6 }}>
                                {format(parsedDate, 'MMM', { locale: es }).replace('.', '')}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      )}

                      {selectedDate && (
                        <motion.section id="seccion-horarios" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }} style={{ scrollMarginTop: 8 }}>
                          <h3 className="text-[17px] font-bold mb-4 px-1">
                            Horarios del {format(parse(selectedDate, 'yyyy-MM-dd', new Date()), "EEEE d", { locale: es })}
                          </h3>
                          {(() => {
                            // Lista completa de horarios válidos para una fecha —
                            // parametrizada por fecha para reusarla también al
                            // buscar sugerencias en otros días.
                            const getValidSlotsForDate = (dateStr) => {
                              return timeSlots.filter((time, index) => {
                                if (shopSettings?.hasBreak && lunchSlots.includes(time)) return false;
                                const now = new Date();
                                if (dateStr === format(now, 'yyyy-MM-dd')) {
                                  const [h, m] = time.split(':').map(Number);
                                  if (h < now.getHours() || (h === now.getHours() && m < now.getMinutes())) return false;
                                }
                                if (!barberoAtiendeEnHorario(selectedBarber, dateStr, time, slotsNeeded)) return false;
                                return isBlockFree(dateStr, index, slotsNeeded, selectedBarber?.id);
                              });
                            };

                            // 🔧 Para las SUGERENCIAS solo hace falta saber "¿este
                            // día tiene AL MENOS un hueco libre?" — con .some() se
                            // corta apenas encuentra el primero.
                            const tieneAlgunHuecoLibre = (dateStr) => {
                              return timeSlots.some((time, index) => {
                                if (shopSettings?.hasBreak && lunchSlots.includes(time)) return false;
                                const now = new Date();
                                if (dateStr === format(now, 'yyyy-MM-dd')) {
                                  const [h, m] = time.split(':').map(Number);
                                  if (h < now.getHours() || (h === now.getHours() && m < now.getMinutes())) return false;
                                }
                                if (!barberoAtiendeEnHorario(selectedBarber, dateStr, time, slotsNeeded)) return false;
                                return isBlockFree(dateStr, index, slotsNeeded, selectedBarber?.id);
                              });
                            };

                            const validSlots = getValidSlotsForDate(selectedDate);

                            if (validSlots.length === 0) {
                              // En vez de solo avisar que no hay nada libre, se
                              // buscan los próximos días que SÍ tengan lugar.
                              const sugerencias = [];
                              for (const d of availableDatesForBarber) {
                                if (sugerencias.length >= 8) break;
                                if (d === selectedDate) continue;
                                if (tieneAlgunHuecoLibre(d)) sugerencias.push(d);
                              }

                              return (
                                <div className="p-5 rounded-[20px]" style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                                  <p className="text-[16px] font-semibold">Este día ya no quedan horarios libres.</p>
                                  {sugerencias.length > 0 ? (
                                    <>
                                      <p className="text-[14px] mt-1 mb-4" style={{ color: textSecondary }}>Estos días todavía tienen lugar:</p>
                                      <div className="flex flex-wrap gap-2">
                                        {sugerencias.map(d => {
                                          const parsedSug = parse(d, 'yyyy-MM-dd', new Date());
                                          return (
                                            <button
                                              key={d}
                                              onClick={() => { setSelectedDate(d); setSelectedTime(''); }}
                                              className="h-11 px-4 rounded-full text-[14px] font-semibold capitalize active:scale-95 transition-transform"
                                              style={{ background: bg, boxShadow: `0 0 0 1px ${t.lineStrong}`, color: textPrimary }}
                                            >
                                              {format(parsedSug, "EEE d 'de' MMM", { locale: es })}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </>
                                  ) : (
                                    <p className="text-[14px] mt-1" style={{ color: textSecondary }}>Probá con otra fecha desde el calendario.</p>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <div className="space-y-6">
                                {[
                                  { id: 'm', label: 'Mañana', slots: validSlots.filter(t => parseInt(t, 10) < 12) },
                                  { id: 't', label: 'Tarde', slots: validSlots.filter(t => parseInt(t, 10) >= 12 && parseInt(t, 10) < 19) },
                                  { id: 'n', label: 'Noche', slots: validSlots.filter(t => parseInt(t, 10) >= 19) },
                                ].filter(g => g.slots.length > 0).map(grupo => (
                                  <div key={grupo.id}>
                                    <p className="flex items-baseline gap-2 text-[14px] font-semibold mb-2.5 px-1" style={{ color: textSecondary }}>
                                      {grupo.label}
                                      <span className="text-[13px] font-medium cv-num" style={{ color: t.inkFaint }}>
                                        {grupo.slots.length} libre{grupo.slots.length > 1 ? 's' : ''}
                                      </span>
                                    </p>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                      {grupo.slots.map(time => {
                                        const isSelected = selectedTime === time;
                                        return (
                                          // La escala va por clases (no inline) para que el
                                          // "active:" del toque pueda pisarla.
                                          <button key={time} onClick={() => setSelectedTime(time)}
                                            aria-pressed={isSelected}
                                            className="cv-display cv-num h-14 rounded-[14px] transition-[transform,background-color,color,box-shadow] duration-150 ease-out active:scale-[0.96] motion-reduce:transform-none"
                                            style={{
                                              fontSize: '1.65rem', fontWeight: 700,
                                              background: isSelected ? brandColor : surface,
                                              boxShadow: isSelected ? 'none' : `0 0 0 1px ${t.line}`,
                                              color: isSelected ? contrastText : textPrimary,
                                            }}>
                                            {time}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </motion.section>
                      )}
                    </motion.div>
                  )}

                  {/* PASO 4: CONFIRMACIÓN */}
                  {step === 4 && (
                    <motion.div key="step4" custom={pasoCustom} variants={screenVariants} initial="hidden" animate="visible" exit="exit" className="w-full">

                      {/* 🎫 Banner de membresía — aparece en vivo apenas el teléfono
                          coincide con un socio activo (o se actualiza si deja de
                          estar cubierto, ej: cambió de servicio y ya no entra) */}
                      <AnimatePresence initial={false}>
                      {membershipCoverage && (
                        <motion.div key="membership-coverage" {...revealProps}>
                        <div className="flex items-start gap-3 p-4 mb-4 rounded-[18px]"
                          style={membershipCoverage.covered ? goldBox : { background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                          <Ticket size={18} className="shrink-0 mt-0.5" style={{ color: membershipCoverage.covered ? goldText : textSecondary }} />
                          <div className="flex-1 min-w-0">
                            {membershipCoverage.covered ? (
                              <>
                                <p className="text-[15px] font-bold" style={{ color: goldText }}>
                                  Cubierto por tu {membershipCoverage.plan?.name || 'membresía'}
                                </p>
                                <p className="text-[14px] mt-0.5 cv-num" style={{ color: textSecondary }}>
                                  {membershipCoverage.plan?.visitsPerMonth
                                    ? `Esta es tu visita ${(membershipCoverage.member?.visitsUsedThisPeriod || 0) + 1} de ${membershipCoverage.plan.visitsPerMonth} este mes.`
                                    : `Esta es tu visita ${(membershipCoverage.member?.visitsUsedThisPeriod || 0) + 1} este mes, sin límite.`}
                                </p>
                                {(() => {
                                  const bonusIds = membershipCoverage.plan?.bonusServiceIds || [];
                                  if (bonusIds.length === 0) return null;
                                  const usaBonusEnEsteTurno = selectedServices.some(s => bonusIds.includes(s.id));
                                  return (
                                    <p className="text-[14px] font-medium mt-1 flex items-center gap-1.5" style={{ color: goldText }}>
                                      <Gift size={13} style={{ flexShrink: 0 }} />
                                      {usaBonusEnEsteTurno
                                        ? 'Este turno usa tu bonus del mes'
                                        : ((membershipCoverage.member?.bonusVisitsUsedThisPeriod || 0) < (membershipCoverage.plan.bonusVisitsPerMonth || 1)
                                            ? 'Todavía tenés tu bonus del mes'
                                            : 'Ya usaste tu bonus de este mes')}
                                    </p>
                                  );
                                })()}
                                {/* 💰 Cobertura PARCIAL — si agregó algún servicio que el
                                    plan no cubre, se lista aparte: qué es gratis (el
                                    plan) y qué se paga (lo extra). */}
                                {(() => {
                                  const noCubiertosIds = membershipCoverage.serviciosNoCubiertosIds || [];
                                  if (noCubiertosIds.length === 0) return null;
                                  const serviciosExtra = selectedServices.filter(s => noCubiertosIds.includes(s.id));
                                  const totalExtra = serviciosExtra.reduce((sum, s) => sum + Number(s.price || 0), 0);
                                  return (
                                    <div className="mt-3 pt-3 text-[14px]" style={{ borderTop: '1px dashed rgba(201,162,58,0.4)' }}>
                                      <p className="font-semibold mb-1.5" style={{ color: textSecondary }}>No incluido en tu plan, se cobra aparte:</p>
                                      {serviciosExtra.map(s => (
                                        <div key={s.id} className="flex justify-between items-center">
                                          <span>{s.name}</span>
                                          <span className="font-semibold cv-num">{formatPrice(s.price)}</span>
                                        </div>
                                      ))}
                                      <div className="flex justify-between items-center mt-2 pt-2 font-bold" style={{ borderTop: `1px solid ${t.line}` }}>
                                        <span>A pagar</span>
                                        <span className="cv-num">{formatPrice(totalExtra)}</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </>
                            ) : (
                              <>
                                <p className="text-[15px] font-bold">Tenés membresía, pero este turno no está incluido</p>
                                <p className="text-[14px] mt-0.5" style={{ color: textSecondary }}>
                                  {membershipCoverage.reason || 'Se cobra normalmente.'}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        </motion.div>
                      )}
                      </AnimatePresence>

                      {/* El ticket del turno */}
                      <div className="rounded-[24px] mb-7 overflow-hidden" style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                        <div className="h-2" style={{ background: membershipCoverage?.covered ? GOLD : brandColor }} />
                        <div className="px-5 pt-4 pb-5">
                          <p className="text-[15px] font-semibold" style={{ color: textSecondary }}>{fechaLarga(selectedDate)}</p>
                          <div className="flex items-end justify-between gap-3">
                            <p className="cv-display cv-num" style={{ fontSize: '4.75rem', lineHeight: 0.85, marginTop: 6 }}>{selectedTime}</p>
                            <button onClick={() => setStep(3)} className="text-[14px] font-semibold underline underline-offset-4 pb-1"
                              style={{ color: textSecondary, textDecorationColor: t.lineStrong }}>
                              Cambiar
                            </button>
                          </div>
                          {selectedBarber?.name && (
                            <div className="flex items-center gap-3 mt-4">
                              {renderAvatar(selectedBarber, 36, 10)}
                              <p className="text-[15px]">Con <strong className="font-semibold">{selectedBarber.name}</strong></p>
                            </div>
                          )}
                          {modoDomicilio && (
                            <p className="flex items-start gap-2 mt-3 text-[15px]">
                              <Home size={17} className="shrink-0 mt-0.5" style={{ color: textSecondary }} />
                              <span className="min-w-0">
                                <strong className="font-semibold">A domicilio</strong>
                                {textoDireccionDomicilio(datosDomicilio) && <span style={{ color: textSecondary }}> · {textoDireccionDomicilio(datosDomicilio)}</span>}
                              </span>
                            </p>
                          )}
                        </div>

                        <div className="cv-perf" aria-hidden="true" />

                        <div className="px-5 pt-5 pb-5 text-[15px]">
                          <ul className="space-y-2">
                            {(selectedServices || []).map(s => (
                              <li key={s.id} className="flex justify-between items-baseline gap-3">
                                <span className="min-w-0">{s.name}</span>
                                {renderPrecioLinea(s)}
                              </li>
                            ))}
                          </ul>
                          <div className="flex justify-between items-end pt-4 mt-4" style={{ borderTop: `1px solid ${t.line}` }}>
                            <span className="font-semibold pb-1">Total</span>
                            {membershipCoverage?.covered ? (
                              <span className="text-right">
                                <span className="block text-[13px] line-through cv-num" style={{ color: t.inkFaint }}>{formatPrice(totalPrice)}</span>
                                <span className="cv-display cv-num" style={{ fontSize: '2.25rem', color: goldText }}>{formatPrice(effectiveTotalPrice)}</span>
                              </span>
                            ) : (
                              <span className="cv-display cv-num" style={{ fontSize: '2.25rem' }}>{formatPrice(totalPrice)}</span>
                            )}
                          </div>

                          {/* Cómo se paga */}
                          <div className="mt-4">
                            {membershipCoverage?.covered && effectiveTotalPrice === 0 ? (
                              <p className="flex items-center gap-2 text-[14px] font-semibold px-3.5 py-3 rounded-xl" style={{ ...goldBox, color: goldText }}>
                                <Ticket size={15} /> Sin cargo, incluido en tu plan
                              </p>
                            ) : requiereSenia ? (
                              <div className="rounded-2xl overflow-hidden" style={{ boxShadow: `0 0 0 1.5px ${WARN}80` }}>
                                <p className="flex items-center gap-2 px-4 py-3 text-[14px] font-bold" style={{ background: `${WARN}1F`, color: isDarkMode ? '#F5B04A' : '#8A4F00' }}>
                                  <Banknote size={16} className="shrink-0" />
                                  Este turno pide una seña del {porcentajeSenia}% por transferencia
                                </p>
                                <div className="p-4">
                                  <div className="flex justify-between items-baseline pb-3 mb-3" style={{ borderBottom: `1px dashed ${t.lineStrong}` }}>
                                    <span className="text-[14px]" style={{ color: textSecondary }}>A transferir</span>
                                    <span className="cv-display cv-num" style={{ fontSize: '1.9rem' }}>{formatPrice(montoSenia)}</span>
                                  </div>
                                  <dl className="mb-3 text-[14px] space-y-1.5">
                                    {shopSettings?.senaTransferencia?.alias && (
                                      <div>
                                        <dt style={{ color: textSecondary }}>Alias</dt>
                                        <dd className="text-[18px] font-bold select-all">{shopSettings.senaTransferencia.alias}</dd>
                                      </div>
                                    )}
                                    {shopSettings?.senaTransferencia?.banco && (
                                      <div className="flex gap-1.5"><dt style={{ color: textSecondary }}>Banco:</dt><dd className="font-semibold">{shopSettings.senaTransferencia.banco}</dd></div>
                                    )}
                                    {shopSettings?.senaTransferencia?.titular && (
                                      <div className="flex gap-1.5"><dt style={{ color: textSecondary }}>Titular:</dt><dd className="font-semibold">{shopSettings.senaTransferencia.titular}</dd></div>
                                    )}
                                  </dl>
                                  <p className="flex items-start gap-2 px-3 py-2.5 rounded-xl mb-3 text-[14px] font-semibold" style={{ background: `${DANGER}14`, color: DANGER }}>
                                    <Clock size={15} className="shrink-0 mt-0.5" />
                                    Tenés 15 minutos para transferir. Si no llega el comprobante, el turno se libera solo.
                                  </p>
                                  <p className="text-[14px] mb-3" style={{ color: textSecondary }}>
                                    Hacé la transferencia y mandanos el comprobante por WhatsApp. Confirmamos tu turno apenas lo recibamos.
                                  </p>
                                  <label className="flex items-start gap-3 cursor-pointer min-h-[44px]">
                                    <input
                                      type="checkbox"
                                      checked={confirmoTransferencia}
                                      onChange={(e) => setConfirmoTransferencia(e.target.checked)}
                                      className="w-5 h-5 shrink-0 mt-0.5"
                                      style={{ accentColor: WARN }}
                                    />
                                    <span className="text-[15px] font-semibold">
                                      Entendido, voy a transferir la seña y avisar por WhatsApp
                                    </span>
                                  </label>
                                </div>
                              </div>
                            ) : currentPlan?.level === 'premium' && shopSettings?.enableBancard && enabledFeatures?.bancard ? (
                              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Forma de pago">
                                {[['local', 'Pagar en el local', Store], ['online', 'Pagar online', CreditCard]].map(([method, label, Icon]) => {
                                  const activo = paymentMethod === method;
                                  return (
                                    <button key={method} onClick={() => setPaymentMethod(method)}
                                      role="radio" aria-checked={activo}
                                      className="flex items-center justify-center gap-2 h-12 rounded-xl text-[14px] font-semibold transition-colors"
                                      style={{
                                        background: activo ? textPrimary : 'transparent',
                                        color: activo ? bg : textPrimary,
                                        boxShadow: activo ? 'none' : `0 0 0 1px ${t.lineStrong}`,
                                      }}>
                                      <Icon size={16} /> {label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="flex items-center gap-2 text-[14px] font-semibold px-3.5 py-3 rounded-xl" style={{ background: t.sunk, color: textPrimary }}>
                                {modoDomicilio ? <><Home size={15} /> Pagás al profesional cuando te atiende</> : <><Store size={15} /> Pagás en el local</>}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 🏠 Dirección del servicio a domicilio — queda guardada en la reserva */}
                      {modoDomicilio && (
                        <div className="mb-7">
                          <h3 className="text-[17px] font-bold mb-3 px-1">¿Dónde te atendemos?</h3>
                          <div className="flex flex-col gap-4">
                            <div>
                              <label htmlFor="cv-dom-ciudad" className="block text-[14px] font-semibold mb-1.5 px-1">Ciudad</label>
                              <select id="cv-dom-ciudad" className="cv-input" value={datosDomicilio.ciudad}
                                onChange={(e) => setDatosDomicilio(prev => ({ ...prev, ciudad: e.target.value }))}>
                                <option value="">Elegí tu ciudad</option>
                                {domicilioConfig.ciudades.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <p className="text-[13px] mt-1.5 px-1 font-medium" style={{ color: textSecondary }}>Solo atendemos en Gran Asunción.</p>
                            </div>
                            <div>
                              <label htmlFor="cv-dom-direccion" className="block text-[14px] font-semibold mb-1.5 px-1">Dirección</label>
                              <div className="relative">
                                <MapPin size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: t.inkFaint }} />
                                <input id="cv-dom-direccion" type="text" className="cv-input" style={{ paddingLeft: 42 }}
                                  value={datosDomicilio.direccion} autoComplete="street-address" enterKeyHint="next"
                                  onChange={(e) => setDatosDomicilio(prev => ({ ...prev, direccion: e.target.value }))}
                                  placeholder="Calle, número y barrio" />
                              </div>
                            </div>
                            <div>
                              <label htmlFor="cv-dom-ref" className="block text-[14px] font-semibold mb-1.5 px-1">Referencia (opcional)</label>
                              <input id="cv-dom-ref" type="text" className="cv-input" value={datosDomicilio.referencia} enterKeyHint="done"
                                onChange={(e) => setDatosDomicilio(prev => ({ ...prev, referencia: e.target.value }))}
                                placeholder="Ej: portón negro, casa 2" />
                            </div>
                            <div>
                              <p className="block text-[14px] font-semibold mb-1.5 px-1">Ubicación en el mapa (opcional)</p>
                              {(typeof datosDomicilio.lat === 'number' || datosDomicilio.linkUbicacion) ? (
                                <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl" style={{ background: t.sunk }}>
                                  <span className="flex items-center gap-2 text-[14px] font-semibold min-w-0">
                                    <CheckCircle size={16} className="shrink-0" style={{ color: brandColor }} />
                                    {datosDomicilio.origenUbicacion === 'gps' ? 'Ubicación actual agregada' : 'Ubicación del link agregada'}
                                  </span>
                                  <button type="button" onClick={() => setDatosDomicilio(prev => ({ ...prev, lat: null, lng: null, origenUbicacion: null, linkUbicacion: '' }))}
                                    className="text-[14px] font-semibold underline underline-offset-4 shrink-0" style={{ color: textSecondary, textDecorationColor: t.lineStrong }}>
                                    Quitar
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <button type="button" onClick={usarMiUbicacion} disabled={buscandoGps || leyendoLink}
                                    className="w-full flex items-center justify-center gap-2 h-12 rounded-xl text-[15px] font-semibold transition-colors active:scale-[0.98] disabled:opacity-60"
                                    style={{ background: surface, color: textPrimary, boxShadow: `0 0 0 1px ${t.lineStrong}` }}>
                                    {buscandoGps ? <Loader2 size={17} className="animate-spin" /> : <LocateFixed size={17} />}
                                    {buscandoGps ? 'Buscando tu ubicación…' : 'Usar mi ubicación actual'}
                                  </button>
                                  <p className="text-[13px] font-medium text-center my-2.5" style={{ color: textSecondary }}>¿Vas a estar en otro lugar? Pegá el link de esa ubicación</p>
                                  <div className="flex gap-2">
                                    <input id="cv-dom-link" type="url" inputMode="url" className="cv-input flex-1 min-w-0" value={linkPegado} enterKeyHint="done"
                                      aria-label="Link de la ubicación"
                                      onChange={(e) => setLinkPegado(e.target.value)}
                                      onPaste={(e) => { const txt = e.clipboardData.getData('text'); if (txt) { e.preventDefault(); setLinkPegado(txt); agregarLinkUbicacion(txt); } }}
                                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarLinkUbicacion(); } }}
                                      placeholder="https://maps.app.goo.gl/…" />
                                    <button type="button" onClick={() => agregarLinkUbicacion()} disabled={!linkPegado.trim() || leyendoLink || buscandoGps}
                                      className="shrink-0 h-[50px] px-4 rounded-xl text-[15px] font-semibold transition-colors active:scale-[0.98] disabled:opacity-50"
                                      style={{ background: textPrimary, color: bg }}>
                                      {leyendoLink ? <Loader2 size={17} className="animate-spin" /> : 'Agregar'}
                                    </button>
                                  </div>
                                </>
                              )}
                              <p className="text-[13px] mt-1.5 px-1 font-medium" style={{ color: errorGps ? DANGER : textSecondary }}>
                                {errorGps || ((typeof datosDomicilio.lat === 'number' || datosDomicilio.linkUbicacion)
                                  ? 'El profesional la abre en el mapa para llegar justo a tu puerta.'
                                  : 'En WhatsApp: tocá la ubicación que te mandaron → Compartir → Copiar. En Google Maps: Compartir → Copiar link.')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <h3 className="text-[17px] font-bold mb-3 px-1">Tus datos</h3>

                      <div className="flex flex-col gap-4">
                        {[
                          { id: 'cv-nombre', icon: User, type: 'text', value: clientInfo.name, onChange: e => setClientInfo({ ...clientInfo, name: e.target.value }), label: 'Nombre y apellido', placeholder: 'Ej: Juan Benítez', autoComplete: 'name', inputMode: 'text', enterKeyHint: 'next' },
                          { id: 'cv-telefono', icon: Phone, type: 'tel', value: clientInfo.phone, onChange: handlePhoneChange, label: 'Teléfono', hint: 'Te confirmamos el turno por WhatsApp a este número.', placeholder: '0981 123 456', error: phoneError, autoComplete: 'tel', inputMode: 'tel', enterKeyHint: 'next' },
                          { id: 'cv-email', icon: Mail, type: 'email', value: clientInfo.email, onChange: e => setClientInfo({ ...clientInfo, email: e.target.value }), label: 'Email (opcional)', placeholder: 'tu@email.com', autoComplete: 'email', inputMode: 'email', enterKeyHint: 'done' },
                        ].map(({ id, icon: Icon, type, value, onChange, label, hint, placeholder, error, autoComplete, inputMode, enterKeyHint }) => (
                          <div key={id}>
                            <label htmlFor={id} className="block text-[14px] font-semibold mb-1.5 px-1">{label}</label>
                            <div className="relative">
                              <Icon size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                                style={{ color: error ? DANGER : t.inkFaint }} />
                              {/* 📱 16px como mínimo: con menos, el iPhone hace zoom
                                  a la página al tocar el campo. autoComplete deja que
                                  el celular complete nombre, teléfono y email solo. */}
                              <input id={id} type={type} value={value} onChange={onChange} placeholder={placeholder}
                                autoComplete={autoComplete} inputMode={inputMode} enterKeyHint={enterKeyHint}
                                aria-invalid={!!error} aria-describedby={error || hint ? `${id}-nota` : undefined}
                                className="cv-input"
                                style={{ paddingLeft: 42 }}
                              />
                            </div>
                            {(error || hint) && (
                              <p id={`${id}-nota`} className="text-[13px] mt-1.5 px-1 font-medium" style={{ color: error ? DANGER : textSecondary }}>
                                {error || hint}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* 🚫 A pedido de Capelli: aviso claro cuando el
                          teléfono ingresado está bloqueado — no puede
                          avanzar, y sabe por qué. */}
                      <AnimatePresence initial={false}>
                      {clienteBloqueado && (
                        <motion.div key="cliente-bloqueado" {...revealProps}>
                        <div className="flex items-start gap-3 p-4 rounded-[18px] mt-4" style={{ background: `${DANGER}14`, boxShadow: `0 0 0 1px ${DANGER}55` }}>
                          <AlertTriangle size={18} className="shrink-0 mt-0.5" style={{ color: DANGER }} />
                          <div>
                            <p className="text-[15px] font-bold" style={{ color: DANGER }}>No podés reservar con este número</p>
                            <p className="text-[14px] mt-0.5" style={{ color: textSecondary }}>
                              {clienteBloqueado.reason || 'Comunicate con la barbería para más información.'}
                            </p>
                          </div>
                        </div>
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* ============================= */}
          {/* BARRA INFERIOR FIJA */}
          {/* ============================= */}
          {view === 'booking' && step >= 1 && !showSuccess && (() => {
            // 💰 Mismo criterio para la seña por transferencia — si el
            // barbero la requiere, no se deja confirmar sin el check.
            const faltaConfirmarSenia = requiereSenia && !confirmoTransferencia;
            // 🚫 A pedido de Capelli: un cliente bloqueado no puede
            // confirmar ningún turno, sin importar qué más haya
            // completado — el botón queda deshabilitado directo.
            // 🏠 A domicilio: ciudad y dirección son obligatorias
            const faltaDomicilio = modoDomicilio && (!datosDomicilio.ciudad || !datosDomicilio.direccion?.trim());
            const deshabilitadoPaso4 = !clientInfo.name || !clientInfo.phone || !!phoneError || clientLoading || faltaConfirmarSenia || !!clienteBloqueado || faltaDomicilio;

            const cta = {
              1: {
                disabled: !selectedBarber,
                onClick: () => selectedBarber && setStep(2),
                label: selectedBarber ? `Continuar con ${selectedBarber.name?.split(' ')[0] || 'este profesional'}` : 'Elegí un profesional',
              },
              2: {
                disabled: selectedServices.length === 0,
                onClick: () => selectedServices.length > 0 && setStep(3),
                label: selectedServices.length > 0 ? 'Elegir día y hora' : 'Elegí al menos un servicio',
              },
              3: {
                disabled: !selectedDate || !selectedTime || clientLoading,
                onClick: verificarYPasarAlSiguiente,
                label: clientLoading ? null : (!selectedDate ? 'Elegí un día' : !selectedTime ? 'Elegí un horario' : `Reservar a las ${selectedTime}`),
              },
              4: {
                disabled: deshabilitadoPaso4,
                // Si el socio se quedó sin cupo y todavía no vio el aviso, el
                // botón primero muestra el aviso en vez de confirmar.
                onClick: faltaAvisoSinCupo ? () => { setAvisoSinCupoOrigen('confirmar'); setShowAvisoSinCupo(true); } : confirmBooking,
                label: clienteBloqueado
                  ? 'No podés reservar'
                  : !clientInfo.name && !clientInfo.phone
                  ? 'Completá tu nombre y teléfono'
                  : !clientInfo.name
                  ? 'Falta tu nombre'
                  : !clientInfo.phone
                  ? 'Falta tu teléfono'
                  : phoneError
                  ? 'Revisá el teléfono'
                  : faltaConfirmarSenia
                  ? 'Confirmá que vas a transferir'
                  : (membershipCoverage?.covered ? 'Confirmar sin costo' : requiereSenia ? 'Reservar y transferir la seña' : (paymentMethod === 'online' ? 'Pagar y confirmar' : 'Confirmar reserva')),
              },
            }[step];
            if (!cta) return null;

            const ctaBg = step === 4 && membershipCoverage?.covered ? GOLD : (step === 4 && requiereSenia ? WARN : brandColor);
            const ctaInk = getContrastColor(ctaBg);
            const precioVisible = membershipCoverage?.covered ? effectiveTotalPrice : totalPrice;

            return (
              <motion.div initial={reduceMotion ? { opacity: 0 } : { y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                className="cv-material fixed bottom-0 left-0 right-0 z-50"
                style={{
                  willChange: 'transform',
                  padding: '12px 16px',
                  paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)',
                  borderTop: `1px solid ${surfaceBorder}`,
                }}>
                <div className="max-w-md mx-auto md:max-w-4xl">

                  {/* Resumen: barbero, servicios, día y hora siempre a la vista */}
                  {step >= 2 && (selectedServices.length > 0 || selectedDate) && (
                    <div className="flex items-center justify-between gap-3 mb-3 px-1">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {renderAvatar(selectedBarber, 30, 8)}
                        <p className="text-[13px] leading-tight min-w-0" style={{ color: textSecondary }}>
                          <span className="block truncate font-semibold" style={{ color: textPrimary }}>
                            {selectedServices.length > 0
                              ? `${selectedServices.length} servicio${selectedServices.length > 1 ? 's' : ''}, ${formatDuration(totalDuration)}`
                              : selectedBarber?.name}
                          </span>
                          {selectedDate && (
                            <span className="block truncate">
                              {capitalizar(format(parse(selectedDate, 'yyyy-MM-dd', new Date()), "EEEE d 'de' MMMM", { locale: es }))}{selectedTime ? `, ${selectedTime} h` : ''}
                            </span>
                          )}
                        </p>
                      </div>
                      {membershipCoverage?.covered && effectiveTotalPrice === 0 ? (
                        <span className="shrink-0 flex items-center gap-1.5 text-[15px] font-bold" style={{ color: goldText }}>
                          <Ticket size={15} /> Incluido
                        </span>
                      ) : precioVisible > 0 && (
                        <span className="shrink-0 cv-display cv-num" style={{ fontSize: '1.65rem' }}>
                          {formatPrice(precioVisible)}
                        </span>
                      )}
                    </div>
                  )}

                  <motion.button whileTap={cta.disabled ? undefined : { scale: 0.98 }}
                    onClick={cta.onClick}
                    disabled={cta.disabled}
                    className="w-full h-14 flex items-center justify-center gap-2 rounded-2xl text-[16px] font-bold transition-colors"
                    style={{
                      background: cta.disabled ? t.sunk : ctaBg,
                      color: cta.disabled ? textSecondary : ctaInk,
                    }}>
                    {clientLoading && (step === 3 || step === 4) && <Loader2 size={18} className="animate-spin" />}
                    {cta.label}
                  </motion.button>
                </div>
              </motion.div>
            );
          })()}

          {/* MODAL DETALLE DE SERVICIO */}
          <AnimatePresence>
          {viewServicePhoto && (
            <motion.div key="service-photo" {...backdropFade}
              className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-4"
              style={{ background: 'rgba(0,0,0,0.6)' }}
              onClick={() => setViewServicePhoto(null)}>
              <motion.div
                initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={servicioSheet.exit || panelExit}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                {...servicioSheet.panelProps}
                onClick={e => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-label={viewServicePhoto.name}
                className="w-full sm:max-w-sm overflow-hidden rounded-t-[28px] sm:rounded-[28px]"
                style={sheetStyle}>
                {viewServicePhoto.image ? (
                  <div {...servicioSheet.handleProps} className="h-56 relative touch-none sm:touch-auto">
                    <img src={viewServicePhoto.image} alt="" draggable={false} className="w-full h-full object-cover" />
                    <div className="sm:hidden absolute top-2.5 left-1/2 -translate-x-1/2"><SheetGrabber onImage /></div>
                  </div>
                ) : (
                  <div {...servicioSheet.handleProps} className="sm:hidden h-7 -mb-4 flex items-center justify-center touch-none">
                    <SheetGrabber />
                  </div>
                )}
                <div className="p-6 pb-[calc(env(safe-area-inset-bottom)+24px)] sm:pb-6">
                  <div {...servicioSheet.handleProps} className="flex items-start justify-between gap-3">
                    <h3 className="cv-display" style={{ fontSize: '2.4rem' }}>{viewServicePhoto.name}</h3>
                    <button onClick={() => setViewServicePhoto(null)} aria-label="Cerrar"
                      className="shrink-0 w-10 h-10 -mr-2 -mt-1 rounded-full flex items-center justify-center">
                      <X size={20} />
                    </button>
                  </div>
                  <p className="flex items-center gap-4 mt-2 text-[15px]" style={{ color: textSecondary }}>
                    <span className="flex items-center gap-1 cv-num"><Clock size={15} /> {getServiceDurationDisplay(viewServicePhoto)} min</span>
                    <span className="font-bold cv-num" style={{ color: textPrimary }}>{viewServicePhoto.isMembershipPlan ? 'Gs 0' : formatPrice(viewServicePhoto.price)}</span>
                  </p>
                  {viewServicePhoto.description?.trim() && (
                    <p className="text-[15px] leading-relaxed mt-4" style={{ color: textSecondary, maxWidth: '60ch' }}>
                      {viewServicePhoto.description}
                    </p>
                  )}
                  {(() => {
                    const yaEsta = selectedServices.some(s => s.id === viewServicePhoto.id);
                    return (
                      <button onClick={() => { handleSelectService(viewServicePhoto); setViewServicePhoto(null); }}
                        className="w-full h-14 mt-6 rounded-2xl font-bold text-[16px] flex justify-center items-center gap-2"
                        style={yaEsta
                          ? { boxShadow: `0 0 0 1.5px ${t.lineStrong}`, color: textPrimary }
                          : { background: brandColor, color: contrastText }}>
                        {yaEsta ? 'Quitar del turno' : 'Agregar al turno'}
                      </button>
                    );
                  })()}
                </div>
              </motion.div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* MIS TURNOS */}
          {view === 'myBookings' && (
            <div className="cv-scroll w-full h-[100dvh] px-4 pb-12 overflow-y-auto no-scrollbar"
              style={{ background: bg, paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}>
              <div className="flex items-center justify-between mb-4">
                <button onClick={volverDeMisTurnos} aria-label="Volver a reservar"
                  className="flex items-center gap-1 h-10 pl-1.5 pr-3 -ml-1.5 rounded-full text-[15px] font-semibold"
                  style={{ color: textPrimary }}>
                  <ChevronLeft size={22} /> Reservar
                </button>
                <UserAuthButton isCompact />
              </div>

              <h2 className="cv-display mb-6 px-1" style={{ fontSize: '3rem' }}>Mis turnos</h2>

              {/* 🎫 TU MEMBRESÍA — aparece sola si el teléfono (logueado o
                  buscado a mano) coincide con un socio activo. El cliente
                  puede chequear cuántas visitas le quedan sin tener que
                  arrancar una reserva nueva. */}
              {myMembership && (
                <div className="mb-6 p-5 rounded-[22px]"
                  style={myMembership.expired
                    ? { background: `${DANGER}12`, boxShadow: `0 0 0 1px ${DANGER}55` }
                    : goldBox}>
                  <p className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: myMembership.expired ? DANGER : goldText }}>
                    {myMembership.expired ? <AlertTriangle size={16} /> : <Ticket size={16} />}
                    {myMembership.expired ? 'Membresía vencida' : 'Tu membresía'}
                  </p>
                  <p className="cv-display mt-2" style={{ fontSize: '2.25rem' }}>{myMembership.plan?.name}</p>

                  {myMembership.expired && (
                    <p className="text-[14px] mt-2" style={{ color: textSecondary }}>
                      Hablá con el local para renovarla y recuperar el precio de socio.
                    </p>
                  )}

                  <dl className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                      <dt className="text-[13px]" style={{ color: textSecondary }}>Visitas este mes</dt>
                      <dd className="cv-display cv-num mt-1" style={{ fontSize: '2rem' }}>
                        {myMembership.plan?.visitsPerMonth
                          ? `${myMembership.member?.visitsUsedThisPeriod || 0} / ${myMembership.plan.visitsPerMonth}`
                          : `${myMembership.member?.visitsUsedThisPeriod || 0} / ∞`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[13px]" style={{ color: textSecondary }}>{myMembership.expired ? 'Venció el' : 'Se renueva el'}</dt>
                      <dd className="text-[16px] font-bold mt-1.5 cv-num" style={{ color: myMembership.expired ? DANGER : textPrimary }}>
                        {myMembership.member?.renewalDate ? formatDate(myMembership.member.renewalDate) : '-'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {!currentUser && (
                <div className="mb-6">
                  <label htmlFor="cv-buscar" className="block text-[15px] font-semibold mb-2 px-1">
                    Buscá tus turnos
                  </label>
                  <div className="flex gap-2">
                    <input id="cv-buscar" type="tel" className="cv-input" value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') searchMyBookings(); }}
                      placeholder="Teléfono, ticket o CI" style={{ flex: 1 }} />
                    <button onClick={searchMyBookings}
                      className="shrink-0 flex items-center gap-1.5 px-5 rounded-xl font-bold text-[15px]"
                      style={{ background: brandColor, color: contrastText }}>
                      <Search size={16} /> Buscar
                    </button>
                  </div>
                  <p className="text-[14px] mt-3 px-1" style={{ color: textSecondary }}>
                    O <button onClick={() => setShowLoginModal(true)} className="font-semibold underline underline-offset-4" style={{ color: textPrimary }}>iniciá sesión</button> para verlos sin buscar.
                  </p>
                </div>
              )}

              <ul className="flex flex-col gap-3">
                {myBookings.map(b => {
                  const rawTicket = b.bookingGroupId ? String(b.bookingGroupId) : getTicketNumber(b.id);
                  const ticketNumber = rawTicket.slice(-5).toUpperCase();
                  let fechaTurno = null;
                  try { fechaTurno = b.date ? parse(b.date, 'yyyy-MM-dd', new Date()) : null; if (fechaTurno && isNaN(fechaTurno)) fechaTurno = null; } catch { fechaTurno = null; }
                  return (
                    <li key={b.id} className="flex rounded-[20px] overflow-hidden"
                      style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                      {/* Talón con la fecha */}
                      <div className="shrink-0 w-[4.75rem] flex flex-col items-center justify-center py-4"
                        style={{ background: `${brandColor}1A`, borderRight: `2px dashed ${t.lineStrong}` }}>
                        {fechaTurno ? (
                          <>
                            <span className="text-[12px] font-semibold capitalize" style={{ color: textSecondary }}>{format(fechaTurno, 'EEE', { locale: es }).replace('.', '')}</span>
                            <span className="cv-display cv-num" style={{ fontSize: '2.4rem', lineHeight: 1 }}>{format(fechaTurno, 'd')}</span>
                            <span className="text-[12px] font-semibold capitalize" style={{ color: textSecondary }}>{format(fechaTurno, 'MMM', { locale: es }).replace('.', '')}</span>
                          </>
                        ) : (
                          <CalendarDays size={20} style={{ color: textSecondary }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="cv-display cv-num" style={{ fontSize: '2rem' }}>{b.startTime}</p>
                          <span className={`${getStatusColor(b.status)} ${getStatusTextColor(b.status)} shrink-0 px-2.5 py-1 rounded-full text-[12px] font-bold border`}>
                            {getStatusText(b.status)}
                          </span>
                        </div>
                        <p className="text-[15px] font-semibold mt-1.5 leading-snug">{b.services?.map(s => s.name).join(', ')}</p>
                        <div className="flex items-center justify-between mt-2 text-[13px]" style={{ color: textSecondary }}>
                          <span className="cv-num">{!fechaTurno && b.date ? `${formatDate(b.date)}, ` : ''}Ticket #{ticketNumber}</span>
                          <span className="text-[15px] font-bold cv-num" style={{ color: textPrimary }}>{formatPrice(b.totalPrice)}</span>
                        </div>
                      </div>
                    </li>
                  );
                })}

                {myBookings.length === 0 && (currentUser || searchPhone) && (
                  <li className="text-center py-12 px-6 rounded-[20px]" style={{ background: surface, boxShadow: `0 0 0 1px ${t.line}` }}>
                    <History size={28} style={{ margin: '0 auto 12px', color: textSecondary }} />
                    {currentUser ? (
                      <>
                        <p className="text-[16px] font-semibold">
                          Todavía no tenés turnos{currentUser.displayName ? `, ${currentUser.displayName.split(' ')[0]}` : ''}.
                        </p>
                        <button onClick={volverDeMisTurnos}
                          className="mt-5 h-12 px-6 rounded-full font-bold text-[15px]"
                          style={{ background: brandColor, color: contrastText }}>
                          Reservar un turno
                        </button>
                      </>
                    ) : (
                      <p className="text-[16px] font-semibold">No encontramos turnos con ese dato. Revisá el número e intentá de nuevo.</p>
                    )}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        <LoginModal show={showLoginModal} onClose={() => setShowLoginModal(false)}
          brandColor={brandColor} isDarkMode={isDarkMode} openDays={shopSettings?.openDays}
          shopName={shopSettings?.shopName} logoUrl={shopSettings?.logoUrl} />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ClientView;